import React, { useState, useEffect, useRef, useCallback, useMemo, startTransition } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  ThemeProvider,
  CssBaseline,
  Button,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
  Popover,
} from "@mui/material";
import { lightTheme, darkTheme } from "./theme";
import JsonEditor from "./components/JsonEditor";
import Preview from "./components/Preview";
import RawJsonEditor from "./components/RawJsonEditor";
import FormRulesPreview from "./components/FormRulesPreview";
import SignerRoleSelector from "./components/SignerRoleSelector";
import InitialsRoleSelector from "./components/InitialsRoleSelector";
import SubsequentSigningRoleSelector from "./components/SubsequentSigningRoleSelector";
import FormCreatorSelector from "./components/FormCreatorSelector";
import FormDetailsSelector from "./components/FormDetailsSelector";
import FormUploadStructure from "./components/FormUploadStructure";
import InteractivePdfViewer from "./components/InteractivePdfViewer";
import CreateFieldDialog from "./components/CreateFieldDialog";
import DashboardPage from "./components/DashboardPage";
import ThemeTransition from "./components/ThemeTransition";
import FieldGuidelines from "./components/FieldGuidelines";
import EditFieldDialog from "./components/EditFieldDialog";
import DecisionOptionCountDialog from "./components/DecisionOptionCountDialog";
import { buildDefaultDecisionOptions } from "./utils/decisionSectionDefaults";
import { applySectionNavigationDefaults } from "./utils/sectionGoTo";
import { sanitizeAdditionalPlacements } from "./utils/additionalFieldLink";
import { pruneUnannotatedSigners } from "./utils/pruneSignatures";
import { deduplicateSectionsByAttributeName, normalizeSignatures } from "./utils/studioStateNormalizers";
import { getEffectivePdfLocationForExtraBinding } from "./utils/pdfFieldPlacement";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAutoSave, loadAutoSave, clearAutoSave } from "./hooks/useAutoSave";
import { useMetricsTracker } from "./hooks/useMetricsTracker";
import { Alert, Snackbar, IconButton as MuiIconButton, Divider, Tooltip, CircularProgress, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudOutlined from "@mui/icons-material/CloudOutlined";
import CloudDownloadOutlined from "@mui/icons-material/CloudDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import ScienceIcon from "@mui/icons-material/Science";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { JsonView } from "react-json-view-lite";
import { jsonViewerStyles } from "./utils/jsonViewerStyles";
import "./App.css";
import { savePdfBlob, loadPdfBlob, clearSavedPdf } from "./utils/pdfStorage";
import { fileToBase64, base64ToFile } from "./utils/pdfBase64";
import {
  loginWithEmailAndPassword,
  loginWithToken,
  saveFormToCloud,
  updateFormToCloud,
  getFormsFromCloud,
  getFormFromCloudById,
  getFormDataFromCloudById,

  getAssignableProfiles,
  assignForm,
  unassignForm,
  deleteFormFromCloud,
  deleteFormDataFromCloud,
  publishFormData,
  getFormDataEvents,
  getFormEvents,
  markAnnotationsComplete,
  unmarkAnnotationsComplete,
  markJsonComplete,
  unmarkJsonComplete,
  setStgReady,
} from "./services/MeviApiService";
import { parseStudioFormData } from "./models/StudioFormData";
import { parseFormJSON } from "./utils/formParser";
import { generateFormJson } from "./utils/formJsonBuilder";
import {
  isRawFormType,
  extractRawIdentity,
  buildIntegrationScaffold,
  RAW_TEMPLATE_KEY,
  RAW_INTEGRATION_KEY,
} from "./utils/rawFormTypes";
import { buildRevisionPublishPayload } from "./utils/revisionPublish";
import PdfV2AnalystElement from "./components/PdfV2AnalystElement";
import { generateRulesJson } from "./components/FormRulesPreview";
import { clearAuth, getToken, getActiveProfile } from "./services/ApiService";
import {
  buildSignaturesTemplate,
  createStudioAnnotationId,
  deriveSignatureAnnotationsFromSignatures,
  getDefaultRoleLabels,
  resolveSignerAttributeBindingsForLoad,
  snapshotSignerFieldDef,
} from "./utils/signatureUtils";
import packageJson from "../package.json";
import { useFormRegistry } from "./hooks/useFormRegistry";
import {
  validateFormIdentity,
  formatRegistryValidationMessage,
} from "./utils/formRegistry";
const appVersion = packageJson.version;

/** JSON Field Editor: attach placed PDF box between same-type fields (all section field types). */
const DEFAULT_SUBSEQ_SECTION_ID = "subseq-section-0";

const ATTACH_COMPATIBLE_FIELD_TYPES = new Set([
  "input",
  "datetime",
  "currency",
  "textarea",
  "checkbox",
  "checkboxWithOther",
  "radio",
  "radioWithOther",
  "decision",
  "decisionWithOther",
  "inlineInput",
]);

const formatApiError = (error, fallback) => {
  let msg = error?.message || fallback;
  if (error?.correlationId) msg += `\nCorrelation ID: ${error.correlationId}`;
  return msg;
};

const FORM_FLAG_STORAGE_KEY = "meviFormStudio_flaggedForms_v2";
const FORM_FLAG_NOTE_MAX_LEN = 280;

/** Non-staging STG buckets, in publish order. */
const PUBLISH_ALL_FORMS_ENVS = ["Forms-1", "Forms-2", "Forms-3", "Forms-4"];
/** Batch-publish target sentinel: every Forms-N bucket, then Staging. */
const PUBLISH_ALL_TARGET_ALL = "__all_buckets__";

/** @returns {Record<string, string>} studioFormId → trimmed note (legacy booleans skipped) */
function readFormFlagMap() {
  try {
    const raw = localStorage.getItem(FORM_FLAG_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "string" && v.trim()) out[k] = v.trim();
    }
    return out;
  } catch {
    return {};
  }
}

function writeFormFlagMap(map) {
  try {
    localStorage.setItem(FORM_FLAG_STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    console.warn("Failed to persist form flags", e);
  }
}

// Split the persisted rawFormJSON blob into { template, integration } strings
// for the two JSON-preview panes. Handles the canonical
// { "form-template", "integration" } shape and falls back (bare template) to
// treating the whole blob as the template with a scaffolded integration.
function splitRawFormBlob(blob) {
  if (!blob || typeof blob !== "object") return { template: null, integration: null };
  const template = blob[RAW_TEMPLATE_KEY] || blob;
  const integration = blob[RAW_INTEGRATION_KEY] || buildIntegrationScaffold(template);
  return {
    template: JSON.stringify(template, null, 2),
    integration: JSON.stringify(integration, null, 2),
  };
}

// Formats an ISO timestamp as "MM/DD/YY HH:mm:ss" in local time (the forms-list
// row's meta line). Empty string for missing/invalid input.
function formatUpdatedStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${String(d.getFullYear()).slice(-2)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/**
 * Alphabetise a list of `{ shortName }` rows on a copy (never in place).
 * `numeric` so RF9 sorts before RF10 rather than after it.
 */
function sortByShortName(rows) {
  return [...rows].sort((a, b) =>
    (a.shortName || "").localeCompare(b.shortName || "", undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

function App() {
  const [mode, setMode] = useState("light");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] =
    useState("light-to-dark");
  const [fields, setFields] = useState([]);
  const fieldsRef = useRef([]);
  fieldsRef.current = fields;
  const [sections, setSections] = useState([
    {
      id: "section-0",
      title: "Default Section",
      attributeName: "defaultSection",
      subtitle: "",
      goTo: "",
    },
  ]); // Initialize with default section
  const [signatures, setSignatures] = useState({});
  const [selectedCreators, setSelectedCreators] = useState([]);
  const [formDetails, setFormDetails] = useState({
    locale: "",
    type: "",
    name: "",
    shortName: "",
    category: "",
    version: "",
  });
  const [formUploadSections, setFormUploadSections] = useState([]);
  const [formRules, setFormRules] = useState({ formNotes: "", fields: [] });
  const [offerCompare, setOfferCompare] = useState({ fields: [] });
  const [extraFormSummary, setExtraFormSummary] = useState({});
  const [extraDealSummary, setExtraDealSummary] = useState({});
  // rulesJSON is authored in JSON only — carried verbatim, never edited in the UI.
  const [rulesJSON, setRulesJSON] = useState(null);
  // initialsJSON's own role metadata + row emails, so initials no longer borrow them from the
  // matching signaturesJSON role. See generateInitialsJSON.
  const [initials, setInitials] = useState(null);
  // Raw / non-PDF "HTML Smart Form" passthrough. Holds the verbatim template
  // JSON string (pretty-printed) for integration forms whose formJSON.type is
  // in RAW_FORM_TYPES. Non-null ONLY for raw forms; ordinary PDF forms leave it
  // null and drive output from the structured editor state above. When set it
  // is the source of truth for save (persisted as rawFormJSON) and publish.
  const [rawFormJson, setRawFormJson] = useState(null);
  // Companion integrations.json entry (pane 2) for mevi-sign smart forms. Held
  // as a pretty-printed string alongside rawFormJson (the form template, pane 1);
  // the two are persisted together under the rawFormJSON blob as
  // { "form-template": ..., "integration": ... }.
  const [rawIntegrationJson, setRawIntegrationJson] = useState(null);
  // A form is in raw mode when its type is a raw type. Derive from formDetails
  // so it stays correct across load / import / JSON-preview edits.
  const isRawMode = isRawFormType(formDetails.type);
  // Raw forms are JSON-preview-only (tab index 3); the other tabs are hidden.
  // Pin the active tab so a raw form never lands on a hidden panel, and force
  // JSON studio mode so it never renders the PDF/annotator layout (whose
  // toggle button is hidden for raw forms).
  useEffect(() => {
    if (isRawMode) {
      setActiveTab(3);
      setStudioMode("json");
    }
  }, [isRawMode]);
  const [additionalFields, setAdditionalFields] = useState([]);
  const additionalFieldsRef = useRef([]);
  additionalFieldsRef.current = additionalFields;
  /** Persona-related extra PDF boxes → published as `fields`[] (full field objects) on each signaturesJSON signer row */
  const [signerAttributeBindings, setSignerAttributeBindings] = useState([]);
  /** Initials-related extra PDF boxes → published as `fields`[] on each initialsJSON signer row */
  const [initialerAttributeBindings, setInitialerAttributeBindings] = useState([]);
  /** subsequentSigningJSON: role metadata + signer rows, shaped exactly like signaturesJSON. */
  const [subsequentSigning, setSubsequentSigning] = useState({});
  /** Subsequent-signing extra PDF boxes → published as `fields`[] on each subsequentSigningJSON signer row */
  const [subsequentSigningAttributeBindings, setSubsequentSigningAttributeBindings] = useState([]);
  const [formMeta, setFormMeta] = useState({
    id: null,
    studioFormId: null,
    name: "",
    description: "",
    status: "draft",
    instanceVersion: 0,
    createdAt: null,
    updatedAt: null,
    published: [],
    metadata: {},
    createdBy: null,
  });
  const [formJsonDirty, setFormJsonDirty] = useState(false);
  const [formRulesDirty, setFormRulesDirty] = useState(false);
  const [offerCompareDirty, setOfferCompareDirty] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState("");
  const [saveDialogDescription, setSaveDialogDescription] = useState("");
  const [saveDialogVersion, setSaveDialogVersion] = useState("");
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [sigWarningOpen, setSigWarningOpen] = useState(false);
  /** Roles whose max / additionalSignaturePage disagree with their slot count. */
  const [sigWarningSlotFixes, setSigWarningSlotFixes] = useState([]);
  const [sigWarningDetails, setSigWarningDetails] = useState([]);
  const pendingSaveRef = useRef(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishDialogName, setPublishDialogName] = useState("");
  const [publishDialogDescription, setPublishDialogDescription] = useState("");
  const [publishDialogVersion, setPublishDialogVersion] = useState("");
  const [publishDialogJsonType, setPublishDialogJsonType] = useState("form");
  const [publishDialogEnv, setPublishDialogEnv] = useState("");
  const [publishDialogPublished, setPublishDialogPublished] = useState([]);
  const [publishInProgress, setPublishInProgress] = useState(false);
  const [publishResultData, setPublishResultData] = useState(null);
  const [publishBlockedAnchor, setPublishBlockedAnchor] = useState(null);
  const [registryImportDialogOpen, setRegistryImportDialogOpen] = useState(false);
  const [registryImportMessage, setRegistryImportMessage] = useState("");
  const pendingImportRef = useRef(null);
  const { registry } = useFormRegistry();
  const [dirtyGuardAction, setDirtyGuardAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const [formsBrowserView, setFormsBrowserView] = useState(null);
  const [formsListTab, setFormsListTab] = useState("all");
  const [formsListSearch, setFormsListSearch] = useState("");
  const [studioForms, setStudioForms] = useState([]);
  const [localeCollapsed, setLocaleCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("formStudioLocaleCollapsed") || "{}"); } catch { return {}; }
  });
  const [annotationCompleteMap, setAnnotationCompleteMap] = useState({});
  const [jsonCompleteMap, setJsonCompleteMap] = useState({});
  // "Ready to deploy to STG" per studio form, mirrored from the latest revision's
  // metadata.stgReady (the StudioForm carries a denormalized copy).
  const [stgReadyMap, setStgReadyMap] = useState({});
  const [stgReadyClearing, setStgReadyClearing] = useState(false);
  // "Publish to All Buckets" batch modal (per-locale, super-admin only).
  const [publishAllLocale, setPublishAllLocale] = useState(null);
  const [publishAllRunning, setPublishAllRunning] = useState(false);
  const [publishAllDone, setPublishAllDone] = useState(false);
  const [publishAllResults, setPublishAllResults] = useState([]);
  const [publishAllErrors, setPublishAllErrors] = useState([]);
  // Forms already on the chosen target, skipped by the batch.
  const [publishAllSkipped, setPublishAllSkipped] = useState([]);
  // Batch target: PUBLISH_ALL_TARGET_ALL (every Forms-N + Staging) or a single
  // Forms-N bucket for a dry run before the real thing.
  const [publishAllTarget, setPublishAllTarget] = useState(PUBLISH_ALL_TARGET_ALL);
  const [formFlagMap, setFormFlagMap] = useState(() => readFormFlagMap());
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagDialogStudioFormId, setFlagDialogStudioFormId] = useState(null);
  const [flagDialogNote, setFlagDialogNote] = useState("");
  const [studioFormsLoading, setStudioFormsLoading] = useState(false);
  const [selectedStudioForm, setSelectedStudioForm] = useState(null);
  const [selectedStudioFormData, setSelectedStudioFormData] = useState(null);
  const [studioFormDatas, setStudioFormDatas] = useState([]);
  const [studioFormDatasLoading, setStudioFormDatasLoading] = useState(false);

  const [showRecoveryBanner, setShowRecoveryBanner] = useState(false);
  const [recoveredData, setRecoveredData] = useState(null);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  // The exact PDF File that matches what's stored in GCS (set on cloud load and after a
  // successful save). When the current pdfFile IS this object, the PDF is unchanged, so the
  // next save references the stored object instead of re-uploading identical bytes. Any
  // upload/replace/recovery creates a new File, breaking identity and forcing a real upload.
  const pdfBaselineFileRef = useRef(null);
  const [pendingReplacePdf, setPendingReplacePdf] = useState(null);
  const pdfReplaceInputRef = useRef(null);
  const [createFieldDialogOpen, setCreateFieldDialogOpen] = useState(false);
  const [publishedDialogData, setPublishedDialogData] = useState(null);
  const [trackingDialogData, setTrackingDialogData] = useState(null);
  const [trackingDialogLoading, setTrackingDialogLoading] = useState(false);
  const [pendingPdfLocation, setPendingPdfLocation] = useState(null);
  const [pendingFieldType, setPendingFieldType] = useState(null);
  /** When true, CreateFieldDialog opens on “Assign to existing field” after Link existing from PDF popup */
  const [createFieldDialogDefaultAssignTab, setCreateFieldDialogDefaultAssignTab] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [annotatorEditingField, setAnnotatorEditingField] = useState(null);
  const [annotatorTab, setAnnotatorTab] = useState(0);
  const [identifySectionsMode, setIdentifySectionsMode] = useState(false);
  const [selectedSectionFieldIds, setSelectedSectionFieldIds] = useState([]);
  const [identifySectionNumber, setIdentifySectionNumber] = useState("");
  const [identifySectionNotes, setIdentifySectionNotes] = useState("");
  const [identifySectionName, setIdentifySectionName] = useState("");
  const [identifySectionDescription, setIdentifySectionDescription] =
    useState("");
  const [identifySectionTitleShort, setIdentifySectionTitleShort] = useState("");
  const [identifySectionInfo, setIdentifySectionInfo] = useState("");
  const [identifySectionMetadata, setIdentifySectionMetadata] = useState("{}");
  const [identifySectionActiveId, setIdentifySectionActiveId] = useState(null);
  const [decisionOptionCountDialogOpen, setDecisionOptionCountDialogOpen] = useState(false);
  const [signatureAnnotations, setSignatureAnnotations] = useState([]);
  const [initialsAnnotations, setInitialsAnnotations] = useState([]);
  const [signaturePlacementMode, setSignaturePlacementMode] = useState(false);
  const [initialsPlacementMode, setInitialsPlacementMode] = useState(false);
  const [jsonHighlightMode, setJsonHighlightMode] = useState(false);
  const [signatureRoleKey, setSignatureRoleKey] = useState("");
  const [signatureSignerKey, setSignatureSignerKey] = useState("");
  const [initialsRoleKey, setInitialsRoleKey] = useState("");
  const [initialsSignerKey, setInitialsSignerKey] = useState("");
  const [selectedSignatureId, setSelectedSignatureId] = useState(null);
  const signatureRolesWithAnnotationsRef = useRef(new Set());
  const tabsDragRef = useRef(null);
  const splitDragRef = useRef(null);
  // Mevi Preview tab (Tab 4) — analyst custom-element ref + the
  // ArrayBuffer view of `pdfFile` we hand to it. The ArrayBuffer is
  // cached in a ref because converting on every render would burn
  // CPU on every keystroke; the effect below repopulates it when
  // pdfFile changes.
  const analystElementRef = useRef(null);
  const [analystPdfBuffer, setAnalystPdfBuffer] = useState(null);
  const [splitPercent, setSplitPercent] = useState(() => {
    const saved = localStorage.getItem("formStudioSplit");
    return saved ? Number(saved) : 50;
  });
  const [annotatorSplitPercent, setAnnotatorSplitPercent] = useState(() => {
    const saved = localStorage.getItem("formStudioAnnotatorSplit");
    return saved ? Number(saved) : 75;
  });
  const [annotatorPanelCollapsed, setAnnotatorPanelCollapsed] = useState(() => {
    return localStorage.getItem("formStudioAnnotatorCollapsed") === "true";
  });
  const LINE_HEIGHT_STORAGE_KEY_PREFIX = "formStudioLineHeight_";
  const [defaultLineHeightPx, setDefaultLineHeightPx] = useState(null);
  const [lineHeightSettingMode, setLineHeightSettingMode] = useState(false);
  const [lineHeightCaptureBox, setLineHeightCaptureBox] = useState(null);
  const [lineHeightPromptDismissed, setLineHeightPromptDismissed] = useState(false);
  /** Stable identity for PDF so we do not reset the line-height prompt on a new File reference for the same document. */
  const prevPdfFingerprintRef = useRef("");

  useEffect(() => {
    if (!pdfFile) {
      prevPdfFingerprintRef.current = "";
      return;
    }
    const fp = `${pdfFile.name}|${pdfFile.size}|${pdfFile.lastModified}`;
    if (fp !== prevPdfFingerprintRef.current) {
      prevPdfFingerprintRef.current = fp;
      setLineHeightPromptDismissed(false);
    }
  }, [pdfFile]);

  // Signer options come from signatures (Phase 4) so Add Signature and Form Settings share one source of truth.
  /**
   * Signer keys selectable for a new signature / initials tab.
   *
   * Every key in the role, including ones that already have a tab: a signer can sign or initial in
   * more than one place, and those publish together as `pdfLocations[]`. This used to subtract the
   * used keys, which capped a signer at one tab.
   */
  const getAvailableSignerKeys = (roleKey, annotations, signaturesState) => {
    if (!roleKey) return [];
    const roleSigs = signaturesState?.[roleKey]?.signatures;
    return Array.isArray(roleSigs) ? roleSigs.map((s) => s?.signerKey).filter(Boolean) : [];
  };

  // Initials placement resolves its roles and signers independently of signaturesJSON.
  //
  // A form can be initialled without being signed — GA exhibits are — and prune removes signature
  // slots that have no tab, which takes the whole role out of `signatures`. Deriving the initials
  // role list from `signatures` alone left those forms with no signer to bind a tab to: the
  // initials tabs opened detached, and re-ticking the role boxes to get them back recreated a
  // full template role of tabless slots (F259 came back with 22). Same union the publish builder
  // uses — see generateInitialsJSON.
  const initialsRolesOf = (signaturesState, storedInitials, annotations) => {
    const keys = [
      ...Object.keys(signaturesState || {}),
      ...Object.keys(storedInitials || {}),
      ...(annotations || []).map((a) => a?.roleKey),
    ].filter(Boolean);
    return [...new Set(keys)];
  };

  const getAvailableInitialsSignerKeys = (roleKey, signaturesState, storedInitials, annotations) => {
    if (!roleKey) return [];
    const fromSigs = getAvailableSignerKeys(roleKey, annotations, signaturesState);
    if (fromSigs.length > 0) return fromSigs;
    const rows = storedInitials?.[roleKey]?.initials ?? storedInitials?.[roleKey]?.signatures ?? [];
    const fromStored = rows.map((r) => r?.signerKey).filter(Boolean);
    if (fromStored.length > 0) return [...new Set(fromStored)];
    // Last resort: whatever is already placed on the PDF for this role.
    return [...new Set((annotations || []).filter((a) => a?.roleKey === roleKey).map((a) => a?.signerKey).filter(Boolean))];
  };

  useEffect(() => {
    if (!identifySectionActiveId && sections.length > 0) {
      const first = sections[0];
      setIdentifySectionActiveId(first.id);
      setIdentifySectionName(first.title || "");
      setIdentifySectionDescription(first.description || "");
      setIdentifySectionTitleShort(first.titleShort || "");
      setIdentifySectionInfo(first.info || "");
      setIdentifySectionMetadata(
        typeof first.metadata === "object" && first.metadata !== null
          ? JSON.stringify(first.metadata, null, 2)
          : "{}",
      );
    }
  }, [identifySectionActiveId, sections]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!tabsDragRef.current) return;
      const { element, startX, scrollLeft } = tabsDragRef.current;
      const delta = event.clientX - startX;
      element.scrollLeft = scrollLeft - delta;
    };

    const handleMouseUp = () => {
      if (tabsDragRef.current) {
        document.body.style.cursor = "";
        tabsDragRef.current = null;
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleTabsMouseDown = (event) => {
    const element = event.currentTarget.querySelector(".MuiTabs-scroller");
    if (!element || element.scrollWidth <= element.clientWidth) return;
    tabsDragRef.current = {
      element,
      startX: event.clientX,
      scrollLeft: element.scrollLeft,
    };
    document.body.style.cursor = "grabbing";
  };
  const [activeTab, setActiveTab] = useState(0);
  const [pdfSelectedFieldIds, setPdfSelectedFieldIds] = useState([]);
  const [pdfPrimarySelectId, setPdfPrimarySelectId] = useState(null);
  const [scrollToFieldId, setScrollToFieldId] = useState(null);
  const [showPdfButtonGlow, setShowPdfButtonGlow] = useState(false);
  const [importedFromJson, setImportedFromJson] = useState(false);
  const [studioMode, setStudioMode] = useState("json");
  const prevStudioModeForLineHeightRef = useRef(studioMode);
  /** Leaving annotator sets Dialog open=false without MUI onClose — dismiss prompt so JSON↔annotator does not re-show every time. */
  useEffect(() => {
    const prev = prevStudioModeForLineHeightRef.current;
    if (prev === "annotator" && studioMode !== "annotator") {
      setLineHeightPromptDismissed(true);
    }
    prevStudioModeForLineHeightRef.current = studioMode;
  }, [studioMode]);

  // Annotators see the Mevi Preview (analyst element) instead of the
  // bespoke field-editor panel once the form is JSON-complete. We
  // read `jsonCompleted` directly off the studio form record (the
  // field the API surfaces alongside `annotationsCompleted` etc.) —
  // not via the dashboard's optimistic map. The flag flows into the
  // analyst-input gates below and the annotator render branch so a
  // single source of truth drives all three. Declared here (above
  // the analyst-input hooks) so the deps arrays can reference it.
  const loadedStudioForm = studioForms.find(
    (f) => f.id === formMeta.studioFormId,
  );
  const isJsonComplete = loadedStudioForm?.jsonCompleted != null;
  const showAnalystForAnnotator = studioMode === "annotator" && isJsonComplete;

  // ── Mevi Preview (Tab 4): lazily read pdfFile as ArrayBuffer when
  // the tab is actually opened. Reading multi-MB PDFs eagerly on
  // every form mutation would be wasteful, so the buffer stays null
  // until the user clicks the tab. Re-runs when pdfFile changes (or
  // is replaced) so a swapped PDF flows into the analyst element.
  useEffect(() => {
    if ((activeTab !== 4 && !showAnalystForAnnotator) || !pdfFile) return undefined;
    let cancelled = false;
    pdfFile
      .arrayBuffer()
      .then((buf) => {
        if (!cancelled) setAnalystPdfBuffer(buf);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(
          "[App] Failed to read PDF as ArrayBuffer for analyst panel",
          err,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, pdfFile, showAnalystForAnnotator]);
  const [activeRole, setActiveRole] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [assignMenuAnchor, setAssignMenuAnchor] = useState(null);
  const [assignMenuForm, setAssignMenuForm] = useState(null);
  const [assignPickerOpen, setAssignPickerOpen] = useState(false);
  const [assignableProfiles, setAssignableProfiles] = useState([]);
  const [formDataMenuAnchor, setFormDataMenuAnchor] = useState(null);
  const [formDataMenuTarget, setFormDataMenuTarget] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [optionAnnotationState, setOptionAnnotationState] = useState(null);
  const [showOptionAnnotationPrompt, setShowOptionAnnotationPrompt] =
    useState(false);
  const [annotatorFormNotes, setAnnotatorFormNotes] = useState("");
  const pdfUploadInputRef = React.useRef(null);
  const [formUploadStructureOpen, setFormUploadStructureOpen] = useState(true);
  const [formStructureOpen, setFormStructureOpen] = useState(true);

  // Auto-login with stored token on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("formStudioEmail");
    const savedToken = localStorage.getItem("formStudioToken");
    if (!savedEmail || !savedToken) return;

    setLoginLoading(true);
    loginWithToken(savedEmail, savedToken)
      .then((profile) => {
        const roles = profile.roles || [];
        const rolePriority = ["studio_super_admin", "studio_admin", "studio_json", "studio_annotator"];
        const studioRole = rolePriority.find((r) => roles.includes(r));
        const role = studioRole === "studio_super_admin" ? "admin" : studioRole ? studioRole.replace("studio_", "") : "";
        if (role) {
          setIsSuperAdmin(roles.includes("studio_super_admin"));
          setActiveRole(role);
          setSnackbar((s) => ({ ...s, open: false }));
          localStorage.setItem("formStudioEmail", savedEmail);
          localStorage.setItem("formStudioToken", getToken());
        } else {
          localStorage.removeItem("formStudioEmail");
          localStorage.removeItem("formStudioToken");
        }
      })
      .catch(() => {
        localStorage.removeItem("formStudioEmail");
        localStorage.removeItem("formStudioToken");
      })
      .finally(() => setLoginLoading(false));
  }, []);

  useEffect(() => {
    const savedMode = localStorage.getItem("formStudioMode");
    if (savedMode === "annotator" || savedMode === "json") {
      setStudioMode(savedMode);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("formStudioMode", studioMode);
  }, [studioMode]);

  useEffect(() => {
    if (!activeRole) return;
    localStorage.setItem("formStudioRole", activeRole);
    if (activeRole === "annotator" && studioMode !== "annotator") {
      setStudioMode("annotator");
    }
    if (activeRole === "json" && studioMode !== "json") {
      setStudioMode("json");
    }
  }, [activeRole, studioMode]);

  // Auto-expand annotator right panel when a field or dialog needs it
  useEffect(() => {
    if (annotatorEditingField || createFieldDialogOpen) {
      setAnnotatorPanelCollapsed(false);
    }
  }, [annotatorEditingField, createFieldDialogOpen]);

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleAssignMenuOpen = (event, form) => {
    event.stopPropagation();
    setAssignMenuAnchor(event.currentTarget);
    setAssignMenuForm(form);
  };

  const handleAssignMenuClose = () => {
    setAssignMenuAnchor(null);
    setAssignMenuForm(null);
  };

  const handleAssignClick = async () => {
    setAssignMenuAnchor(null);
    try {
      const profiles = await getAssignableProfiles();
      setAssignableProfiles(profiles || []);
      setAssignPickerOpen(true);
    } catch (error) {
      console.error("Failed to fetch assignable profiles:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Failed to fetch profiles."), severity: "error" });
    }
  };

  const handleAssignProfile = async (profileId) => {
    const form = assignMenuForm;
    const profile = assignableProfiles.find((p) => p.id === profileId) || null;
    setAssignPickerOpen(false);
    setAssignMenuForm(null);
    try {
      await assignForm(form.id, profileId);
      setStudioForms((prev) =>
        prev.map((f) => f.id === form.id ? { ...f, assignedTo: profileId, assignedToUserProfile: profile } : f)
      );
    } catch (error) {
      console.error("Failed to assign form:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Failed to assign form."), severity: "error" });
    }
  };

  const handleUnassign = async () => {
    const form = assignMenuForm;
    handleAssignMenuClose();
    try {
      await unassignForm(form.id);
      setStudioForms((prev) =>
        prev.map((f) => f.id === form.id ? { ...f, assignedTo: null, assignedToUserProfile: null } : f)
      );
    } catch (error) {
      console.error("Failed to unassign form:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Failed to unassign form."), severity: "error" });
    }
  };

  const toggleLocale = (locale) => {
    setLocaleCollapsed((prev) => {
      const next = { ...prev, [locale]: !prev[locale] };
      localStorage.setItem("formStudioLocaleCollapsed", JSON.stringify(next));
      return next;
    });
  };

  const handleInlineAssignClick = async (e, form) => {
    e.stopPropagation();
    setAssignMenuForm(form);
    try {
      const profiles = await getAssignableProfiles();
      setAssignableProfiles(profiles || []);
      setAssignPickerOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: formatApiError(error, "Failed to fetch profiles."), severity: "error" });
    }
  };

  const handleInlineUnassign = async (e, form) => {
    e.stopPropagation();
    try {
      await unassignForm(form.id);
      setStudioForms((prev) =>
        prev.map((f) => f.id === form.id ? { ...f, assignedTo: null, assignedToUserProfile: null } : f)
      );
    } catch (error) {
      setSnackbar({ open: true, message: formatApiError(error, "Failed to unassign form."), severity: "error" });
    }
  };

  const handleToggleAnnotationComplete = async () => {
    const form = assignMenuForm;
    if (!form) return;
    handleAssignMenuClose();
    const isComplete = !!annotationCompleteMap[form.id];
    setAnnotationCompleteMap((prev) => ({ ...prev, [form.id]: !isComplete }));
    try {
      if (isComplete) {
        await unmarkAnnotationsComplete(form.id, form.annotationsCompleted);
        setStudioForms((prev) =>
          prev.map((f) => f.id === form.id ? { ...f, annotationsCompleted: null } : f)
        );
      } else {
        const detail = await getFormFromCloudById(form.id);
        const latestId = [...(detail.studioFormDatas || [])]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.id;
        if (!latestId) throw new Error("No revision found");
        await markAnnotationsComplete(form.id, latestId);
        setStudioForms((prev) =>
          prev.map((f) => f.id === form.id ? { ...f, annotationsCompleted: latestId } : f)
        );
      }
    } catch (err) {
      setAnnotationCompleteMap((prev) => ({ ...prev, [form.id]: isComplete }));
      setSnackbar({ open: true, message: formatApiError(err, "Failed to update completion state."), severity: "error" });
    }
  };

  // Toggle the "ready to deploy to STG" flag for a form's latest revision.
  // The backend owns the rules (latest revision only; ON requires a form publish
  // to a bucket other than Staging; OFF requires revision ownership or
  // studio_super_admin) and returns an error we surface as a toast.
  const handleStgReadyToggle = async (form, isOn) => {
    if (!form) return;
    setStgReadyMap((prev) => ({ ...prev, [form.id]: !isOn }));
    try {
      const detail = await getFormFromCloudById(form.id);
      const latestId = [...(detail.studioFormDatas || [])]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.id;
      if (!latestId) throw new Error("No revision found");
      await setStgReady(form.id, latestId, !isOn);
      setStudioForms((prev) =>
        prev.map((f) =>
          f.id === form.id ? { ...f, metadata: { ...(f.metadata || {}), stgReady: !isOn } } : f
        )
      );
      setSnackbar({
        open: true,
        message: !isOn ? "Marked ready to deploy to STG" : "Cleared ready to deploy to STG",
        severity: "success",
      });
    } catch (err) {
      setStgReadyMap((prev) => ({ ...prev, [form.id]: isOn }));
      setSnackbar({
        open: true,
        message: formatApiError(err, "Failed to update ready to deploy to STG."),
        severity: "error",
      });
    }
  };

  // Clear the flag from inside the save dialog: a revision marked ready to deploy
  // to STG can't be superseded by a new revision until it's turned off. Backend
  // re-checks ownership / super-admin, so a rejection surfaces as an error toast
  // and the Save button stays disabled.
  const handleClearStgReadyForSave = async () => {
    if (!formMeta.studioFormId || !formMeta.id) return;
    setStgReadyClearing(true);
    try {
      await setStgReady(formMeta.studioFormId, formMeta.id, false);
      setFormMeta((prev) => ({ ...prev, metadata: { ...(prev.metadata || {}), stgReady: false } }));
      setStgReadyMap((prev) => ({ ...prev, [formMeta.studioFormId]: false }));
      setStudioForms((prev) =>
        prev.map((f) =>
          f.id === formMeta.studioFormId ? { ...f, metadata: { ...(f.metadata || {}), stgReady: false } } : f
        )
      );
      setSnackbar({ open: true, message: "Cleared ready to deploy to STG", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: formatApiError(err, "Failed to clear ready to deploy to STG."),
        severity: "error",
      });
    } finally {
      setStgReadyClearing(false);
    }
  };

  const handleToggleJsonComplete = async () => {
    const form = assignMenuForm;
    if (!form) return;
    handleAssignMenuClose();
    const isComplete = !!jsonCompleteMap[form.id];
    setJsonCompleteMap((prev) => ({ ...prev, [form.id]: !isComplete }));
    try {
      if (isComplete) {
        await unmarkJsonComplete(form.id, form.jsonCompleted);
        setStudioForms((prev) =>
          prev.map((f) => f.id === form.id ? { ...f, jsonCompleted: null } : f)
        );
      } else {
        const detail = await getFormFromCloudById(form.id);
        const latestId = [...(detail.studioFormDatas || [])]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.id;
        if (!latestId) throw new Error("No revision found");
        await markJsonComplete(form.id, latestId);
        setStudioForms((prev) =>
          prev.map((f) => f.id === form.id ? { ...f, jsonCompleted: latestId } : f)
        );
      }
    } catch (err) {
      setJsonCompleteMap((prev) => ({ ...prev, [form.id]: isComplete }));
      setSnackbar({ open: true, message: formatApiError(err, "Failed to update completion state."), severity: "error" });
    }
  };

  const handleAnnotatorMarkComplete = async () => {
    const studioFormId = formMeta.studioFormId;
    const studioFormDataId = formMeta.id;
    if (!studioFormId || !studioFormDataId) return;
    const isComplete = !!annotationCompleteMap[studioFormId];
    setAnnotationCompleteMap((prev) => ({ ...prev, [studioFormId]: !isComplete }));
    try {
      if (isComplete) await unmarkAnnotationsComplete(studioFormId, studioFormDataId);
      else await markAnnotationsComplete(studioFormId, studioFormDataId);
      setStudioForms((prev) =>
        prev.map((f) => f.id === studioFormId ? { ...f, annotationsCompleted: isComplete ? null : studioFormDataId } : f)
      );
      setSnackbar({ open: true, message: !isComplete ? "Marked as annotation complete" : "Marked as incomplete", severity: "success" });
    } catch (err) {
      setAnnotationCompleteMap((prev) => ({ ...prev, [studioFormId]: isComplete }));
      setSnackbar({ open: true, message: formatApiError(err, "Failed to update completion state."), severity: "error" });
    }
  };

  const handleJsonMarkComplete = async () => {
    const studioFormId = formMeta.studioFormId;
    const studioFormDataId = formMeta.id;
    if (!studioFormId || !studioFormDataId) return;
    const isComplete = !!jsonCompleteMap[studioFormId];
    setJsonCompleteMap((prev) => ({ ...prev, [studioFormId]: !isComplete }));
    try {
      if (isComplete) await unmarkJsonComplete(studioFormId, studioFormDataId);
      else await markJsonComplete(studioFormId, studioFormDataId);
      setStudioForms((prev) =>
        prev.map((f) => f.id === studioFormId ? { ...f, jsonCompleted: isComplete ? null : studioFormDataId } : f)
      );
      setSnackbar({ open: true, message: !isComplete ? "Marked as JSON complete" : "Marked as JSON incomplete", severity: "success" });
    } catch (err) {
      setJsonCompleteMap((prev) => ({ ...prev, [studioFormId]: isComplete }));
      setSnackbar({ open: true, message: formatApiError(err, "Failed to update completion state."), severity: "error" });
    }
  };

  const updateFormFlagMap = useCallback((updater) => {
    setFormFlagMap((prev) => {
      const next = updater(prev);
      writeFormFlagMap(next);
      return next;
    });
  }, []);

  const clearFormFlag = useCallback(
    (studioFormId) => {
      if (!studioFormId) return;
      updateFormFlagMap((prev) => {
        if (!(studioFormId in prev)) return prev;
        const next = { ...prev };
        delete next[studioFormId];
        return next;
      });
    },
    [updateFormFlagMap],
  );

  const setFormFlagNote = useCallback(
    (studioFormId, note) => {
      const trimmed = String(note || "").trim();
      if (!studioFormId || !trimmed) return;
      updateFormFlagMap((prev) => ({ ...prev, [studioFormId]: trimmed }));
    },
    [updateFormFlagMap],
  );

  const openFlagDialog = useCallback((studioFormId) => {
    if (!studioFormId) return;
    setFlagDialogStudioFormId(studioFormId);
    setFlagDialogNote("");
    setFlagDialogOpen(true);
  }, []);

  const closeFlagDialog = useCallback(() => {
    setFlagDialogOpen(false);
    setFlagDialogStudioFormId(null);
    setFlagDialogNote("");
  }, []);

  const handleFlagDialogSave = useCallback(() => {
    const id = flagDialogStudioFormId;
    const trimmed = flagDialogNote.trim();
    if (!id || !trimmed) return;
    setFormFlagNote(id, trimmed);
    closeFlagDialog();
  }, [flagDialogStudioFormId, flagDialogNote, setFormFlagNote, closeFlagDialog]);

  const handleFlagIconPress = useCallback(
    (studioFormId, isCurrentlyFlagged) => {
      if (!studioFormId) return;
      if (isCurrentlyFlagged) clearFormFlag(studioFormId);
      else openFlagDialog(studioFormId);
    },
    [clearFormFlag, openFlagDialog],
  );

  const handleFormDataMenuOpen = (event, formData) => {
    event.stopPropagation();
    setFormDataMenuAnchor(event.currentTarget);
    setFormDataMenuTarget(formData);
  };

  const handleFormDataMenuClose = () => {
    setFormDataMenuAnchor(null);
    setFormDataMenuTarget(null);
  };

  const PUBLISH_ENVS = ["Forms-1", "Forms-2", "Forms-3", "Forms-4", "Staging"];

  const getUserFormsEnv = () => {
    const email = getActiveProfile()?.email || "";
    const match = email.match(/mevi-forms(\d+)@/);
    return match ? `Forms-${match[1]}` : null;
  };

  // Available publish envs = role-allowed envs minus those already published as "form".
  const getAvailableEnvs = (published) => {
    const base = isSuperAdmin
      ? PUBLISH_ENVS
      : (() => { const env = getUserFormsEnv(); return env ? [env] : []; })();
    const takenFormEnvs = new Set(
      (published || []).filter((p) => p.jsonType === "form").map((p) => p.env)
    );
    return base.filter((env) => !takenFormEnvs.has(env));
  };

  // Publish targets the form currently loaded in the editor (handlePublishConfirm
  // serializes the live editor state), so source everything from formMeta/formDetails.
  const handlePublishClick = () => {
    const published = formMeta.published || [];
    const envs = getAvailableEnvs(published);
    setPublishDialogPublished(published);
    setPublishDialogName(formMeta.name || formDetails.shortName || "");
    setPublishDialogDescription(formMeta.description || "");
    setPublishDialogVersion(formDetails.version || "");
    setPublishDialogJsonType("form");
    setPublishDialogEnv(isSuperAdmin ? "" : (envs[0] || ""));
    setPublishDialogOpen(true);
  };

  // Whether the current user has any publish capability at all (drives button visibility).
  const canEverPublish = isSuperAdmin || !!getUserFormsEnv();

  const registryValidation = useMemo(() => {
    if (!registry) return null;
    const result = validateFormIdentity(formDetails, registry, { strict: false });
    return {
      ...result,
      message: formatRegistryValidationMessage(result, formDetails),
    };
  }, [registry, formDetails]);

  const publishRegistryValidation = useMemo(() => {
    if (!registry) return null;
    return validateFormIdentity(formDetails, registry, { strict: true });
  }, [registry, formDetails]);

  // Null === can publish right now; otherwise the human-readable reason it's blocked
  // (shown in a popover when the muted button is clicked).
  const publishBlockedReason = useMemo(() => {
    if (!formMeta.id) {
      return "Save this form to the cloud before publishing.";
    }
    if (formJsonDirty || formRulesDirty || offerCompareDirty) {
      return "Save your changes to the cloud before publishing.";
    }
    if (saveInProgress) return "Saving — try again in a moment.";
    if (publishInProgress) return "Publish already in progress.";
    if (getAvailableEnvs(formMeta.published).length === 0) {
      return isSuperAdmin
        ? "This revision is already published to every environment."
        : `This revision is already published to ${getUserFormsEnv()}.`;
    }
    if (publishDialogJsonType === "form" && isRawMode) {
      // Raw / non-PDF smart form: bypass the PDF-form registry identity check
      // (these integration types aren't in the registry) and validate the
      // verbatim JSON instead.
      try {
        const json = JSON.parse(rawFormJson || "");
        if (!isRawFormType(json.formJSON?.type)) {
          return "Raw form JSON type is not a smart-form type.";
        }
        if (!json.id || !json.version) {
          return "Raw form JSON is missing a top-level id or version.";
        }
      } catch {
        return "Raw form JSON is invalid — fix it in JSON Preview before publishing.";
      }
      // mevi-sign forms require a non-empty companion integration block.
      let integ;
      try {
        integ = rawIntegrationJson ? JSON.parse(rawIntegrationJson) : null;
      } catch {
        return "Integration JSON is invalid — fix it in JSON Preview before publishing.";
      }
      if (!integ || typeof integ !== "object" || Array.isArray(integ)) {
        return "Add the Integration block in JSON Preview before publishing.";
      }
      if (!integ.data || typeof integ.data !== "object" || Object.keys(integ.data).length === 0) {
        return "Integration `data` is empty — add the mevi-sign config in JSON Preview before publishing.";
      }
    } else if (registry && publishDialogJsonType === "form") {
      const validation = validateFormIdentity(formDetails, registry, { strict: true });
      if (!validation.valid) {
        return formatRegistryValidationMessage(validation, formDetails);
      }
      try {
        const json = JSON.parse(
          generateFormJson({
            fields,
            sections,
            signatures,
            selectedCreators,
            formDetails,
            formUploadSections,
            signatureAnnotations,
            initialsAnnotations,
            additionalFields,
            signerAttributeBindings,
            initialerAttributeBindings,
            subsequentSigning,
            subsequentSigningAttributeBindings,
            isPurchaseContract: formDetails.settings?.type?.type === "isPurchaseContract",
            extraFormSummary,
            extraDealSummary,
            rulesJSON,
    initials,
      initials,
          initials,
            initials,
          }),
        );
        if (json.id !== formDetails.formId || json.formJSON?.type !== formDetails.type) {
          return "Published JSON id/type does not match Form Settings.";
        }
      } catch {
        return "Could not validate publish JSON.";
      }
    }
    return null;
  }, [
    formMeta.id,
    formMeta.published,
    formJsonDirty,
    formRulesDirty,
    offerCompareDirty,
    saveInProgress,
    publishInProgress,
    isSuperAdmin,
    registry,
    publishDialogJsonType,
    formDetails,
    fields,
    sections,
    signatures,
    selectedCreators,
    formUploadSections,
    signatureAnnotations,
    initialsAnnotations,
    additionalFields,
    signerAttributeBindings,
    initialerAttributeBindings,
    subsequentSigning,
    subsequentSigningAttributeBindings,
    extraFormSummary,
    extraDealSummary,
    rulesJSON,
    initials,
    isRawMode,
    rawFormJson,
    rawIntegrationJson,
  ]);

  const handlePublishConfirm = async () => {
    setPublishDialogOpen(false);
    setPublishInProgress(true);
    try {
      const updatedVersion = publishDialogVersion.trim();
      if (updatedVersion !== formDetails.version) {
        setFormDetails((prev) => ({ ...prev, version: updatedVersion }));
      }
      const publishFormDetails = { ...formDetails, version: updatedVersion };

      let json;
      // Companion integrations.json entry, sent only for raw mevi-sign forms.
      let integrationJson;
      if (publishDialogJsonType === "form" && isRawMode) {
        // Raw / non-PDF smart form: publish the verbatim template JSON, NOT the
        // lossy generateFormJson rebuild. Sync the version chosen in the dialog
        // into the raw object so GCS + DB revision agree.
        json = JSON.parse(rawFormJson);
        json.version = updatedVersion;
        // The BE writes this to integrations.json (identity forced to match).
        integrationJson = rawIntegrationJson ? JSON.parse(rawIntegrationJson) : buildIntegrationScaffold(json);
        integrationJson.version = updatedVersion;
      } else if (publishDialogJsonType === "form") {
        json = JSON.parse(generateFormJson({ fields, sections, signatures, selectedCreators, formDetails: publishFormDetails, formUploadSections, signatureAnnotations, initialsAnnotations, additionalFields, signerAttributeBindings, initialerAttributeBindings, subsequentSigning, subsequentSigningAttributeBindings, isPurchaseContract, extraFormSummary, extraDealSummary, rulesJSON, initials }));
      } else if (publishDialogJsonType === "rules") {
        json = JSON.parse(generateRulesJson(formRules, publishFormDetails.formId || "00000000-0000-0000-0000-000000000000"));
      } else {
        json = offerCompare.fields;
      }

      const result = await publishFormData(formMeta.studioFormId, formMeta.id, {
        name: publishDialogName.trim(),
        description: publishDialogDescription.trim(),
        version: publishDialogVersion.trim(),
        env: publishDialogEnv,
        jsonType: publishDialogJsonType,
        json,
        ...(integrationJson != null ? { integrationJson } : {}),
      });
      // Reflect the new publish locally so the toolbar button shows this env+type as taken.
      // Reaching Staging retires the "ready to deploy to STG" flag (the backend clears
      // it on its side too) — mirror that locally so the UI matches without a refetch.
      const clearsStgReady = publishDialogEnv === "Staging";
      setFormMeta((prev) => ({
        ...prev,
        published: [
          ...(prev.published || []),
          {
            env: publishDialogEnv,
            jsonType: publishDialogJsonType,
            publishedAt: new Date().toISOString(),
            publishedBy: getActiveProfile()?.id,
          },
        ],
        ...(clearsStgReady ? { metadata: { ...(prev.metadata || {}), stgReady: false } } : {}),
      }));
      if (clearsStgReady && formMeta.studioFormId) {
        setStgReadyMap((prev) => ({ ...prev, [formMeta.studioFormId]: false }));
        setStudioForms((prev) =>
          prev.map((f) =>
            f.id === formMeta.studioFormId ? { ...f, metadata: { ...(f.metadata || {}), stgReady: false } } : f
          )
        );
      }
      setSnackbar({ open: true, message: "Published successfully.", severity: "success" });
      setPublishResultData(result);
    } catch (error) {
      setSnackbar({ open: true, message: formatApiError(error, "Publish failed."), severity: "error" });
    } finally {
      setPublishInProgress(false);
    }
  };

  const handleViewFormDataTracking = async () => {
    const target = formDataMenuTarget;
    handleFormDataMenuClose();
    setTrackingDialogLoading(true);
    try {
      const events = await getFormDataEvents(selectedStudioForm.id, target.id);
      setTrackingDialogData({
        title: `Tracking: ${target.name || "Revision"}`,
        sections: [{ events: Array.isArray(events) ? events : [] }],
        rawJson: events,
      });
    } catch (err) {
      setSnackbar({ open: true, message: formatApiError(err, "Failed to load tracking events."), severity: "error" });
    } finally {
      setTrackingDialogLoading(false);
    }
  };

  const handleViewFormTracking = async () => {
    const form = assignMenuForm;
    handleAssignMenuClose();
    setTrackingDialogLoading(true);
    try {
      const result = await getFormEvents(form.id);
      const items = Array.isArray(result) ? result : [];
      setTrackingDialogData({
        title: `Tracking: ${form.name || "Form"}`,
        rawJson: result,
        sections: items.map((item) => ({
          studioFormDataId: item.studioFormDataId,
          name: item.name,
          version: item.version,
          updatedAt: item.updatedAt,
          instanceVersion: item.instanceVersion,
          events: Array.isArray(item.events) ? item.events : [],
        })),
      });
    } catch (err) {
      setSnackbar({ open: true, message: formatApiError(err, "Failed to load tracking events."), severity: "error" });
    } finally {
      setTrackingDialogLoading(false);
    }
  };

  const handleDeleteFormClick = () => {
    const form = assignMenuForm;
    handleAssignMenuClose();
    setDeleteTarget({ type: "form", id: form.id, name: form.name });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteFormDataClick = () => {
    const fd = formDataMenuTarget;
    handleFormDataMenuClose();
    setDeleteTarget({ type: "formData", studioFormId: selectedStudioForm.id, id: fd.id, name: fd.name });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const target = deleteTarget;
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
    try {
      if (target.type === "form") {
        await deleteFormFromCloud(target.id);
        fetchStudioForms();
      } else {
        await deleteFormDataFromCloud(target.studioFormId, target.id);
        handleStudioFormClick(selectedStudioForm);
      }
      setSnackbar({ open: true, message: "Deleted.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: formatApiError(err, "Delete failed."), severity: "error" });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const canDeleteFormData = () => {
    if (activeRole === "admin" || activeRole === "json") return true;
    if (activeRole === "annotator") {
      return selectedStudioForm?.assignedTo === getActiveProfile()?.id;
    }
    return false;
  };

  const handleLogout = () => {
    localStorage.removeItem("formStudioRole");
    clearAuth(); // Also clears formStudioEmail and formStudioToken from localStorage
    setActiveRole("");
    setIsSuperAdmin(false);
    setLoginEmail("");
    setLoginPassword("");
    setLoginError("");
    setProfileMenuAnchor(null);
  };

  useEffect(() => {
    if (studioMode === "annotator") {
      setEditingField(null);
    } else {
      setAnnotatorEditingField(null);
    }
  }, [studioMode]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Resolve PDF selection id (field id or option marker key) to field id
  const resolveSelectIdToFieldId = (selectId) => {
    if (!selectId) return null;
    if (fields.some((f) => f.id === selectId)) return selectId;
    const field = fields.find((f) => selectId.startsWith(f.id + "-"));
    return field?.id ?? null;
  };

  const isPdfExtraBindingSelectId = (selectId) => {
    if (!selectId || typeof selectId !== "string") return false;
    if ((additionalFields || []).some((a) => a.id === selectId)) return true;
    if ((signerAttributeBindings || []).some((b) => b.id === selectId)) return true;
    if ((initialerAttributeBindings || []).some((b) => b.id === selectId)) return true;
    if ((subsequentSigningAttributeBindings || []).some((b) => b.id === selectId)) return true;
    if ((signatureAnnotations || []).some((s) => s.id === selectId)) return true;
    if ((initialsAnnotations || []).some((s) => s.id === selectId)) return true;
    return false;
  };

  const handlePdfFieldSelect = (selectId, opts = {}) => {
    if (opts.replaceWithMultiple && Array.isArray(selectId)) {
      const resolved = selectId.map((id) => {
        const fid = resolveSelectIdToFieldId(id);
        if (fid) return fid;
        return isPdfExtraBindingSelectId(id) ? id : null;
      });
      const fieldIds = [...new Set(resolved.filter(Boolean))];
      setPdfSelectedFieldIds(fieldIds);
      const first = selectId.length > 0 ? selectId[0] : null;
      setPdfPrimarySelectId(first);
      setSelectedSignatureId(
        first &&
          ((signatureAnnotations || []).some((s) => s.id === first) ||
            (initialsAnnotations || []).some((s) => s.id === first))
          ? first
          : null,
      );
      return;
    }
    const addToSelection = opts.addToSelection === true;
    const fieldId = resolveSelectIdToFieldId(selectId);
    const extraBinding = isPdfExtraBindingSelectId(selectId);
    if (!fieldId && !extraBinding) {
      setPdfSelectedFieldIds([]);
      setPdfPrimarySelectId(null);
      setSelectedSignatureId(null);
      return;
    }
    const selectionId = extraBinding ? selectId : fieldId;
    if (addToSelection) {
      setPdfSelectedFieldIds((prev) =>
        prev.includes(selectionId) ? prev.filter((id) => id !== selectionId) : [...prev, selectionId]
      );
      setPdfPrimarySelectId(selectId);
    } else {
      setPdfSelectedFieldIds([selectionId]);
      setPdfPrimarySelectId(selectId);
    }
    setSelectedSignatureId(
      (signatureAnnotations || []).some((s) => s.id === selectionId) ||
        (initialsAnnotations || []).some((s) => s.id === selectionId)
        ? selectionId
        : null,
    );
  };

  // Resolve first selected to field id for Field Editor (single-field edit)
  const fieldEditorFieldId = (() => {
    const primary = pdfSelectedFieldIds?.[0];
    if (!primary) return null;
    if ((additionalFields || []).some((a) => a.id === primary)) return null;
    if ((signerAttributeBindings || []).some((b) => b.id === primary)) return null;
    if ((initialerAttributeBindings || []).some((b) => b.id === primary)) return null;
    if ((subsequentSigningAttributeBindings || []).some((b) => b.id === primary)) return null;
    if ((signatureAnnotations || []).some((s) => s.id === primary)) return null;
    if ((initialsAnnotations || []).some((s) => s.id === primary)) return null;
    if (fields.some((f) => f.id === primary)) return primary;
    const field = fields.find((f) => primary.startsWith(f.id + "-"));
    return field?.id ?? null;
  })();

  const fieldEditorPdfExtraId = (() => {
    const primary = pdfSelectedFieldIds?.[0];
    if (!primary) return null;
    if ((additionalFields || []).some((a) => a.id === primary)) return primary;
    if ((signerAttributeBindings || []).some((b) => b.id === primary)) return primary;
    if ((initialerAttributeBindings || []).some((b) => b.id === primary)) return primary;
    if ((subsequentSigningAttributeBindings || []).some((b) => b.id === primary)) return primary;
    if ((signatureAnnotations || []).some((s) => s.id === primary)) return primary;
    if ((initialsAnnotations || []).some((s) => s.id === primary)) return primary;
    return null;
  })();

  const pdfSectionFieldSelections = useMemo(
    () => (pdfSelectedFieldIds || []).filter((id) => fields.some((f) => f.id === id)),
    [pdfSelectedFieldIds, fields],
  );

  // Clear selection when selected fields are removed
  useEffect(() => {
    const stillPresent = pdfSelectedFieldIds.filter((id) => {
      if (fields.some((f) => f.id === id)) return true;
      if ((additionalFields || []).some((a) => a.id === id)) return true;
      if ((signerAttributeBindings || []).some((b) => b.id === id)) return true;
      if ((initialerAttributeBindings || []).some((b) => b.id === id)) return true;
      if ((subsequentSigningAttributeBindings || []).some((b) => b.id === id)) return true;
      if ((signatureAnnotations || []).some((s) => s.id === id)) return true;
      if ((initialsAnnotations || []).some((s) => s.id === id)) return true;
      const resolved = resolveSelectIdToFieldId(id);
      return resolved ? fields.some((f) => f.id === resolved) : false;
    });
    if (stillPresent.length !== pdfSelectedFieldIds.length) {
      setPdfSelectedFieldIds(stillPresent);
      setPdfPrimarySelectId((prev) => {
        if (prev && stillPresent.includes(prev)) return prev;
        const resolved = resolveSelectIdToFieldId(prev);
        if (resolved && stillPresent.includes(resolved)) return prev;
        return stillPresent[0] ?? null;
      });
    }
  }, [
    fields,
    additionalFields,
    signerAttributeBindings,
    initialerAttributeBindings,
    subsequentSigningAttributeBindings,
    signatureAnnotations,
    initialsAnnotations,
    pdfSelectedFieldIds,
  ]);

  // Snap to Field Editor tab when user selects a field on the PDF
  useEffect(() => {
    if (
      fieldEditorFieldId ||
      fieldEditorPdfExtraId ||
      (pdfSelectedFieldIds && pdfSelectedFieldIds.length > 1)
    )
      setActiveTab(0);
  }, [fieldEditorFieldId, fieldEditorPdfExtraId, pdfSelectedFieldIds]);

  const handleThemeToggle = () => {
    // 1. Start Transition Visuals
    const nextMode = mode === "light" ? "dark" : "light";
    setTransitionDirection(
      mode === "light" ? "light-to-dark" : "dark-to-light",
    );
    setIsTransitioning(true);

    // 2. Schedule the Theme Switch (Optimized timing)
    // Faster warp effect: 600ms instead of 1000ms
    setTimeout(() => {
      setMode(nextMode);
    }, 600);

    // 3. Cleanup Transition State
    // Total animation: 1000ms instead of 1800ms
    setTimeout(() => {
      setIsTransitioning(false);
    }, 1000);
  };

  // Check for auto-saved data on mount
  React.useEffect(() => {
    const saved = loadAutoSave();
    if (saved) {
      const dirty = saved.data?.jsonStatus?.dirty || {};
      if (dirty.form || dirty.rules || dirty.compare) {
        setRecoveredData(saved);
        setShowRecoveryBanner(true);
      } else {
        clearAutoSave();
      }
    }
  }, []);

  const isPurchaseContract = formDetails.settings?.type?.type === "isPurchaseContract";
  /** Raw / mevi-sign forms publish stored JSON verbatim and have no PDF to annotate. */
  const isPdfV2Form = !isRawFormType(formDetails.type) && formDetails.settings?.pdfV2 !== false;

  // Form-level daysAfter settings drive the days-after Type dropdown options in the field editor.
  const daysAfterSettings = formDetails.settings?.daysAfter || {};

  // Auto-save form data (same shape as StudioFormData)
  const autoSaveData = {
    ...formMeta,
    fields,
    sections,
    formDetails,
    signatures,
    selectedCreators,
    formUploadSections,
    signatureAnnotations,
    initialsAnnotations,
    formRules,
    offerCompare,
    extraFormSummary,
    extraDealSummary,
    rulesJSON,
    initials,
    additionalFields,
    signerAttributeBindings,
    initialerAttributeBindings,
    subsequentSigning,
    subsequentSigningAttributeBindings,
    // Verbatim template + companion integration JSON for raw / non-PDF smart
    // forms (null for PDF forms). Kept in autosave so a browser refresh can
    // restore both editor panes.
    rawFormJson,
    rawIntegrationJson,
    jsonStatus: {
      dirty: { form: formJsonDirty, rules: formRulesDirty, compare: offerCompareDirty },
    },
  };
  useAutoSave(autoSaveData, isFormLoaded);

  const { trackSave, trackLoad } = useMetricsTracker({
    studioFormId: formMeta.studioFormId,
    studioFormDataId: formMeta.id,
    profileId: getActiveProfile()?.id,
    formJsonDirty,
    formRulesDirty,
    offerCompareDirty,
  });

  // formRules helpers
  const DEFAULT_RULES = { required: false, notes: "", sectionNumber: "", sectionName: "", sectionDescription: "", sectionNotes: "" };
  const DEFAULT_FORM_RULES_FIELD = { rules: { ...DEFAULT_RULES }, pdfLocation: null, options: [] };

  const getFormRulesField = (attrName) => {
    const entry = formRules.fields.find((f) => f.attributeName === attrName);
    return entry || { ...DEFAULT_FORM_RULES_FIELD, attributeName: attrName, rules: { ...DEFAULT_RULES } };
  };

  const updateFormRulesField = (attrName, updates) => {
    setFormRules((prev) => {
      const exists = prev.fields.find((f) => f.attributeName === attrName);
      if (exists) {
        return { ...prev, fields: prev.fields.map((f) =>
          f.attributeName === attrName
            ? { ...f, ...updates, rules: updates.rules ? { ...f.rules, ...updates.rules } : f.rules }
            : f
        )};
      }
      const newField = {
        attributeName: attrName,
        rules: { ...DEFAULT_RULES, ...(updates.rules || {}) },
        pdfLocation: updates.pdfLocation ?? null,
        options: updates.options || [],
      };
      return { ...prev, fields: [...prev.fields, newField] };
    });
    setFormRulesDirty(true);
  };

  const removeFormRulesField = (attrName) => {
    setFormRules((prev) => ({ ...prev, fields: prev.fields.filter((f) => f.attributeName !== attrName) }));
    setFormRulesDirty(true);
  };

  // offerCompare helpers
  const isFieldInOfferCompare = (key) =>
    offerCompare.fields.some((f) => f.key === key);

  const addFieldToOfferCompare = (key, label) => {
    setOfferCompare((prev) => ({
      ...prev,
      fields: [
        ...prev.fields.filter((f) => f.key !== key),
        { key, label: label || "", formSummaryFields: [key] },
      ],
    }));
    setOfferCompareDirty(true);
  };

  const removeFieldFromOfferCompare = (key) => {
    setOfferCompare((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => f.key !== key),
    }));
    setOfferCompareDirty(true);
  };

  const updateOfferCompareField = (key, updates) => {
    setOfferCompare((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => f.key === key ? { ...f, ...updates } : f),
    }));
    setOfferCompareDirty(true);
  };

  const handleFormRulesChange = (updater) => {
    setFormRules(updater);
    setFormRulesDirty(true);
  };

  useEffect(() => {
    if (pdfFile) {
      savePdfBlob(pdfFile).catch((error) => {
        console.error("Failed to persist PDF:", error);
      });
    }
  }, [pdfFile]);

  // Trigger glow effect when form is loaded from JSON and button is visible
  React.useEffect(() => {
    if (importedFromJson && isFormLoaded && !pdfFile) {
      // Delay to ensure button is rendered after transition animations
      // HomePage has 1400ms delay, so we add 800ms more for button render
      const glowTimer = setTimeout(() => {
        setShowPdfButtonGlow(true);
        setImportedFromJson(false); // Reset flag
      }, 800); // Wait for button to render after form loads
      return () => clearTimeout(glowTimer);
    }
  }, [importedFromJson, isFormLoaded, pdfFile]);

  // Auto-fade glow effect after 5 seconds (then 1 second fade)
  React.useEffect(() => {
    if (showPdfButtonGlow) {
      const timer = setTimeout(() => {
        setShowPdfButtonGlow(false);
      }, 6000); // Glow for 5 seconds visible + 1 second fade = 6 seconds total
      return () => clearTimeout(timer);
    }
  }, [showPdfButtonGlow]);

  // Load default line height from localStorage (local-only, keyed by form)
  useEffect(() => {
    const key = LINE_HEIGHT_STORAGE_KEY_PREFIX + (formMeta.studioFormId || "local");
    const raw = localStorage.getItem(key);
    if (raw !== null) {
      const n = Number(raw);
      if (!Number.isNaN(n) && n > 0) setDefaultLineHeightPx(n);
    } else {
      setDefaultLineHeightPx(null);
    }
  }, [formMeta.studioFormId]);

  const handleRestoreData = async () => {
    if (recoveredData && recoveredData.data) {
      const {
        id, studioFormId, name, description, status, instanceVersion, createdAt, updatedAt,
        fields: savedFields,
        sections: savedSections,
        formDetails: savedFormDetails,
        signatures: savedSignatures,
        selectedCreators: savedCreators,
        formUploadSections: savedFormUploadSections,
        signatureAnnotations: savedSignatureAnnotations,
        initialsAnnotations: savedInitialsAnnotations,
        formRules: savedFormRules,
        offerCompare: savedOfferCompare,
        jsonStatus: savedJsonStatus,
      } = recoveredData.data;

      setFormMeta({
        id: id || null,
        studioFormId: studioFormId || null,
        name: name || "",
        description: description || "",
        status: status || "draft",
        instanceVersion: instanceVersion || 0,
        createdAt: createdAt || null,
        updatedAt: updatedAt || null,
        published: [], // recovered draft is dirty; publish stays blocked until saved
        metadata: {},
        createdBy: null,
      });

      if (savedSections) {
        const { sections: dedupedRecovery, sectionIdMap: recoveryMap } = deduplicateSectionsByAttributeName(savedSections);
        setSections(dedupedRecovery.length ? dedupedRecovery : savedSections);
        if (savedFields) {
          setFields(
            savedFields.map((f) => ({
              ...f,
              sectionId: recoveryMap[f.sectionId] ?? f.sectionId,
            }))
          );
        }
      } else if (savedFields) setFields(savedFields);
      if (savedFormDetails) setFormDetails(savedFormDetails);
      // Raw / non-PDF smart form: restore the verbatim template JSON so the
      // JSON-preview editor isn't empty after a refresh (isRawMode derives from
      // the restored formDetails.type and pins the JSON tab). null for PDF forms.
      setRawFormJson(recoveredData.data.rawFormJson ?? null);
      setRawIntegrationJson(recoveredData.data.rawIntegrationJson ?? null);
      if (savedSignatures) {
        const { signatures: sigStripped, bindings } = resolveSignerAttributeBindingsForLoad(
          savedSignatures,
          recoveredData.data.signerAttributeBindings,
        );
        setSignatures(normalizeSignatures(sigStripped));
        setSignerAttributeBindings(bindings);
      } else if (recoveredData.data.selectedRoles) {
        // Legacy: convert role key array to full signatures object
        const template = buildSignaturesTemplate();
        setSignatures(normalizeSignatures(Object.fromEntries(
          recoveredData.data.selectedRoles.map(key => [key, template[key]]).filter(([, v]) => !!v)
        )));
      }
      if (savedCreators) setSelectedCreators(savedCreators);
      if (savedFormUploadSections)
        setFormUploadSections(savedFormUploadSections);
      if (savedSignatureAnnotations)
        setSignatureAnnotations(savedSignatureAnnotations);
      if (savedInitialsAnnotations)
        setInitialsAnnotations(savedInitialsAnnotations);
      if (savedFormRules)
        setFormRules(savedFormRules);
      if (savedOfferCompare)
        setOfferCompare(savedOfferCompare);
      setExtraFormSummary(recoveredData.data.extraFormSummary || {});
      setExtraDealSummary(recoveredData.data.extraDealSummary || {});
      setRulesJSON(recoveredData.data.rulesJSON ?? null);
      setInitials(recoveredData.data.initials ?? null);
      setAdditionalFields(sanitizeAdditionalPlacements(recoveredData.data.additionalFields ?? [], { ...recoveredData.data, fields: savedFields || [] }));
      setInitialerAttributeBindings(recoveredData.data.initialerAttributeBindings ?? []);
      setSubsequentSigning(recoveredData.data.subsequentSigning ?? {});
      setSubsequentSigningAttributeBindings(recoveredData.data.subsequentSigningAttributeBindings ?? []);
      if (!savedSignatures) {
        setSignerAttributeBindings(recoveredData.data.signerAttributeBindings ?? []);
      }
      setInitialerAttributeBindings(recoveredData.data.initialerAttributeBindings ?? []);

      // Restore dirty state from auto-save (survives browser refresh)
      const dirty = savedJsonStatus?.dirty || {};
      setFormJsonDirty(dirty.form === true);
      setFormRulesDirty(dirty.rules === true);
      setOfferCompareDirty(dirty.compare === true);

      setIsFormLoaded(true); // Ensure the form transition happens
      setShowRecoveryBanner(false);
      setRecoveredData(null);
      trackLoad("local");

      try {
        const savedPdf = await loadPdfBlob();
        if (savedPdf?.blob) {
          try {
            const restoredFile = new File(
              [savedPdf.blob],
              savedPdf.name || "document.pdf",
              {
                type: savedPdf.type || "application/pdf",
                lastModified: savedPdf.lastModified || Date.now(),
              },
            );
            setPdfFile(restoredFile);
          } catch (fileError) {
            console.error("Failed to rebuild PDF File:", fileError);
            setPdfFile(savedPdf.blob);
          }
        }
      } catch (error) {
        console.error("Failed to restore saved PDF:", error);
      }
    }
  };

  const handleDiscardRecovery = () => {
    clearAutoSave();
    clearSavedPdf();
    setShowRecoveryBanner(false);
    setRecoveredData(null);
  };

  const handlePdfUpload = (file) => {
    setPdfFile(file);
    setIsFormLoaded(true);
    savePdfBlob(file).catch((error) => {
      console.error("Failed to persist PDF:", error);
    });
  };

  // Swap the PDF blob while preserving fields, sections, formRules, offerCompare,
  // signatures, signatureAnnotations, additionalFields, signerAttributeBindings,
  // subsequentSigning, and all formDetails. PDF content is part of Form JSON
  // output (via pdfB64/pdfName), so flip formJsonDirty so the user re-saves.
  const handlePdfReplace = (file) => {
    if (!file) return;
    setPdfFile(file);
    setIsFormLoaded(true);
    setFormJsonDirty(true);
    savePdfBlob(file).catch((error) => {
      console.error("Failed to persist replacement PDF:", error);
    });
  };

  const handleAnnotatorSaveField = (updatedField) => {
    setFormJsonDirty(true);
    const { rules: savedRulesData, offerCompare: savedOC, ...fieldWithoutIsolated } = updatedField;
    if (fieldWithoutIsolated.metadata) {
      const meta = { ...fieldWithoutIsolated.metadata };
      delete meta.pdfLocationContinuations;
      fieldWithoutIsolated.metadata = meta;
    }

    if (fieldWithoutIsolated.attributeName) {
      updateFormRulesField(fieldWithoutIsolated.attributeName, {
        rules: {
          ...(savedRulesData || {}),
          required: !!fieldWithoutIsolated.validation?.required,
        },
        pdfLocation: fieldWithoutIsolated.metadata?.pdfLocation || null,
        options: buildFormRulesOptions(fieldWithoutIsolated.metadata),
      });
      if (savedOC) {
        addFieldToOfferCompare(fieldWithoutIsolated.attributeName, fieldWithoutIsolated.label || "");
      } else {
        removeFieldFromOfferCompare(fieldWithoutIsolated.attributeName);
      }
    }

    if (identifySectionsMode && selectedSectionFieldIds.length > 0) {
      const sectionRulesUpdate = {
        sectionNumber: savedRulesData?.sectionNumber || "",
        sectionNotes: savedRulesData?.sectionNotes || "",
        sectionName: savedRulesData?.sectionName || "",
        sectionDescription: savedRulesData?.sectionDescription || "",
      };
      fields
        .filter((f) => f.id !== updatedField.id && selectedSectionFieldIds.includes(f.id) && f.attributeName)
        .forEach((f) => updateFormRulesField(f.attributeName, { rules: sectionRulesUpdate }));
    }

    setFields((prevFields) =>
      prevFields.map((field) =>
        field.id === updatedField.id ? fieldWithoutIsolated : field,
      ),
    );

    const savedAttr = fieldWithoutIsolated.attributeName;
    if (updatedField.id && savedAttr) {
      setSignerAttributeBindings((prev) =>
        prev.map((b) =>
          b.linkedFieldId === updatedField.id ? { ...b, attributeName: savedAttr } : b,
        ),
      );
      setAdditionalFields((prev) =>
        prev.map((e) =>
          e.linkedFieldId === updatedField.id ? { ...e, attributeName: savedAttr } : e,
        ),
      );
      setSubsequentSigningAttributeBindings((prev) =>
        prev.map((b) =>
          b.linkedFieldId === updatedField.id ? { ...b, attributeName: savedAttr } : b,
        ),
      );
    }

    if (pdfFile) {
      const itemsNeedingPlacement = buildOptionAnnotationItems(fieldWithoutIsolated).filter(
        (item) => !getOptionPdfLocation(fieldWithoutIsolated, item)?.page,
      );
      if (itemsNeedingPlacement.length > 0) {
        // Replaces any in-progress option-placement queue (same as starting a new placement flow).
        setOptionAnnotationState({ fieldId: fieldWithoutIsolated.id, items: itemsNeedingPlacement });
        setShowOptionAnnotationPrompt(true);
      }
    }

    if (fieldWithoutIsolated.excludeFromFormJSON) {
      setAdditionalFields((prev) => {
        const entries = [];
        const mainLoc = fieldWithoutIsolated.metadata?.pdfLocation;
        if (mainLoc) {
          entries.push({
            id: `additional-${Date.now()}-${prev.length}`,
            linkedFieldId: fieldWithoutIsolated.id,
            attributeName: fieldWithoutIsolated.attributeName,
            pdfLocation: { ...mainLoc },
            lines: fieldWithoutIsolated.annotationMultiline ?? mainLoc.lines ?? 0,
          });
        }
        const overflowFields = fields.filter((f) => f.isOverflow === fieldWithoutIsolated.attributeName);
        overflowFields.forEach((of) => {
          if (of.metadata?.pdfLocation) {
            entries.push({
              id: `additional-${Date.now()}-${prev.length + entries.length}`,
              linkedFieldId: of.id,
              attributeName: of.attributeName,
              pdfLocation: { ...of.metadata.pdfLocation },
              lines: of.annotationMultiline ?? of.metadata.pdfLocation?.lines ?? 0,
            });
          }
        });
        return [...prev, ...entries];
      });
    }
    setAnnotatorEditingField(null);
  };

  /** Assign a drawn PDF rectangle to an existing fields[] row (primary metadata.pdfLocation). */
  const handleAssignPdfLocationToExistingField = (fieldId) => {
    const loc = pendingPdfLocation;
    if (!loc || !fieldId) return;
    const field = fieldsRef.current.find((f) => f.id === fieldId);
    if (!field) return;

    const mergedLocation = {
      ...(field.metadata?.pdfLocation || {}),
      ...loc,
    };

    const rules = getFormRulesField(field.attributeName)?.rules ?? {};
    const updatedField = {
      ...field,
      metadata: {
        ...(field.metadata || {}),
        pdfLocation: { ...mergedLocation },
      },
      rules,
      offerCompare: isFieldInOfferCompare(field.attributeName),
      ...(studioMode === "annotator" ? { touchedByAnnotator: true } : {}),
    };

    handleAnnotatorSaveField(updatedField);

    setAdditionalFields((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setSignerAttributeBindings((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setSignatureAnnotations((prev) =>
      prev.map((e) => {
        if (e.linkedFieldId !== fieldId) return e;
        const pl = { ...mergedLocation };
        delete pl.lines;
        return { ...e, pdfLocation: pl };
      }),
    );

    setPendingPdfLocation(null);
    setPendingFieldType(null);
    setCreateFieldDialogOpen(false);
    setCreateFieldDialogDefaultAssignTab(false);
    setPdfSelectedFieldIds([fieldId]);
    setPdfPrimarySelectId(fieldId);
  };

  /** JSON Field Editor: move a placed box from a temporary/source field onto an existing unplaced field; remove source row. */
  const handleAttachPlacedAnnotationToField = (sourceFieldId, targetFieldId) => {
    if (!sourceFieldId || !targetFieldId || sourceFieldId === targetFieldId) return;
    const source = fieldsRef.current.find((f) => f.id === sourceFieldId);
    const target = fieldsRef.current.find((f) => f.id === targetFieldId);
    if (!source || !target || !target.attributeName) return;
    if (source.type !== target.type) return;
    if (!ATTACH_COMPATIBLE_FIELD_TYPES.has(source.type)) return;
    if (source.isOverflow) return;
    if (target.isOverflow) return;
    const srcPage = source.metadata?.pdfLocation?.page;
    if (srcPage == null) return;
    if (target.metadata?.pdfLocation?.page != null) return;

    const mergedLocation = { ...(source.metadata?.pdfLocation || {}) };
    if ((source.annotationMultiline ?? 0) > 0) {
      mergedLocation.lines = source.annotationMultiline;
    } else if (mergedLocation.lines == null && source.metadata?.pdfLocation?.lines != null) {
      mergedLocation.lines = source.metadata.pdfLocation.lines;
    }
    const lineCountForField =
      (source.annotationMultiline ?? 0) > 0
        ? source.annotationMultiline
        : mergedLocation.lines != null && Number(mergedLocation.lines) > 0
          ? Number(mergedLocation.lines)
          : target.annotationMultiline ?? 0;

    const rules = getFormRulesField(target.attributeName)?.rules ?? {};
    const updatedTarget = {
      ...target,
      metadata: {
        ...(target.metadata || {}),
        pdfLocation: { ...mergedLocation },
      },
      annotationMultiline: lineCountForField,
      rules,
      offerCompare: isFieldInOfferCompare(target.attributeName),
    };

    handleAnnotatorSaveField(updatedTarget);

    setAdditionalFields((prev) =>
      prev.map((e) =>
        e.linkedFieldId === targetFieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setSignerAttributeBindings((prev) =>
      prev.map((e) =>
        e.linkedFieldId === targetFieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setInitialerAttributeBindings((prev) =>
      prev.map((e) =>
        e.linkedFieldId === targetFieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setSignatureAnnotations((prev) =>
      prev.map((e) => {
        if (e.linkedFieldId !== targetFieldId) return e;
        const pl = { ...mergedLocation };
        delete pl.lines;
        return { ...e, pdfLocation: pl };
      }),
    );
    setInitialsAnnotations((prev) =>
      prev.map((e) => {
        if (e.linkedFieldId !== targetFieldId) return e;
        const pl = { ...mergedLocation };
        delete pl.lines;
        return { ...e, pdfLocation: pl };
      }),
    );

    handleAnnotatorDeleteField(sourceFieldId);

    setPdfSelectedFieldIds([targetFieldId]);
    setPdfPrimarySelectId(targetFieldId);
  };

  const handleSavePdfExtraBinding = useCallback((updatedField) => {
    setFormJsonDirty(true);
    const id = updatedField.id;
    const rawTarget = updatedField._pdfExtraOutputTarget;
    const rawPersonaSubtype = updatedField._pdfPersonaSubtype;
    if (rawTarget === "standard") {
      setAdditionalFields((prev) => prev.filter((e) => e.id !== id));
      setSignerAttributeBindings((prev) => prev.filter((e) => e.id !== id));
      setInitialerAttributeBindings((prev) => prev.filter((e) => e.id !== id));
      setSignatureAnnotations((prev) => prev.filter((e) => e.id !== id));
      setInitialsAnnotations((prev) => prev.filter((e) => e.id !== id));
      setSubsequentSigningAttributeBindings((prev) => prev.filter((e) => e.id !== id));
      setPdfSelectedFieldIds([]);
      setPdfPrimarySelectId(null);
      setSelectedSignatureId(null);
      return;
    }
    /** Legacy UI values + current: additionalField / personaData (+ subtype) */
    let effectiveTarget = rawTarget;
    let personaSubtype = rawPersonaSubtype;
    if (rawTarget === "additionalFieldsJSON") effectiveTarget = "additionalField";
    if (rawTarget === "signaturesJSON") {
      effectiveTarget = "personaData";
      personaSubtype = personaSubtype || "attributePlacements";
    }
    if (rawTarget === "signaturesJSON_signatureImage") {
      effectiveTarget = "personaData";
      personaSubtype = "signatureImage";
    }
    if (rawTarget === "initialsJSON") {
      effectiveTarget = "initialsData";
      personaSubtype = personaSubtype || "attributePlacements";
    }
    if (rawTarget === "initialsJSON_signatureImage") {
      effectiveTarget = "initialsData";
      personaSubtype = "signatureImage";
    }
    if (rawTarget === "subsequentSigningJSON") {
      effectiveTarget = "subsequentSigning";
    }
    /** additionalFieldsJSON | signer row fields[] (legacy: additionalFields[]) | signature image pdfLocation | subsequentSigningJSON */
    const normalized =
      effectiveTarget === "personaData" && personaSubtype === "signatureImage"
        ? "sigImage"
        : effectiveTarget === "personaData"
          ? "sigAttrs"
      : effectiveTarget === "initialsData" && personaSubtype === "signatureImage"
          ? "initImage"
      : effectiveTarget === "initialsData"
          ? "initAttrs"
          : effectiveTarget === "subsequentSigning"
            ? "subsequentSigning"
            : "additionalFieldsJSON";
    const attr = updatedField.attributeName ?? "";
    const linkedFieldId = updatedField.linkedFieldId;
    const locRaw = updatedField.metadata?.pdfLocation;
    const lineCount =
      locRaw?.lines != null && Number(locRaw.lines) > 0 ? Number(locRaw.lines) : 0;
    const loc = locRaw ? { ...locRaw } : null;
    if (loc) {
      if (lineCount > 0) loc.lines = lineCount;
      else delete loc.lines;
    }

    const compound = updatedField._pdfSyntheticSignerCompound || "";
    const tab = compound.indexOf("\t");
    const roleKey = tab >= 0 ? compound.slice(0, tab) : "";
    const signerKey = tab >= 0 ? compound.slice(tab + 1) : "";

    setAdditionalFields((prevAf) => {
      const fromAf = prevAf.find((e) => e.id === id);
      const stripped = prevAf.filter((e) => e.id !== id);
      if (normalized !== "additionalFieldsJSON") return stripped;
      const lf = linkedFieldId ?? fromAf?.linkedFieldId;
      stripped.push({
        id,
        ...(lf ? { linkedFieldId: lf } : {}),
        attributeName: attr,
        label: updatedField.label ?? fromAf?.label ?? "",
        pdfLocation: loc || fromAf?.pdfLocation,
        lines: lineCount,
      });
      return stripped;
    });

    setSignerAttributeBindings((prevSb) => {
      const fromSb = prevSb.find((e) => e.id === id);
      const stripped = prevSb.filter((e) => e.id !== id);
      if (normalized !== "sigAttrs") return stripped;
      const lf = linkedFieldId ?? fromSb?.linkedFieldId;
      const def = snapshotSignerFieldDef(updatedField) ?? fromSb?.def;
      stripped.push({
        id,
        ...(lf ? { linkedFieldId: lf } : {}),
        roleKey: roleKey || fromSb?.roleKey || "",
        signerKey: signerKey || fromSb?.signerKey || "",
        attributeName: attr,
        label: updatedField.label ?? fromSb?.label ?? "",
        pdfLocation: loc || fromSb?.pdfLocation,
        lines: lineCount,
        ...(def ? { def } : {}),
      });
      return stripped;
    });

    setSignatureAnnotations((prevAnn) => {
      const fromAnn = prevAnn.find((e) => e.id === id);
      const stripped = prevAnn.filter((e) => e.id !== id);
      if (normalized !== "sigImage") return stripped;
      const baseLoc = loc || fromAnn?.pdfLocation || null;
      const pdfLocation = baseLoc ? { ...baseLoc } : {};
      delete pdfLocation.lines;
      const lfAnn = linkedFieldId ?? fromAnn?.linkedFieldId;
      stripped.push({
        id,
        ...(lfAnn ? { linkedFieldId: lfAnn } : {}),
        roleKey: roleKey || fromAnn?.roleKey || "",
        signerKey: signerKey || fromAnn?.signerKey || "",
        pdfLocation,
      });
      return stripped;
    });

    setInitialerAttributeBindings((prevIb) => {
      const fromIb = prevIb.find((e) => e.id === id);
      const stripped = prevIb.filter((e) => e.id !== id);
      if (normalized !== "initAttrs") return stripped;
      const lf = linkedFieldId ?? fromIb?.linkedFieldId;
      const def = snapshotSignerFieldDef(updatedField) ?? fromIb?.def;
      stripped.push({
        id,
        ...(lf ? { linkedFieldId: lf } : {}),
        roleKey: roleKey || fromIb?.roleKey || "",
        signerKey: signerKey || fromIb?.signerKey || "",
        attributeName: attr,
        label: updatedField.label ?? fromIb?.label ?? "",
        pdfLocation: loc || fromIb?.pdfLocation,
        lines: lineCount,
        ...(def ? { def } : {}),
      });
      return stripped;
    });

    setInitialsAnnotations((prevAnn) => {
      const fromAnn = prevAnn.find((e) => e.id === id);
      const stripped = prevAnn.filter((e) => e.id !== id);
      if (normalized !== "initImage") return stripped;
      const baseLoc = loc || fromAnn?.pdfLocation || null;
      const pdfLocation = baseLoc ? { ...baseLoc } : {};
      delete pdfLocation.lines;
      const lfAnn = linkedFieldId ?? fromAnn?.linkedFieldId;
      stripped.push({
        id,
        ...(lfAnn ? { linkedFieldId: lfAnn } : {}),
        roleKey: roleKey || fromAnn?.roleKey || "",
        signerKey: signerKey || fromAnn?.signerKey || "",
        pdfLocation,
      });
      return stripped;
    });

    setSubsequentSigningAttributeBindings((prevSb) => {
      const fromSb = prevSb.find((e) => e.id === id);
      const stripped = prevSb.filter((e) => e.id !== id);
      if (normalized !== "subsequentSigning") return stripped;
      const lf = linkedFieldId ?? fromSb?.linkedFieldId;
      const def = snapshotSignerFieldDef(updatedField) ?? fromSb?.def;
      stripped.push({
        id,
        ...(lf ? { linkedFieldId: lf } : {}),
        roleKey: roleKey || fromSb?.roleKey || "",
        signerKey: signerKey || fromSb?.signerKey || "",
        attributeName: attr,
        label: updatedField.label ?? fromSb?.label ?? "",
        pdfLocation: loc || fromSb?.pdfLocation,
        lines: lineCount,
        ...(def ? { def } : {}),
      });
      return stripped;
    });

    setPdfSelectedFieldIds([]);
    setPdfPrimarySelectId(null);
    setSelectedSignatureId(null);
  }, []);

  const handleDeletePdfExtraBinding = useCallback((fieldOrId) => {
    const id =
      typeof fieldOrId === "object" && fieldOrId !== null ? fieldOrId.id : fieldOrId;
    setFormJsonDirty(true);
    setAdditionalFields((prev) => prev.filter((e) => e.id !== id));
    setSignerAttributeBindings((prev) => prev.filter((e) => e.id !== id));
    setInitialerAttributeBindings((prev) => prev.filter((e) => e.id !== id));
    setSignatureAnnotations((prev) => prev.filter((e) => e.id !== id));
    setInitialsAnnotations((prev) => prev.filter((e) => e.id !== id));
    setSubsequentSigningAttributeBindings((prev) => prev.filter((e) => e.id !== id));
    setPdfSelectedFieldIds([]);
    setPdfPrimarySelectId(null);
    setSelectedSignatureId(null);
  }, []);

  const handlePdfClick = (page, x, y, width, height, chosenType) => {
    setFormJsonDirty(true);
    if (optionAnnotationState) {
      const nextLocation = { page, x, y, width, height };
      const placedItem = optionAnnotationState.items[0];
      const fieldId = optionAnnotationState.fieldId;

      // Apply the placed location to the field synchronously so we have the final state
      const currentField = fields.find((f) => f.id === fieldId);
      const updatedField = currentField
        ? applyOptionLocation(currentField, placedItem, nextLocation)
        : null;

      setFields((prevFields) =>
        prevFields.map((field) => {
          if (field.id !== fieldId) return field;
          return updatedField || applyOptionLocation(field, placedItem, nextLocation);
        }),
      );

      // Sync option pdfLocation to formRules
      if (currentField?.attributeName) {
        setFormRules((prev) => {
          const entry = prev.fields.find((f) => f.attributeName === currentField.attributeName);
          if (!entry) return prev;
          const updatedOptions = (entry.options || []).map((opt) => {
            if (placedItem.kind === "option" && opt.kind === "option" && opt.index === placedItem.index) {
              return { ...opt, pdfLocation: nextLocation };
            }
            if (opt.kind === placedItem.kind && opt.kind !== "option") {
              return { ...opt, pdfLocation: nextLocation };
            }
            return opt;
          });
          return {
            ...prev,
            fields: prev.fields.map((f) =>
              f.attributeName === currentField.attributeName ? { ...f, options: updatedOptions } : f,
            ),
          };
        });
      }

      if (optionAnnotationState.items.length > 1) {
        setOptionAnnotationState({
          ...optionAnnotationState,
          items: optionAnnotationState.items.slice(1),
        });
        setShowOptionAnnotationPrompt(true);
      } else {
        // All options placed — auto-save the field, no manual Save click needed
        setOptionAnnotationState(null);
        setShowOptionAnnotationPrompt(false);
        if (updatedField) {
          handleAnnotatorSaveField(updatedField);
          setPdfSelectedFieldIds([fieldId]);
          setPdfPrimarySelectId(fieldId);
        }
      }
      return;
    }

    if (signaturePlacementMode) {
      if (!signatureRoleKey || !signatureSignerKey) {
        return;
      }
      const pdfLocation = { page, x, y, width, height };
      setSignatureAnnotations((prev) => {
        const next = [
          ...prev,
          {
            id: createStudioAnnotationId("signature"),
            roleKey: signatureRoleKey,
            signerKey: signatureSignerKey,
            pdfLocation,
          },
        ];
        const nextKeys = getAvailableSignerKeys(signatureRoleKey, next, signatures);
        setSignatureSignerKey(nextKeys[0] || "");
        return next;
      });
      return;
    }

    if (initialsPlacementMode) {
      if (!initialsRoleKey || !initialsSignerKey) {
        return;
      }
      const pdfLocation = { page, x, y, width, height };
      setInitialsAnnotations((prev) => {
        const next = [
          ...prev,
          {
            id: createStudioAnnotationId("initials"),
            roleKey: initialsRoleKey,
            signerKey: initialsSignerKey,
            pdfLocation,
          },
        ];
        const nextKeys = getAvailableInitialsSignerKeys(initialsRoleKey, signatures, initials, next);
        setInitialsSignerKey(nextKeys[0] || "");
        return next;
      });
      return;
    }

    const pdfLocation = { page, x, y, width, height };
    setPendingPdfLocation(pdfLocation);
    setPendingFieldType(chosenType || null);
    setAnnotatorEditingField(null);
    setAnnotatorTab(0);
    setCreateFieldDialogDefaultAssignTab(false);
    setCreateFieldDialogOpen(true);
    if (studioMode === "json") setActiveTab(0); // Field Editor tab so create form is in focus
  };

  const handleCreateField = (newField) => {
    setFormJsonDirty(true);
    const optionTypes = [
      "checkbox",
      "checkboxWithOther",
      "radio",
      "radioWithOther",
      "decision",
      "decisionWithOther",
    ];
    const isOptionType = optionTypes.includes(newField.type);
    const isAnnotatorCreate = studioMode === "annotator";
    if (isAnnotatorCreate) {
      newField = {
        ...newField,
        touchedByAnnotator: true,
      };
    }

    // If we have a pending PDF location, add it to metadata
    if (pendingPdfLocation && !isOptionType) {
      newField.metadata = {
        ...newField.metadata,
        pdfLocation: pendingPdfLocation,
      };
    }

    // Extract rules and offerCompare from the new field, sync to isolated state
    const { rules: newFieldRulesData, offerCompare: newFieldOC, ...fieldWithoutIsolated } = newField;
    newField = fieldWithoutIsolated;

    registerFormRulesForCommittedField(newField, newFieldRulesData, !!newFieldOC);

    // Propagate section rules to selected fields in identify-sections mode
    if (identifySectionsMode && selectedSectionFieldIds.length > 0) {
      const sectionRulesUpdate = {
        sectionNumber: newFieldRulesData?.sectionNumber || "",
        sectionNotes: newFieldRulesData?.sectionNotes || "",
        sectionName: newFieldRulesData?.sectionName || "",
        sectionDescription: newFieldRulesData?.sectionDescription || "",
      };
      fields
        .filter((f) => selectedSectionFieldIds.includes(f.id) && f.attributeName)
        .forEach((f) => updateFormRulesField(f.attributeName, { rules: sectionRulesUpdate }));
    }

    if (isOptionType) {
      appendOptionLikeFieldToFieldsAndStartQueue(newField);
    } else {
      setFields((prev) => [...prev, newField]);
    }
    setPendingPdfLocation(null); // Reset pending location
    setPendingFieldType(null);
  };

  /**
   * Instantly creates a field from the annotator popup (no dialog needed).
   * type: "input" | "checkbox" | "decision" | "decisionWithOther"
   * pdfLocation: { page, x, y, width, height }
   * options: array of label strings (checkbox / decision / decisionWithOther)
   */
  const _toCamelCase = (str) =>
    str
      .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
      .replace(/^(.)/, (m) => m.toLowerCase());

  const handleInstantCreate = (type, pdfLocation, options = []) => {
    setFormJsonDirty(true);
    const fieldId = `field-${Date.now()}`;
    const attributeName = `field${Date.now()}`;

    const defaultRules = {
      notes: "",
      sectionNumber: "",
      sectionName: "",
      sectionDescription: "",
      sectionNotes: "",
    };

    let metadata = null;
    if (type === "checkbox") {
      metadata = {
        options: options.map((text, i) => ({
          text,
          value: _toCamelCase(text) || `option${i + 1}`,
        })),
      };
    } else if (type === "decision") {
      metadata = {
        options: options.map((text, i) => ({
          text,
          value: _toCamelCase(text) || `option${i + 1}`,
          goTo: null,
        })),
      };
    } else if (type === "decisionWithOther") {
      metadata = {
        options: options.map((text, i) => ({
          text,
          value: _toCamelCase(text) || `option${i + 1}`,
          goTo: null,
        })),
        other: {
          decisionValue: "other",
          input: {
            attributeName: "otherInput",
            placeholder: "Please specify...",
            validation: {},
          },
        },
      };
    } else {
      metadata = { pdfLocation };
    }

    const newField = {
      id: fieldId,
      label: "",
      type,
      attributeName,
      placeholder: null,
      constraint: null,
      constraintId: null,
      info: null,
      hint: null,
      eventTrigger: null,
      value: null,
      validation: { min: null, max: null, minLength: null, maxLength: null, patterns: null, required: null },
      validationErrorMessage: null,
      fieldNotes: "",
      rules: defaultRules,
      dealSummary: undefined,
      formSummary: undefined,
      metadata,
      touchedByAnnotator: true,
    };

    const optionInstantTypes = ["checkbox", "decision", "decisionWithOther"];
    if (optionInstantTypes.includes(type)) {
      registerFormRulesForCommittedField(newField, newField.rules, false);
      appendOptionLikeFieldToFieldsAndStartQueue(newField);
    } else {
      setFields((prev) => [...prev, newField]);
    }

    // Open EditFieldDialog immediately so annotator can fill in label
    setCreateFieldDialogOpen(false);
    setPendingPdfLocation(null);
    setPendingFieldType(null);
    setAnnotatorEditingField(newField);
    setPdfSelectedFieldIds([newField.id]);
    setPdfPrimarySelectId(newField.id);
  };

  const handleOpenCreateDialog = () => {
    setPendingPdfLocation(null);
    setAnnotatorEditingField(null);
    setCreateFieldDialogOpen(true);
  };

  const handleFileUpload = (fields) => {
    setFormJsonDirty(true);
    setFields(fields);
    // Trigger transition effect
    setIsFormLoaded(true);
    // Reset JSON import flag (this is CSV upload, not JSON)
    setImportedFromJson(false);
  };

  const applyImportedFormData = (importedData, formDetailsOverride = null, rawSource = null) => {
    // Extract all data from imported form
    const {
      fields: importedFields,
      sections: importedSections,
      formDetails: importedFormDetails,
      signatures: importedSignatures,
      selectedCreators: importedCreators,
      formUploadSections: importedUploadSections,
      extraFormSummary: importedExtraFormSummary,
      extraDealSummary: importedExtraDealSummary,
      rulesJSON: importedRulesJSON,
      initials: importedInitials,
      additionalFields: importedAdditionalFields,
      signerAttributeBindings: importedSignerAttributeBindings,
      initialerAttributeBindings: importedInitialerAttributeBindings,
      initialsAnnotations: importedInitialsAnnotations,
      subsequentSigning: importedSubsequentSigning,
      subsequentSigningAttributeBindings: importedSubsequentSigningAttributeBindings,
    } = importedData;

    // Deduplicate sections by attributeName so duplicate section buttons never appear
    const defaultSection = {
      id: "section-0",
      title: "Default Section",
      attributeName: "defaultSection",
      subtitle: "",
      goTo: "",
      goToManual: false,
    };
    const rawSections = importedSections || [defaultSection];
    const { sections: dedupedSections, sectionIdMap } = deduplicateSectionsByAttributeName(rawSections);
    const sectionsToSet = applySectionNavigationDefaults(
      dedupedSections.length ? dedupedSections : [defaultSection],
    );
    const importedFieldsNorm = (importedFields || []).map((f) => ({
      ...f,
      sectionId: sectionIdMap[f.sectionId] ?? f.sectionId,
    }));

    setFields(importedFieldsNorm);
    setSections(sectionsToSet);
    // Preserve all formDetails properties including metadata
    const resolvedFormDetails = formDetailsOverride || importedFormDetails || {
      locale: "",
      type: "",
      name: "",
      shortName: "",
      category: "",
      formId: "00000000-0000-0000-0000-000000000000",
      metadata: {
        order: null,
      },
    };
    // Raw / non-PDF "HTML Smart Form" detection. These import through the same
    // dialog (parseFormJSON still succeeds) but must NOT be driven by the lossy
    // structured pipeline. When the source is a raw type, capture the verbatim
    // JSON and derive formDetails identity straight from it.
    const rawParsed = (() => {
      try {
        return rawSource ? JSON.parse(rawSource) : null;
      } catch {
        return null;
      }
    })();
    const isRawImport =
      isRawFormType(resolvedFormDetails?.type) || isRawFormType(rawParsed?.formJSON?.type);
    if (isRawImport && rawParsed) {
      // Import accepts a single form-template JSON; scaffold the companion
      // integration entry (pane 2) from its identity for the author to fill in.
      setRawFormJson(JSON.stringify(rawParsed, null, 2));
      setRawIntegrationJson(JSON.stringify(buildIntegrationScaffold(rawParsed), null, 2));
      setFormDetails(extractRawIdentity(rawParsed));
    } else {
      setRawFormJson(null);
      setRawIntegrationJson(null);
      setFormDetails(resolvedFormDetails);
    }
    const normalizedSignatures = normalizeSignatures(importedSignatures || {});
    setSignatures(normalizedSignatures);
    setSignatureAnnotations(deriveSignatureAnnotationsFromSignatures(normalizedSignatures));
    setSelectedSignatureId(null);
    setSelectedCreators(importedCreators || []);
    setFormUploadSections(importedUploadSections || []);
    setExtraFormSummary(importedExtraFormSummary || {});
    setExtraDealSummary(importedExtraDealSummary || {});
    setRulesJSON(importedRulesJSON ?? null);
    setInitials(importedInitials ?? null);
    setAdditionalFields(
      sanitizeAdditionalPlacements(importedAdditionalFields ?? [], {
        fields: importedFieldsNorm,
        signerAttributeBindings: importedSignerAttributeBindings ?? [],
        initialerAttributeBindings: importedInitialerAttributeBindings ?? [],
        initialsAnnotations: importedInitialsAnnotations ?? [],
        subsequentSigningAttributeBindings: importedSubsequentSigningAttributeBindings ?? [],
      }),
    );
    setSignerAttributeBindings(importedSignerAttributeBindings ?? []);
    setInitialerAttributeBindings(importedInitialerAttributeBindings ?? []);
    setInitialsAnnotations(importedInitialsAnnotations ?? []);
    setSubsequentSigning(importedSubsequentSigning ?? {});
    setSubsequentSigningAttributeBindings(importedSubsequentSigningAttributeBindings ?? []);
    setFormRules({ formNotes: "", fields: [] });
    setOfferCompare({ fields: [] });
    setFormJsonDirty(false);
    setFormRulesDirty(false);
    setOfferCompareDirty(false);

    // Mark form as loaded
    setIsFormLoaded(true);

    // Mark that this was imported from JSON (for glow effect)
    setImportedFromJson(true);

    // Raw forms are JSON-preview-only (tab index 3). Ordinary forms open on
    // Form Settings (index 1) to show the imported content.
    setActiveTab(isRawImport && rawParsed ? 3 : 1);
  };

  const handleFormImport = (importedData, rawSource = null) => {
    const importedFormDetails = importedData?.formDetails;
    const locale = importedFormDetails?.locale;
    // Raw / non-PDF smart forms skip registry identity validation (they are
    // integration types the PDF-form registry doesn't model) and import
    // straight through as a verbatim JSON passthrough.
    if (isRawFormType(importedFormDetails?.type)) {
      applyImportedFormData(importedData, null, rawSource);
      return;
    }
    if (
      registry &&
      locale &&
      registry.locales?.[locale]
    ) {
      const validation = validateFormIdentity(importedFormDetails, registry, {
        strict: false,
      });
      if (!validation.skipped && !validation.valid && validation.errors.length > 0) {
        pendingImportRef.current = importedData;
        setRegistryImportMessage(
          formatRegistryValidationMessage(validation, importedFormDetails) ||
            "Imported form identity does not match the registry.",
        );
        setRegistryImportDialogOpen(true);
        return;
      }
    }
    applyImportedFormData(importedData);
  };

  const handleRegistryImportApplyFix = () => {
    const importedData = pendingImportRef.current;
    if (!importedData) {
      setRegistryImportDialogOpen(false);
      return;
    }
    const base = importedData.formDetails || {};
    const validation = validateFormIdentity(base, registry, { strict: false });
    const fix = validation.suggestedFix;
    const merged = fix
      ? {
          ...base,
          formId: fix.formId || base.formId,
          type: fix.type || base.type,
          ...(fix.shortName ? { shortName: fix.shortName } : {}),
        }
      : base;
    pendingImportRef.current = null;
    setRegistryImportDialogOpen(false);
    applyImportedFormData(importedData, merged);
  };

  const handleRegistryImportKeep = () => {
    const importedData = pendingImportRef.current;
    pendingImportRef.current = null;
    setRegistryImportDialogOpen(false);
    if (importedData) applyImportedFormData(importedData);
  };

  // ── Mevi Preview (Tab 4) — analyst custom-element wiring ─────
  // The analyst element receives the live form template parsed back
  // to an object (mirrors the publish-dialog generateFormJson call
  // at this file's ~line 845). Only computed when Tab 4 is active
  // so the deps (`fields`, `sections`, signatures, etc.) don't burn
  // CPU rebuilding the JSON during typical Form-Structure work.
  const analystTemplate = useMemo(() => {
    if (activeTab !== 4 && !showAnalystForAnnotator) return null;
    try {
      return JSON.parse(
        generateFormJson({
          fields,
          sections,
          signatures,
          selectedCreators,
          formDetails,
          formUploadSections,
          signatureAnnotations,
          initialsAnnotations,
          additionalFields,
          signerAttributeBindings,
          initialerAttributeBindings,
          subsequentSigning,
          subsequentSigningAttributeBindings,
          isPurchaseContract: formDetails.settings?.type?.type === "isPurchaseContract",
          extraFormSummary,
          extraDealSummary,
          rulesJSON,
    initials,
      initials,
          initials,
        }),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[App] generateFormJson failed for analyst panel", err);
      return null;
    }
  }, [
    activeTab,
    showAnalystForAnnotator,
    fields,
    sections,
    signatures,
    selectedCreators,
    formDetails,
    formUploadSections,
    signatureAnnotations,
    initialsAnnotations,
    additionalFields,
    signerAttributeBindings,
    initialerAttributeBindings,
    subsequentSigning,
    subsequentSigningAttributeBindings,
    extraFormSummary,
    extraDealSummary,
    rulesJSON,
    initials,
  ]);

  // "Save Form Template" inside the analyst element fires this
  // CustomEvent — the detail carries a fresh template snapshot that
  // we route through the SAME path as JSON Preview → Edit → Apply
  // Changes (`handleFormJsonEdit`), so dirty-state, signal handling,
  // and Save-to-Cloud orange-button behavior all match. Declared as
  // a plain function (not `useCallback`) so it always closes over
  // the current `handleFormJsonEdit` rather than the first-render
  // closure — every render gets a fresh handler.
  const handleAnalystSaveTemplate = (detail) => {
    const tpl = detail?.template;
    if (!tpl) {
      setSnackbar({
        open: true,
        message: "Save Form Template: no template snapshot returned.",
        severity: "error",
      });
      return;
    }
    const result = parseFormJSON(JSON.stringify(tpl));
    if (!result.success) {
      setSnackbar({
        open: true,
        message:
          "Save Form Template failed: " + (result.errors || []).join("\n"),
        severity: "error",
      });
      return;
    }
    handleFormJsonEdit(result.data);
    setSnackbar({
      open: true,
      message: "Form template saved from analyst panel.",
      severity: "success",
    });
  };

  // Apply Changes from the raw JSON-preview editor (RawJsonEditor). Unlike
  // handleFormJsonEdit this never runs the structured parse/rebuild: it just
  // stores the validated verbatim string and re-derives the identity mirror
  // (formDetails) so the StudioForm columns + save/publish version stay in
  // sync. Marks the form dirty so Save-to-Cloud lights up.
  const handleRawJsonApply = (normalizedTemplate, normalizedIntegration) => {
    setRawFormJson(normalizedTemplate);
    if (normalizedIntegration != null) setRawIntegrationJson(normalizedIntegration);
    try {
      setFormDetails(extractRawIdentity(JSON.parse(normalizedTemplate)));
    } catch {
      // RawJsonEditor already validated before calling; ignore.
    }
    setFormJsonDirty(true);
  };

  const handleFormJsonEdit = (editedData) => {
    const {
      fields: editedFields,
      sections: editedSections,
      formDetails: editedFormDetails,
      signatures: editedSignatures,
      selectedCreators: editedCreators,
      formUploadSections: editedUploadSections,
      extraFormSummary: editedExtraFormSummary,
      extraDealSummary: editedExtraDealSummary,
      rulesJSON: editedRulesJSON,
      initials: editedInitials,
      additionalFields: editedAdditionalFields,
      signerAttributeBindings: editedSignerAttributeBindings,
      initialerAttributeBindings: editedInitialerAttributeBindings,
      initialsAnnotations: editedInitialsAnnotations,
      subsequentSigning: editedSubsequentSigning,
      subsequentSigningAttributeBindings: editedSubsequentSigningAttributeBindings,
    } = editedData;

    const rawSections = editedSections || sections;
    const { sections: dedupedSections, sectionIdMap } = deduplicateSectionsByAttributeName(rawSections);
    const sectionsToSet = applySectionNavigationDefaults(
      dedupedSections.length ? dedupedSections : rawSections,
    );
    const fieldsToSet = (editedFields || fields).map((f) => ({
      ...f,
      sectionId: sectionIdMap[f.sectionId] ?? f.sectionId,
    }));

    setFields(fieldsToSet);
    setSections(sectionsToSet);
    setFormDetails(editedFormDetails || formDetails);

    const normalizedSignatures = normalizeSignatures(editedSignatures || signatures);
    setSignatures(normalizedSignatures);
    setSignatureAnnotations(deriveSignatureAnnotationsFromSignatures(normalizedSignatures));
    setSelectedSignatureId(null);
    setSelectedCreators(editedCreators || selectedCreators);
    setFormUploadSections(editedUploadSections || formUploadSections);
    if (editedExtraFormSummary) setExtraFormSummary(editedExtraFormSummary);
    if (editedExtraDealSummary) setExtraDealSummary(editedExtraDealSummary);
    if (editedRulesJSON !== undefined) setRulesJSON(editedRulesJSON);
    if (editedInitials !== undefined) setInitials(editedInitials);
    if (editedAdditionalFields !== undefined) setAdditionalFields(sanitizeAdditionalPlacements(editedAdditionalFields ?? [], { ...editedData, fields: fieldsToSet }));
    if (editedSignerAttributeBindings !== undefined) setSignerAttributeBindings(editedSignerAttributeBindings ?? []);
    if (editedInitialerAttributeBindings !== undefined) setInitialerAttributeBindings(editedInitialerAttributeBindings ?? []);
    if (editedInitialsAnnotations !== undefined) setInitialsAnnotations(editedInitialsAnnotations ?? []);
    // subsequentSigningJSON is a role-keyed block plus its derived placements, exactly like
    // signaturesJSON. This branch still referenced editedSubsequentSigningSections /
    // editedSubsequentSigningFields — identifiers from the old array shape that no longer exist
    // anywhere — so applying a JSON Preview edit threw a ReferenceError before reaching the end
    // of the handler, and the values that ARE destructured above were never applied at all.
    if (editedSubsequentSigning !== undefined) {
      setSubsequentSigning(
        editedSubsequentSigning && typeof editedSubsequentSigning === "object" && !Array.isArray(editedSubsequentSigning)
          ? editedSubsequentSigning
          : {},
      );
    }
    if (editedSubsequentSigningAttributeBindings !== undefined) {
      setSubsequentSigningAttributeBindings(editedSubsequentSigningAttributeBindings ?? []);
    }
    setFormJsonDirty(true);
  };

  const handleFieldAssignment = (fieldId, sectionId) => {
    setFormJsonDirty(true);
    setFields((prevFields) => {
      const newFields = prevFields.map((field) => {
        if (field.id === fieldId) {
          // Preserve all field properties, including metadata
          return {
            ...field,
            sectionId,
            touchedByJson: true,
            metadata: field.metadata, // Explicitly preserve metadata
          };
        }
        return field;
      });
      return newFields;
    });
  };

  // Initials roles live in `initials` and never touch `signatures` — the signature path is
  // unchanged. Ticking adds metadata with no rows; the role publishes nothing until a tab exists.
  const handleInitialsRolesChange = (nextInitials) => {
    setFormJsonDirty(true);
    setInitials(Object.keys(nextInitials || {}).length > 0 ? nextInitials : null);
  };

  const handleRolesChange = (newSignatures) => {
    setFormJsonDirty(true);
    const normalized = normalizeSignatures(newSignatures);
    setSignatures(normalized);
    setSignerAttributeBindings((prev) =>
      prev.filter((b) =>
        (normalized[b.roleKey]?.signatures || []).some((s) => s.signerKey === b.signerKey),
      ),
    );
  };

  /** subsequentSigningJSON roles + signer rows, the peer of handleRolesChange. */
  const handleSubsequentSigningChange = (nextSubsequentSigning) => {
    setFormJsonDirty(true);
    const next =
      nextSubsequentSigning && typeof nextSubsequentSigning === "object" && !Array.isArray(nextSubsequentSigning)
        ? nextSubsequentSigning
        : {};
    setSubsequentSigning(next);
    // A binding whose signer row is gone has nothing left to publish onto.
    setSubsequentSigningAttributeBindings((prev) =>
      prev.filter((b) => (next[b.roleKey]?.signatures || []).some((s) => s.signerKey === b.signerKey)),
    );
  };

  const handleCreatorsChange = (newCreators) => {
    setFormJsonDirty(true);
    setSelectedCreators(newCreators);
  };

  const handleFormDetailsChange = (newDetails) => {
    setFormJsonDirty(true);
    setFormDetails(newDetails);
  };

  const handleFieldsUpdate = (updatedFields) => {
    setFormJsonDirty(true);
    const nextIds = new Set(updatedFields.map((f) => f.id));
    const removedFieldIds = fields.map((f) => f.id).filter((id) => !nextIds.has(id));
    // Strip rules and offerCompare from fields and sync all shared data to isolated state
    const cleanedFields = updatedFields.map((f) => {
      const stripped = { ...f };
      if (stripped.attributeName) {
        updateFormRulesField(stripped.attributeName, {
          rules: {
            ...(stripped.rules || {}),
            required: !!stripped.validation?.required,
          },
          pdfLocation: stripped.metadata?.pdfLocation || null,
          options: buildFormRulesOptions(stripped.metadata),
        });
      }
      delete stripped.rules;
      if (stripped.offerCompare !== undefined && stripped.attributeName) {
        if (stripped.offerCompare) {
          addFieldToOfferCompare(stripped.attributeName, stripped.label || "");
        } else {
          removeFieldFromOfferCompare(stripped.attributeName);
        }
        delete stripped.offerCompare;
      } else if (stripped.attributeName && isFieldInOfferCompare(stripped.attributeName)) {
        // Label may have changed — sync it
        updateOfferCompareField(stripped.attributeName, { label: stripped.label || "" });
      }
      return stripped;
    });
    setFields(cleanedFields);

    if (removedFieldIds.length > 0) {
      setAdditionalFields((prev) => prev.filter((e) => !removedFieldIds.includes(e.linkedFieldId)));
      setSignerAttributeBindings((prev) => prev.filter((e) => !removedFieldIds.includes(e.linkedFieldId)));
      setSubsequentSigningAttributeBindings((prev) => prev.filter((e) => !removedFieldIds.includes(e.linkedFieldId)));
    }

    // Clean up orphaned entries for deleted fields
    const activeAttrNames = new Set(cleanedFields.map((f) => f.attributeName).filter(Boolean));
    setFormRules((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => activeAttrNames.has(f.attributeName)),
    }));
    setOfferCompare((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => activeAttrNames.has(f.key)),
    }));
  };

  function buildOptionAnnotationItems(field) {
    const metadata = field.metadata || {};
    const items = [];
    const options = Array.isArray(metadata.options) ? metadata.options : [];

    options.forEach((option, index) => {
      items.push({
        kind: "option",
        index,
        label: option.text || option.value || `Option ${index + 1}`,
      });
    });

    if (metadata.other?.checkbox) {
      items.push({
        kind: "otherCheckbox",
        label:
          metadata.other.checkbox.text ||
          metadata.other.checkbox.value ||
          "Other",
      });
    }

    if (metadata.other?.radio) {
      items.push({
        kind: "otherRadio",
        label:
          metadata.other.radio.text || metadata.other.radio.value || "Other",
      });
    }

    if (metadata.other?.input) {
      items.push({
        kind: "otherInput",
        label: metadata.other.input.attributeName || "Other Input",
      });
    }

    return items;
  }

  /** Register formRules for a newly committed field (mirrors handleCreateField rules sync). */
  function registerFormRulesForCommittedField(field, rulesData, offerCompareFlag) {
    if (!field.attributeName) return;
    updateFormRulesField(field.attributeName, {
      rules: {
        ...(rulesData || {}),
        required: !!field.validation?.required,
      },
      pdfLocation: field.metadata?.pdfLocation || null,
      options: buildFormRulesOptions(field.metadata),
    });
    if (offerCompareFlag) {
      addFieldToOfferCompare(field.attributeName, field.label || "");
    } else {
      removeFieldFromOfferCompare(field.attributeName);
    }
  }

  /** Append option-like field to fields[] (strip field-level pdfLocation) and start multi-box placement queue. */
  function appendOptionLikeFieldToFieldsAndStartQueue(field) {
    let f = field;
    if (f.metadata?.pdfLocation) {
      const { pdfLocation, ...restMetadata } = f.metadata;
      f = { ...f, metadata: restMetadata };
    }
    const items = buildOptionAnnotationItems(f);
    setFields((prev) => [...prev, f]);
    if (items.length > 0) {
      setOptionAnnotationState({ fieldId: f.id, items });
      setShowOptionAnnotationPrompt(true);
    }
  }

  /** Field already in state (e.g. via handleFieldsUpdate); only start option placement wizard. */
  function startOptionAnnotationQueueIfFieldAlreadyCommitted(field) {
    const items = buildOptionAnnotationItems(field);
    if (items.length > 0) {
      setOptionAnnotationState({ fieldId: field.id, items });
      setShowOptionAnnotationPrompt(true);
    }
  }

  function buildFormRulesOptions(metadata) {
    const options = [];
    if (Array.isArray(metadata?.options)) {
      metadata.options.forEach((opt, index) => {
        options.push({
          kind: "option",
          index,
          value: opt.value || "",
          pdfLocation: opt.pdfLocation || null,
        });
      });
    }
    if (metadata?.other?.checkbox) {
      options.push({
        kind: "otherCheckbox",
        value: metadata.other.checkbox.value || "Other",
        pdfLocation: metadata.other.checkbox.pdfLocation || null,
      });
    }
    if (metadata?.other?.radio) {
      options.push({
        kind: "otherRadio",
        value: metadata.other.radio.value || "Other",
        pdfLocation: metadata.other.radio.pdfLocation || null,
      });
    }
    if (metadata?.other?.input) {
      options.push({
        kind: "otherInput",
        value: metadata.other.input.attributeName || "Other Input",
        pdfLocation: metadata.other.input.pdfLocation || null,
      });
    }
    return options;
  }

  function applyOptionLocation(field, item, pdfLocation) {
    if (!item) return field;
    const metadata = { ...(field.metadata || {}) };

    if (item.kind === "option") {
      const options = Array.isArray(metadata.options)
        ? [...metadata.options]
        : [];
      if (options[item.index]) {
        options[item.index] = {
          ...options[item.index],
          pdfLocation,
        };
      }
      metadata.options = options;
    }

    if (item.kind === "otherCheckbox") {
      metadata.other = {
        ...(metadata.other || {}),
        checkbox: {
          ...(metadata.other?.checkbox || {}),
          pdfLocation,
        },
      };
    }

    if (item.kind === "otherRadio") {
      metadata.other = {
        ...(metadata.other || {}),
        radio: {
          ...(metadata.other?.radio || {}),
          pdfLocation,
        },
      };
    }

    if (item.kind === "otherInput") {
      metadata.other = {
        ...(metadata.other || {}),
        input: {
          ...(metadata.other?.input || {}),
          pdfLocation,
        },
      };
    }

    return {
      ...field,
      metadata,
    };
  }

  /**
   * Next free `section-N` id (max existing suffix + 1). Never reuses an id:
   * `section-${sections.length}` collides after a delete (e.g. section-0 +
   * section-2 → length 2 → "section-2" again), which silently corrupts goTo
   * defaults (looked up by id) and field→section assignment.
   * Mirrors JsonEditor.getNextSectionId.
   */
  const getNextSectionId = () => {
    const numericIds = sections
      .map((s) => parseInt(String(s.id).replace(/^section-/, ""), 10))
      .filter((n) => !Number.isNaN(n));
    const max = numericIds.length ? Math.max(...numericIds) : 0;
    return `section-${max + 1}`;
  };

  /** Deduplicate sections by attributeName (keep first). Returns { sections, sectionIdMap } where sectionIdMap maps removed section id -> kept section id for updating field.sectionId. */
  const handleIdentifyAddSection = () => {
    setFormJsonDirty(true);
    const newSectionId = getNextSectionId();
    const newSectionTitle = `New Section ${sections.length + 1}`;
    const newAttributeName = newSectionTitle.trim();

    const newSection = {
      id: newSectionId,
      title: newSectionTitle,
      attributeName: newAttributeName,
      subtitle: "",
      goTo: "",
      goToManual: false,
    };

    const updatedSections = applySectionNavigationDefaults([...sections, newSection]);
    setSections(updatedSections);
    setIdentifySectionActiveId(newSectionId);
    setIdentifySectionNumber("");
    setIdentifySectionName(newSectionTitle);
    setIdentifySectionDescription("");
    setIdentifySectionNotes("");
    setIdentifySectionTitleShort("");
    setIdentifySectionInfo("");
    setIdentifySectionMetadata("{}");
  };

  const commitIdentifyAddDecisionSection = (optionCount) => {
    setFormJsonDirty(true);
    const newSectionId = getNextSectionId();
    const newSectionTitle = `New Decision Section ${sections.length + 1}`;
    const newAttributeName = newSectionTitle.trim();

    const newSection = {
      id: newSectionId,
      title: newSectionTitle,
      attributeName: newAttributeName,
      type: "decision",
      subtitle: "",
      description: "",
      goTo: null,
      goToManual: true,
    };

    const defaultRules = {
      notes: "",
      sectionNumber: "",
      sectionName: "",
      sectionDescription: "",
      sectionNotes: "",
    };
    const newField = {
      id: `field-${Date.now()}`,
      sectionId: newSectionId,
      order: 1,
      type: "decision",
      label: "Decision",
      attributeName: newAttributeName,
      validation: {},
      rules: defaultRules,
      metadata: {
        options: buildDefaultDecisionOptions(optionCount),
      },
      touchedByAnnotator: true,
    };

    const updatedSections = applySectionNavigationDefaults([...sections, newSection]);
    setSections(updatedSections);
    registerFormRulesForCommittedField(newField, newField.rules, false);
    appendOptionLikeFieldToFieldsAndStartQueue(newField);
    setIdentifySectionActiveId(newSectionId);
    setIdentifySectionNumber("");
    setIdentifySectionName(newSectionTitle);
    setIdentifySectionDescription("");
    setIdentifySectionNotes("");
    setIdentifySectionTitleShort("");
    setIdentifySectionInfo("");
    setIdentifySectionMetadata("{}");
  };

  const handleIdentifyAddDecisionSection = () => {
    setDecisionOptionCountDialogOpen(true);
  };

  const enterSignaturePlacementMode = () => {
    setInitialsPlacementMode(false);
    setSignaturePlacementMode((prev) => {
      if (prev) return false; // toggle off when clicking Annotate (or same button again)
      setIdentifySectionsMode(false);
      setSelectedSectionFieldIds([]);
      setCreateFieldDialogOpen(false);
      setAnnotatorEditingField(null);
      return true;
    });
  };

  const exitSignaturePlacementMode = () => {
    setSignaturePlacementMode(false);
  };

  const enterInitialsPlacementMode = () => {
    setSignaturePlacementMode(false);
    setInitialsPlacementMode((prev) => {
      if (prev) return false;
      setIdentifySectionsMode(false);
      setSelectedSectionFieldIds([]);
      setCreateFieldDialogOpen(false);
      setAnnotatorEditingField(null);
      return true;
    });
  };

  const exitInitialsPlacementMode = () => {
    setInitialsPlacementMode(false);
  };

  const closeIdentifySectionsPanel = () => {
    setIdentifySectionsMode(false);
    setSignaturePlacementMode(false);
    setInitialsPlacementMode(false);
    setSelectedSectionFieldIds([]);
    setIdentifySectionNumber("");
    setIdentifySectionNotes("");
  };

  useEffect(() => {
    if (!signaturePlacementMode) return;
    const availableRoles = Object.keys(signatures || {}).filter(Boolean);
    if (availableRoles.length === 0) {
      setSignatureRoleKey("");
      setSignatureSignerKey("");
      return;
    }
    if (!signatureRoleKey || !availableRoles.includes(signatureRoleKey)) {
      setSignatureRoleKey(availableRoles[0]);
      return;
    }
    const availableKeys = getAvailableSignerKeys(
      signatureRoleKey,
      signatureAnnotations,
      signatures,
    );
    if (!availableKeys.includes(signatureSignerKey)) {
      setSignatureSignerKey(availableKeys[0] || "");
    }
  }, [
    signaturePlacementMode,
    signatureRoleKey,
    signatureSignerKey,
    signatureAnnotations,
    signatures,
  ]);

  useEffect(() => {
    if (!initialsPlacementMode) return;
    const availableRoles = initialsRolesOf(signatures, initials, initialsAnnotations);
    if (availableRoles.length === 0) {
      setInitialsRoleKey("");
      setInitialsSignerKey("");
      return;
    }
    if (!initialsRoleKey || !availableRoles.includes(initialsRoleKey)) {
      setInitialsRoleKey(availableRoles[0]);
      return;
    }
    const availableKeys = getAvailableInitialsSignerKeys(
      initialsRoleKey,
      signatures,
      initials,
      initialsAnnotations,
    );
    if (!availableKeys.includes(initialsSignerKey)) {
      setInitialsSignerKey(availableKeys[0] || "");
    }
  }, [
    initialsPlacementMode,
    initialsRoleKey,
    initialsSignerKey,
    initialsAnnotations,
    signatures,
    initials,
  ]);

  // Signature roles follow SIGNATURE annotations only.
  //
  // Initials annotations used to be unioned in here, because initialsJSON was generated from the
  // surviving `signatures` roles and an initialled role had to exist there or its initials were
  // dropped. Initials now own their metadata (see generateInitialsJSON + Initials Roles), so that
  // coupling only did harm: on an initials-only form it re-seeded a full template role per
  // initialled role — F259 came back with 22 signature slots that have nowhere to sign, on every
  // load, undoing the prune.
  useEffect(() => {
    const rolesWithSignatures = Array.from(
      new Set(signatureAnnotations.map((entry) => entry.roleKey).filter(Boolean)),
    );
    const rolesWithSignaturesSet = new Set(rolesWithSignatures);
    const previousRolesWithSignatures = signatureRolesWithAnnotationsRef.current;

    // Only run setSignatures when the set of roles with annotations actually changed
    // (e.g. add/remove signature). Skip when only pdfLocation changed (nudge/resize).
    const rolesChanged =
      previousRolesWithSignatures.size !== rolesWithSignaturesSet.size ||
      [...rolesWithSignaturesSet].some((r) => !previousRolesWithSignatures.has(r));
    if (rolesChanged) {
      setSignatures((prev) => {
        const template = buildSignaturesTemplate();
        const next = { ...prev };

        rolesWithSignatures.forEach((role) => {
          if (!next[role]) next[role] = template[role];
        });

        previousRolesWithSignatures.forEach((role) => {
          if (!rolesWithSignaturesSet.has(role) && next[role]) {
            delete next[role];
          }
        });

        return normalizeSignatures(next);
      });
    }

    signatureRolesWithAnnotationsRef.current = rolesWithSignaturesSet;
  }, [signatureAnnotations]);

  const handleIdentifyDeleteSection = (sectionId) => {
    setFormJsonDirty(true);
    setSections((prevSections) => {
      if (prevSections.length <= 1) {
        return prevSections;
      }
      const remainingSections = prevSections.filter(
        (section) => section.id !== sectionId,
      );
      const updatedSections = applySectionNavigationDefaults(remainingSections);

      if (identifySectionActiveId === sectionId) {
        const nextActive = updatedSections[0] || null;
        setIdentifySectionActiveId(nextActive?.id || null);
        setIdentifySectionName(nextActive?.title || "");
        setIdentifySectionDescription(nextActive?.description || "");
        setIdentifySectionTitleShort(nextActive?.titleShort || "");
        setIdentifySectionInfo(nextActive?.info || "");
        setIdentifySectionMetadata(
          typeof nextActive?.metadata === "object" && nextActive?.metadata !== null
            ? JSON.stringify(nextActive.metadata, null, 2)
            : "{}",
        );
      }

      return updatedSections;
    });

    setFields((prevFields) =>
      prevFields.map((field) =>
        field.sectionId === sectionId ? { ...field, sectionId: null } : field,
      ),
    );
  };

  const handleAssignFieldToIdentifySection = (field) => {
    if (!identifySectionsMode || !identifySectionActiveId) return;
    setFormJsonDirty(true);
    const fieldId = typeof field === "object" ? field.id : field;
    if (!fieldId) return;

    setFields((prevFields) => {
      const targetFields = prevFields.filter(
        (item) => item.sectionId === identifySectionActiveId,
      );
      let nextOrder =
        targetFields.reduce((max, item) => Math.max(max, item.order || 0), 0) +
        1;

      return prevFields.map((item) => {
        if (item.id !== fieldId) return item;
        if (item.sectionId === identifySectionActiveId) return item;
        return {
          ...item,
          sectionId: identifySectionActiveId,
          order: nextOrder++,
          touchedByAnnotator: true,
        };
      });
    });

    setSelectedSectionFieldIds((prev) =>
      prev.includes(fieldId) ? prev : [...prev, fieldId],
    );
  };

  const handleIdentifySectionSelectFields = (ids) => {
    setSelectedSectionFieldIds(ids);
    if (ids.length === 0) {
      setIdentifySectionNumber("");
      setIdentifySectionNotes("");
      return;
    }
    const selectedFields = fields.filter((field) => ids.includes(field.id));
    if (selectedFields.length === 0) {
      setIdentifySectionNumber("");
      setIdentifySectionNotes("");
      return;
    }
    const firstRules = getFormRulesField(selectedFields[0].attributeName)?.rules;
    const sameSectionNumber = firstRules && selectedFields.every(
      (field) =>
        (getFormRulesField(field.attributeName)?.rules?.sectionNumber || "") ===
        (firstRules.sectionNumber || ""),
    );
    const sameSectionNotes = firstRules && selectedFields.every(
      (field) =>
        (getFormRulesField(field.attributeName)?.rules?.sectionNotes || "") ===
        (firstRules.sectionNotes || ""),
    );
    setIdentifySectionNumber(
      sameSectionNumber ? (firstRules?.sectionNumber || "") : "",
    );
    setIdentifySectionNotes(
      sameSectionNotes ? (firstRules?.sectionNotes || "") : "",
    );
  };

  // Role and signer options from signatures (Phase 4) only — single source of truth.
  const signatureRoleOptions = Object.keys(signatures || {}).filter(Boolean);
  const signatureSignerOptions = signatureRoleKey
    ? getAvailableSignerKeys(signatureRoleKey, signatureAnnotations, signatures)
    : [];
  const initialsSignerOptions = initialsRoleKey
    ? getAvailableInitialsSignerKeys(initialsRoleKey, signatures, initials, initialsAnnotations)
    : [];
  const defaultRoleLabels = getDefaultRoleLabels();
  const signatureRoleLabels = Object.fromEntries(
    signatureRoleOptions.map((roleKey) => [
      roleKey,
      signatures[roleKey]?.label ?? defaultRoleLabels[roleKey] ?? roleKey,
    ])
  );

  const handleAnnotatorDeleteField = (fieldOrId) => {
    setFormJsonDirty(true);
    const fieldId =
      typeof fieldOrId === "object" && fieldOrId !== null
        ? fieldOrId.id
        : fieldOrId;
    const deletedField = fields.find((f) => f.id === fieldId);
    if (deletedField?.attributeName) {
      removeFormRulesField(deletedField.attributeName);
      removeFieldFromOfferCompare(deletedField.attributeName);
    }
    setFields((prevFields) =>
      prevFields.filter((field) => field.id !== fieldId),
    );
    setAdditionalFields((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
    setSignerAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
    setInitialerAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
    setSignatureAnnotations((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
    setInitialsAnnotations((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
    setSubsequentSigningAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
    setSelectedSectionFieldIds((prev) => prev.filter((id) => id !== fieldId));
    setPdfSelectedFieldIds((prev) => prev.filter((id) => id !== fieldId));
    setPdfPrimarySelectId((prev) => (resolveSelectIdToFieldId(prev) === fieldId ? null : prev));
    setAnnotatorEditingField(null);
  };

  const handleCombinePdfFields = () => {
    const sectionIds = pdfSelectedFieldIds.filter((id) => fields.some((f) => f.id === id));
    if (sectionIds.length < 2) return;
    const toCombine = fields.filter((f) => sectionIds.includes(f.id));
    const withLocation = toCombine.filter((f) => f.metadata?.pdfLocation?.page);
    if (withLocation.length === 0) return;
    const firstPage = withLocation[0].metadata.pdfLocation.page;
    const onFirstPage = withLocation.filter((f) => f.metadata.pdfLocation.page === firstPage);
    if (onFirstPage.length < 2) return;
    const locs = onFirstPage.map((f) => f.metadata.pdfLocation);
    const minX = Math.min(...locs.map((l) => l.x));
    const minY = Math.min(...locs.map((l) => l.y));
    const maxRight = Math.max(...locs.map((l) => l.x + l.width));
    const maxBottom = Math.max(...locs.map((l) => l.y + l.height));
    const unionLocation = {
      page: firstPage,
      x: minX,
      y: minY,
      width: maxRight - minX,
      height: maxBottom - minY,
    };
    const first = onFirstPage[0];
    const newId = `field-${Date.now()}`;
    const newField = {
      id: newId,
      sectionId: first.sectionId,
      order: first.order,
      type: "text",
      label: first.label || "Combined field",
      attributeName: first.attributeName || "combinedField",
      placeholder: first.placeholder,
      required: first.required,
      metadata: {
        ...first.metadata,
        pdfLocation: unionLocation,
      },
    };
    setFormJsonDirty(true);
    const combinedRemoveIds = onFirstPage.map((f) => f.id);
    setFields((prev) => {
      const idsToRemove = new Set(combinedRemoveIds);
      const next = prev.filter((f) => !idsToRemove.has(f.id));
      const insertIndex = next.findIndex((f) => f.order >= first.order && f.sectionId === first.sectionId);
      const at = insertIndex === -1 ? next.length : insertIndex;
      next.splice(at, 0, newField);
      return next;
    });
    setAdditionalFields((prev) => prev.filter((e) => !combinedRemoveIds.includes(e.linkedFieldId)));
    setSignerAttributeBindings((prev) => prev.filter((e) => !combinedRemoveIds.includes(e.linkedFieldId)));
    setSignatureAnnotations((prev) => prev.filter((e) => !combinedRemoveIds.includes(e.linkedFieldId)));
    setInitialsAnnotations((prev) => prev.filter((e) => !combinedRemoveIds.includes(e.linkedFieldId)));
    setInitialerAttributeBindings((prev) => prev.filter((e) => !combinedRemoveIds.includes(e.linkedFieldId)));
    setSubsequentSigningAttributeBindings((prev) => prev.filter((e) => !combinedRemoveIds.includes(e.linkedFieldId)));
    onFirstPage.forEach((f) => {
      if (f.attributeName) {
        removeFormRulesField(f.attributeName);
        removeFieldFromOfferCompare(f.attributeName);
      }
    });
    setPdfSelectedFieldIds([newId]);
    setPdfPrimarySelectId(newId);
  };

  const isSamePdfLocation = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const ax = a.x ?? 0, ay = a.y ?? 0, aw = a.width ?? 0, ah = a.height ?? 0, ap = a.page;
    const bx = b.x ?? 0, by = b.y ?? 0, bw = b.width ?? 0, bh = b.height ?? 0, bp = b.page;
    return ap === bp && ax === bx && ay === by && aw === bw && ah === bh;
  };

  /** Mirror a linked extra-PDF rect onto the section field (inverse of handleFieldLocationChange). */
  const mirrorPdfLocationToSectionField = (fieldId, mergedLocation) => {
    if (!fieldId || !mergedLocation?.page) return;
    setFields((prevFields) =>
      prevFields.map((field) => {
        if (field.id !== fieldId) return field;
        const nextMeta = {
          ...field.metadata,
          pdfLocation: { ...mergedLocation },
        };
        delete nextMeta.pdfLocationContinuations;
        return { ...field, metadata: nextMeta };
      }),
    );
    const targetField = fieldsRef.current.find((f) => f.id === fieldId);
    if (targetField?.attributeName) {
      updateFormRulesField(targetField.attributeName, { pdfLocation: mergedLocation });
    }
    setEditingField((prevField) => {
      if (!prevField || prevField.id !== fieldId) return prevField;
      const nextMeta = {
        ...prevField.metadata,
        pdfLocation: { ...mergedLocation },
      };
      delete nextMeta.pdfLocationContinuations;
      return { ...prevField, metadata: nextMeta };
    });
  };

  /** Field Editor: sync extra PDF bindings (linkedFieldId is client-only, stripped from published JSON). */
  const applyFieldPdfBindingFromEditor = useCallback((payload) => {
    if (!payload?.fieldId) return;
    setFormJsonDirty(true);
    const { fieldId, kind, attributeName, pdfLocation, lines, personaCompound, signers: payloadSigners } = payload;

    const clearLinked = () => {
      setAdditionalFields((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setSignerAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setInitialerAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setSignatureAnnotations((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setInitialsAnnotations((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setSubsequentSigningAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
    };

    if (kind !== "data" && kind !== "persona" && kind !== "signatureImage" && kind !== "initialsPersona" && kind !== "initialsImage" && kind !== "subsequentSigning") {
      const linkedAdditional = additionalFieldsRef.current.find((e) => e.linkedFieldId === fieldId);
      const sectionField = fieldsRef.current.find((f) => f.id === fieldId);
      if (linkedAdditional?.pdfLocation?.page) {
        const mergedLocation = { ...linkedAdditional.pdfLocation };
        setFields((prevFields) =>
          prevFields.map((field) => {
            if (field.id !== fieldId) return field;
            const nextMeta = { ...field.metadata, pdfLocation: mergedLocation };
            delete nextMeta.pdfLocationContinuations;
            return { ...field, metadata: nextMeta };
          }),
        );
        if (sectionField?.attributeName) {
          updateFormRulesField(sectionField.attributeName, { pdfLocation: mergedLocation });
        }
        setEditingField((prevField) => {
          if (!prevField || prevField.id !== fieldId) return prevField;
          const nextMeta = { ...prevField.metadata, pdfLocation: mergedLocation };
          delete nextMeta.pdfLocationContinuations;
          return { ...prevField, metadata: nextMeta };
        });
      }
      clearLinked();
      return;
    }
    if (!pdfLocation?.page) {
      clearLinked();
      return;
    }
    if (kind !== "signatureImage" && kind !== "initialsImage" && !attributeName) {
      clearLinked();
      return;
    }

    let roleKey = "";
    let signerKey = "";
    if (
      kind === "persona" ||
      kind === "signatureImage" ||
      kind === "initialsPersona" ||
      kind === "initialsImage" ||
      kind === "subsequentSigning"
    ) {
      const compound = personaCompound || "";
      const tab = compound.indexOf("\t");
      if (tab < 0) {
        clearLinked();
        return;
      }
      roleKey = compound.slice(0, tab);
      signerKey = compound.slice(tab + 1);
      if (!roleKey || !signerKey) {
        clearLinked();
        return;
      }
    }

    clearLinked();

    const loc = { ...pdfLocation };
    const lineCount =
      lines != null && Number(lines) > 0
        ? Number(lines)
        : loc.lines != null && Number(loc.lines) > 0
          ? Number(loc.lines)
          : 0;
    if (kind === "signatureImage" || kind === "initialsImage") {
      delete loc.lines;
    } else if (lineCount > 0) {
      loc.lines = lineCount;
    } else {
      delete loc.lines;
    }

    if (kind === "data") {
      setAdditionalFields((prev) => [
        ...prev,
        {
          id: `linked-additional-${fieldId}-${Date.now()}`,
          linkedFieldId: fieldId,
          attributeName,
          pdfLocation: loc,
          lines: lineCount,
        },
      ]);
      return;
    }

    if (kind === "signatureImage") {
      setSignatureAnnotations((prev) => [
        ...prev,
        {
          id: `linked-sig-ann-${fieldId}-${Date.now()}`,
          linkedFieldId: fieldId,
          roleKey,
          signerKey,
          pdfLocation: loc,
        },
      ]);
      return;
    }

    if (kind === "initialsImage") {
      setInitialsAnnotations((prev) => [
        ...prev,
        {
          id: `linked-init-ann-${fieldId}-${Date.now()}`,
          linkedFieldId: fieldId,
          roleKey,
          signerKey,
          pdfLocation: loc,
        },
      ]);
      return;
    }

    if (kind === "persona") {
      const def = snapshotSignerFieldDef(fieldsRef.current.find((f) => f.id === fieldId));
      setSignerAttributeBindings((prev) => [
        ...prev,
        {
          id: `linked-signer-${fieldId}-${Date.now()}`,
          linkedFieldId: fieldId,
          roleKey,
          signerKey,
          attributeName,
          pdfLocation: loc,
          lines: lineCount,
          ...(def ? { def } : {}),
        },
      ]);
      return;
    }

    if (kind === "initialsPersona") {
      const def = snapshotSignerFieldDef(fieldsRef.current.find((f) => f.id === fieldId));
      setInitialerAttributeBindings((prev) => [
        ...prev,
        {
          id: `linked-initialer-${fieldId}-${Date.now()}`,
          linkedFieldId: fieldId,
          roleKey,
          signerKey,
          attributeName,
          pdfLocation: loc,
          lines: lineCount,
          ...(def ? { def } : {}),
        },
      ]);
      return;
    }

    if (kind === "subsequentSigning") {
      const def = snapshotSignerFieldDef(fieldsRef.current.find((f) => f.id === fieldId));
      setSubsequentSigningAttributeBindings((prev) => [
        ...prev,
        {
          id: `linked-subsequent-${fieldId}-${Date.now()}`,
          linkedFieldId: fieldId,
          roleKey,
          signerKey,
          attributeName,
          pdfLocation: loc,
          lines: lineCount,
          ...(def ? { def } : {}),
        },
      ]);
      return;
    }
  }, []);

  const handleFieldLocationChange = (fieldId, nextLocation) => {
    const targetField = fields.find((f) => f.id === fieldId);
    const current = targetField?.metadata?.pdfLocation;
    const nextLocFlat = { ...(nextLocation || {}) };
    delete nextLocFlat.pdfLocationContinuations;

    const mergedLocation = {
      ...targetField?.metadata?.pdfLocation,
      ...nextLocFlat,
    };

    if (current && isSamePdfLocation(current, { ...current, ...nextLocFlat })) {
      return;
    }

    setFormJsonDirty(true);

    setFields((prevFields) =>
      prevFields.map((field) => {
        if (field.id !== fieldId) return field;
        const nextMeta = {
          ...field.metadata,
          pdfLocation: {
            ...field.metadata?.pdfLocation,
            ...nextLocFlat,
          },
        };
        delete nextMeta.pdfLocationContinuations;
        return {
          ...field,
          metadata: nextMeta,
        };
      }),
    );

    /** Linked persona/additional/signature rows mirror primary pdfLocation only (single rect). */
    setAdditionalFields((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId
          ? { ...e, pdfLocation: { ...mergedLocation } }
          : e,
      ),
    );
    setSignerAttributeBindings((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId
          ? { ...e, pdfLocation: { ...mergedLocation } }
          : e,
      ),
    );
    setInitialerAttributeBindings((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId
          ? { ...e, pdfLocation: { ...mergedLocation } }
          : e,
      ),
    );
    setSubsequentSigningAttributeBindings((prev) =>
      prev.map((b) =>
        b.linkedFieldId === fieldId ? { ...b, pdfLocation: { ...mergedLocation } } : b,
      ),
    );

    setSignatureAnnotations((prev) =>
      prev.map((e) => {
        if (e.linkedFieldId !== fieldId) return e;
        const pl = { ...mergedLocation };
        delete pl.lines;
        return { ...e, pdfLocation: pl };
      }),
    );
    setInitialsAnnotations((prev) =>
      prev.map((e) => {
        if (e.linkedFieldId !== fieldId) return e;
        const pl = { ...mergedLocation };
        delete pl.lines;
        return { ...e, pdfLocation: pl };
      }),
    );

    setEditingField((prevField) => {
      if (!prevField || prevField.id !== fieldId) return prevField;
      const nextMeta = {
        ...prevField.metadata,
        pdfLocation: {
          ...prevField.metadata?.pdfLocation,
          ...nextLocFlat,
        },
      };
      delete nextMeta.pdfLocationContinuations;
      return {
        ...prevField,
        metadata: nextMeta,
      };
    });

    // Sync pdfLocation to formRules
    if (targetField?.attributeName) {
      updateFormRulesField(targetField.attributeName, { pdfLocation: mergedLocation });
    }
  };

  /** Keep linked Extra PDF rows aligned when placements live on options (checkbox/radio/decision). */
  const syncLinkedPdfMirrorsForField = (field) => {
    if (!field?.id) return;
    const fieldId = field.id;
    const eff = getEffectivePdfLocationForExtraBinding(field);
    if (!eff?.pdfLocation?.page) {
      setAdditionalFields((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setSignerAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setInitialerAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setSignatureAnnotations((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setInitialsAnnotations((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      setSubsequentSigningAttributeBindings((prev) => prev.filter((e) => e.linkedFieldId !== fieldId));
      return;
    }
    const mergedLocation = { ...eff.pdfLocation };
    const lineCount = eff.lines != null && Number(eff.lines) > 0 ? Number(eff.lines) : 0;
    if (lineCount > 0) mergedLocation.lines = lineCount;
    else delete mergedLocation.lines;

    setAdditionalFields((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setSignerAttributeBindings((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setInitialerAttributeBindings((prev) =>
      prev.map((e) =>
        e.linkedFieldId === fieldId ? { ...e, pdfLocation: { ...mergedLocation } } : e,
      ),
    );
    setSignatureAnnotations((prev) =>
      prev.map((e) => {
        if (e.linkedFieldId !== fieldId) return e;
        const pl = { ...mergedLocation };
        delete pl.lines;
        return { ...e, pdfLocation: pl };
      }),
    );
    setInitialsAnnotations((prev) =>
      prev.map((e) => {
        if (e.linkedFieldId !== fieldId) return e;
        const pl = { ...mergedLocation };
        delete pl.lines;
        return { ...e, pdfLocation: pl };
      }),
    );
    setSubsequentSigningAttributeBindings((prev) =>
      prev.map((b) =>
        b.linkedFieldId === fieldId ? { ...b, pdfLocation: { ...mergedLocation } } : b,
      ),
    );
  };

  const getOptionPdfLocation = (field, item) => {
    if (!field || !item) return null;
    if (item.kind === "option") return field.metadata?.options?.[item.index]?.pdfLocation ?? null;
    if (item.kind === "otherCheckbox") return field.metadata?.other?.checkbox?.pdfLocation ?? null;
    if (item.kind === "otherRadio") return field.metadata?.other?.radio?.pdfLocation ?? null;
    if (item.kind === "otherInput") return field.metadata?.other?.input?.pdfLocation ?? null;
    return null;
  };

  const handleOptionLocationChange = (fieldId, markerMeta, nextLocation) => {
    const targetField = fields.find((f) => f.id === fieldId);
    if (!targetField) return;
    const current = getOptionPdfLocation(targetField, markerMeta);
    if (current && isSamePdfLocation(current, { ...current, ...nextLocation })) return;
    const updatedSnapshot = applyOptionLocation(targetField, markerMeta, nextLocation);
    setFormJsonDirty(true);
    setFields((prevFields) =>
      prevFields.map((field) => {
        if (field.id !== fieldId) return field;
        return applyOptionLocation(field, markerMeta, nextLocation);
      }),
    );

    syncLinkedPdfMirrorsForField(updatedSnapshot);

    // Sync option pdfLocation to formRules
    if (targetField?.attributeName) {
      setFormRules((prev) => {
        const entry = prev.fields.find((f) => f.attributeName === targetField.attributeName);
        if (!entry) return prev;
        const updatedOptions = (entry.options || []).map((opt) => {
          if (markerMeta.kind === "option" && opt.kind === "option" && opt.index === markerMeta.index) {
            return { ...opt, pdfLocation: nextLocation };
          }
          if (opt.kind === markerMeta.kind && opt.kind !== "option") {
            return { ...opt, pdfLocation: nextLocation };
          }
          return opt;
        });
        return {
          ...prev,
          fields: prev.fields.map((f) =>
            f.attributeName === targetField.attributeName ? { ...f, options: updatedOptions } : f,
          ),
        };
      });
    }
  };

  const handleSignatureLocationChange = (signatureId, nextLocation) => {
    const current =
      signatureAnnotations.find((e) => e.id === signatureId)?.pdfLocation ||
      initialsAnnotations.find((e) => e.id === signatureId)?.pdfLocation;
    if (current && isSamePdfLocation(current, { ...current, ...nextLocation })) return;
    setFormJsonDirty(true);
    // Apply synchronously (NOT startTransition) so the signature/initials box
    // tracks the handle drag and nudge in real time — matches
    // handleFieldLocationChange. Deferring via a transition made the box lag the
    // pointer (appeared frozen) and show a stale size vs the nudger readout.
    setSignatureAnnotations((prev) =>
      prev.map((entry) =>
        entry.id === signatureId
          ? {
              ...entry,
              pdfLocation: {
                ...entry.pdfLocation,
                ...nextLocation,
              },
            }
          : entry,
      ),
    );
    setInitialsAnnotations((prev) =>
      prev.map((entry) =>
        entry.id === signatureId
          ? {
              ...entry,
              pdfLocation: {
                ...entry.pdfLocation,
                ...nextLocation,
              },
            }
          : entry,
      ),
    );
  };

  const handleSignatureDelete = (signatureId) => {
    setFormJsonDirty(true);
    setSignatureAnnotations((prev) =>
      prev.filter((entry) => entry.id !== signatureId),
    );
    setInitialsAnnotations((prev) =>
      prev.filter((entry) => entry.id !== signatureId),
    );
    setSelectedSignatureId((prev) =>
      prev === signatureId ? null : prev,
    );
  };

  const handleAdditionalFieldLocationChange = (id, nextLocation) => {
    const entry = additionalFieldsRef.current.find((e) => e.id === id);
    const current = entry?.pdfLocation;
    if (current && isSamePdfLocation(current, { ...current, ...nextLocation })) return;
    const mergedLocation = { ...current, ...nextLocation };
    const linkedFieldId = entry?.linkedFieldId;
    setFormJsonDirty(true);
    setAdditionalFields((prev) =>
      prev.map((e) =>
        e.id === id
          ? { ...e, pdfLocation: { ...e.pdfLocation, ...nextLocation } }
          : e,
      ),
    );
    if (linkedFieldId) {
      mirrorPdfLocationToSectionField(linkedFieldId, mergedLocation);
    }
  };

  const handleDeleteAdditionalField = (id) => {
    setFormJsonDirty(true);
    setAdditionalFields((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleAddAdditionalAnnotation = ({ attributeName, pdfLocation, lines = 0 }) => {
    setFormJsonDirty(true);
    setAdditionalFields((prev) => {
      const id = `additional-${Date.now()}-${prev.length}`;
      return [...prev, { id, attributeName, pdfLocation, lines }];
    });
  };

  const handleSignerBindingLocationChange = (id, nextLocation) => {
    const current =
      signerAttributeBindings.find((e) => e.id === id)?.pdfLocation ||
      initialerAttributeBindings.find((e) => e.id === id)?.pdfLocation;
    if (current && isSamePdfLocation(current, { ...current, ...nextLocation })) return;
    setFormJsonDirty(true);
    setSignerAttributeBindings((prev) =>
      prev.map((entry) =>
        entry.id === id
          ? { ...entry, pdfLocation: { ...entry.pdfLocation, ...nextLocation } }
          : entry,
      ),
    );
    setInitialerAttributeBindings((prev) =>
      prev.map((entry) =>
        entry.id === id
          ? { ...entry, pdfLocation: { ...entry.pdfLocation, ...nextLocation } }
          : entry,
      ),
    );
  };

  const handleSubsequentSigningLocationChange = (id, nextLocation) => {
    const current = subsequentSigningAttributeBindings.find((b) => b.id === id)?.pdfLocation;
    if (current && isSamePdfLocation(current, { ...current, ...nextLocation })) return;
    setFormJsonDirty(true);
    setSubsequentSigningAttributeBindings((prev) =>
      prev.map((entry) =>
        entry.id === id
          ? { ...entry, pdfLocation: { ...(entry.pdfLocation || {}), ...nextLocation } }
          : entry,
      ),
    );
  };

  const handleDeleteSubsequentSigningBinding = (id) => {
    setFormJsonDirty(true);
    setSubsequentSigningAttributeBindings((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleAddSubsequentSigningAnnotation = ({ roleKey, signerKey, attributeName, pdfLocation, lines = 0 }) => {
    setFormJsonDirty(true);
    setSubsequentSigningAttributeBindings((prev) => {
      const id = `subsequent-bind-${Date.now()}-${prev.length}`;
      return [...prev, { id, roleKey, signerKey, attributeName, pdfLocation, lines }];
    });
  };

  const handleDeleteSignerBinding = (id) => {
    setFormJsonDirty(true);
    setSignerAttributeBindings((prev) => prev.filter((entry) => entry.id !== id));
    setInitialerAttributeBindings((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleAddSignerBindingAnnotation = ({ roleKey, signerKey, attributeName, pdfLocation, lines = 0 }) => {
    setFormJsonDirty(true);
    setSignerAttributeBindings((prev) => {
      const id = `signer-bind-${Date.now()}-${prev.length}`;
      return [...prev, { id, roleKey, signerKey, attributeName, pdfLocation, lines }];
    });
  };

  const handleLineHeightSet = () => {
    setLineHeightSettingMode(true);
    setLineHeightCaptureBox({ page: 1, x: 10, y: 20, width: 80, height: 5 });
  };

  const handleLineHeightConfirm = (lineHeightPx) => {
    setDefaultLineHeightPx(lineHeightPx);
    const key = LINE_HEIGHT_STORAGE_KEY_PREFIX + (formMeta.studioFormId || "local");
    localStorage.setItem(key, String(lineHeightPx));
    setLineHeightCaptureBox(null);
    setLineHeightSettingMode(false);
    setSnackbar({ open: true, message: "Line height set. You can draw fields on the PDF.", severity: "success" });
  };

  const handleLineHeightCancel = () => {
    setLineHeightCaptureBox(null);
    setLineHeightSettingMode(false);
  };

  const handleLineHeightConfirmError = (message) => {
    setSnackbar({ open: true, message: message || "Could not set line height.", severity: "warning" });
  };

  const handleLineHeightReset = () => {
    setDefaultLineHeightPx(null);
    const key = LINE_HEIGHT_STORAGE_KEY_PREFIX + (formMeta.studioFormId || "local");
    localStorage.removeItem(key);
  };

  useEffect(() => {
    if (!studioForms.length) return;
    const newAnnotation = {};
    const newJson = {};
    const newStgReady = {};
    studioForms.forEach((f) => {
      newAnnotation[f.id] = f.annotationsCompleted != null;
      newJson[f.id] = f.jsonCompleted != null;
      newStgReady[f.id] = f.metadata?.stgReady === true;
    });
    setAnnotationCompleteMap(newAnnotation);
    setJsonCompleteMap(newJson);
    setStgReadyMap(newStgReady);
  }, [studioForms]);

  useEffect(() => {
    if (!isFormLoaded) fetchStudioForms();
  }, [isFormLoaded]);

  const fetchStudioForms = async () => {
    setStudioFormsLoading(true);
    try {
      const forms = await getFormsFromCloud();
      setStudioForms(forms);
    } catch (error) {
      console.error("Failed to fetch studio forms:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Failed to fetch studio forms."), severity: "error" });
    } finally {
      setStudioFormsLoading(false);
    }
  };

  // ── "Publish to All Buckets" batch (per locale, super-admin) ──────────────
  const openPublishAll = (locale) => {
    setPublishAllLocale(locale);
    setPublishAllRunning(false);
    setPublishAllDone(false);
    setPublishAllResults([]);
    setPublishAllErrors([]);
    setPublishAllSkipped([]);
    setPublishAllTarget(PUBLISH_ALL_TARGET_ALL);
  };

  const closePublishAll = () => {
    if (publishAllRunning) return;
    setPublishAllLocale(null);
  };

  // For each stgReady form in the locale, publish its LATEST revision
  // (publish-only, no save) to every Forms-N bucket it isn't already published
  // to, then finally Staging. Reuses the same helpers/endpoint as the
  // --force-gcs script; the single-form publish flow is untouched.
  //
  // `target` is PUBLISH_ALL_TARGET_ALL or a single Forms-N bucket. Single-bucket
  // runs are a dry run: they never touch Staging, so stgReady survives and the
  // full batch can follow (it skips the bucket already published to).
  const runPublishToAllBuckets = async (locale, target = PUBLISH_ALL_TARGET_ALL) => {
    const singleEnv = target === PUBLISH_ALL_TARGET_ALL ? null : target;
    const targetForms = studioForms.filter((f) => f.locale === locale && stgReadyMap[f.id]);
    setPublishAllRunning(true);
    setPublishAllDone(false);
    const results = [];
    const errors = [];
    const skipped = [];
    for (const form of targetForms) {
      const shortName = form.shortName || form.name || form.formName || form.formId || "Untitled";
      try {
        const detail = await getFormFromCloudById(form.id);
        const revs = [...(detail.studioFormDatas || [])].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        const latest = revs[0];
        if (!latest) throw new Error("no revisions to publish");
        const data = await getFormDataFromCloudById(form.id, latest.id);
        const { version, name, json, integrationJson } = buildRevisionPublishPayload(data);
        // Envs the latest revision is already published to (form JSON).
        const publishedEnvs = new Set(
          (form.published || []).filter((p) => p && p.jsonType === "form").map((p) => p.env),
        );
        // Missing Forms-N (in order), then Staging (stgReady ⇒ not on Staging yet).
        // Single-bucket dry run: just that bucket, and only if it's missing it.
        const targets = singleEnv
          ? (publishedEnvs.has(singleEnv) ? [] : [singleEnv])
          : [...PUBLISH_ALL_FORMS_ENVS.filter((e) => !publishedEnvs.has(e)), "Staging"];
        if (targets.length === 0) skipped.push(shortName);
        const publishedTo = [];
        for (const env of targets) {
          try {
            await publishFormData(form.id, latest.id, {
              name,
              description: "",
              version,
              env,
              jsonType: "form",
              json,
              ...(integrationJson != null ? { integrationJson } : {}),
              allowSameVersion: true,
            });
            publishedTo.push(env);
          } catch (e) {
            errors.push({ shortName, error: `${env}: ${formatApiError(e, "publish failed")}` });
          }
        }
        if (publishedTo.length) {
          results.push({ shortName, publishedTo, description: data.description || latest.description || "" });
        }
      } catch (e) {
        errors.push({ shortName, error: formatApiError(e, "publish failed") });
      }
      // Surface progress as each form completes.
      setPublishAllResults([...results]);
      setPublishAllErrors([...errors]);
      setPublishAllSkipped([...skipped]);
    }
    setPublishAllRunning(false);
    setPublishAllDone(true);
    // Reaching Staging retires stgReady on the backend — refresh so the maps
    // (and this button's visibility) reflect it.
    fetchStudioForms();
  };

  const handleDashboardLoadForm = async (form) => {
    try {
      const result = await getFormFromCloudById(form.id);
      const datas = (result.studioFormDatas || [])
        .map((d) => ({ ...d }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const latest = datas[0];
      if (!latest) {
        setSnackbar({ open: true, message: "No revisions found for this form.", severity: "warning" });
        return;
      }
      const data = await getFormDataFromCloudById(form.id, latest.id);
      setFormMeta({
        id: data.id || null,
        studioFormId: data.studioFormId || null,
        name: data.name || "",
        description: data.description || "",
        status: data.status || "draft",
        instanceVersion: data.instanceVersion || 0,
        createdAt: data.createdAt || null,
        updatedAt: data.updatedAt || null,
        published: data.published || [],
        metadata: data.metadata || {},
        createdBy: data.createdBy || null,
      });
      const { sections: dedupedSections, sectionIdMap } = deduplicateSectionsByAttributeName(data.sections || []);
      if (data.fields) {
        setFields(
          data.fields.map((f) => ({
            ...f,
            sectionId: sectionIdMap[f.sectionId] ?? f.sectionId,
          }))
        );
      }
      if (data.sections && dedupedSections.length) setSections(dedupedSections);
      else if (data.sections) setSections(data.sections);
      if (data.formDetails) setFormDetails(data.formDetails);
      // Raw / non-PDF smart form: hold the verbatim template JSON; the JSON-only
      // editor and publish read from it instead of the structured state.
      const dashIsRaw = isRawFormType(data.formDetails?.type);
      if (dashIsRaw && data.rawFormJSON) {
        const split = splitRawFormBlob(data.rawFormJSON);
        setRawFormJson(split.template);
        setRawIntegrationJson(split.integration);
      } else {
        setRawFormJson(null);
        setRawIntegrationJson(null);
      }
      if (data.signatures && Object.keys(data.signatures).length > 0) {
        const { signatures: sigStripped, bindings } = resolveSignerAttributeBindingsForLoad(
          data.signatures,
          data.signerAttributeBindings,
        );
        setSignatures(normalizeSignatures(sigStripped));
        setSignerAttributeBindings(bindings);
      } else if (data.selectedRoles && data.selectedRoles.length > 0) {
        const template = buildSignaturesTemplate();
        setSignatures(normalizeSignatures(Object.fromEntries(
          data.selectedRoles.map((key) => [key, template[key]]).filter(([, v]) => !!v)
        )));
        setSignerAttributeBindings(data.signerAttributeBindings ?? []);
      } else {
        setSignatures(normalizeSignatures({}));
        setSignerAttributeBindings(data.signerAttributeBindings ?? []);
      }
      if (data.selectedCreators) setSelectedCreators(data.selectedCreators);
      if (data.formUploadSections) setFormUploadSections(data.formUploadSections);
      if (data.signatureAnnotations) setSignatureAnnotations(data.signatureAnnotations);
      setInitialsAnnotations(data.initialsAnnotations ?? []);
      if (data.formRules) setFormRules(data.formRules);
      if (data.offerCompare) setOfferCompare(data.offerCompare);
      setExtraFormSummary(data.extraFormSummary || {});
      setExtraDealSummary(data.extraDealSummary || {});
      setRulesJSON(data.rulesJSON ?? null);
      setInitials(data.initials ?? null);
      setAdditionalFields(sanitizeAdditionalPlacements(data.additionalFields ?? [], data));
      setInitialerAttributeBindings(data.initialerAttributeBindings ?? []);
      setSubsequentSigning(data.subsequentSigning ?? {});
      setSubsequentSigningAttributeBindings(data.subsequentSigningAttributeBindings ?? []);
      setFormJsonDirty(false);
      setFormRulesDirty(false);
      setOfferCompareDirty(false);
      if (data.pdfB64) {
        try {
          const loadedPdf = base64ToFile(data.pdfB64, data.pdfName || "document.pdf");
          setPdfFile(loadedPdf);
          pdfBaselineFileRef.current = loadedPdf; // matches GCS; next save reuses if untouched
        } catch (e) {
          console.warn("Failed to restore PDF from cloud:", e);
          setPdfFile(null);
          pdfBaselineFileRef.current = null;
        }
      } else {
        setPdfFile(null);
        pdfBaselineFileRef.current = null;
      }
      setIsFormLoaded(true);
      // Raw forms are JSON-preview-only (tab 3). Otherwise, in JSON mode open on
      // Form Settings so locale/version are visible right away.
      if (dashIsRaw) setActiveTab(3);
      else if (studioMode === "json") setActiveTab(1);
      trackLoad("cloud");
    } catch (error) {
      console.error("Failed to load form from dashboard:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Failed to load form."), severity: "error" });
    }
  };

  const handleStudioFormClick = async (form) => {
    setSelectedStudioForm(form);
    setFormsBrowserView("revisions");
    setStudioFormDatasLoading(true);
    try {
      const result = await getFormFromCloudById(form.id);
      setSelectedStudioForm(result);
      const datas = (result.studioFormDatas || [])
        .map((d) => parseStudioFormData(d))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setStudioFormDatas(datas);
    } catch (error) {
      console.error("Failed to fetch studio form:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Failed to fetch revisions."), severity: "error" });
    } finally {
      setStudioFormDatasLoading(false);
    }
  };

  const handleStudioFormDataClick = (formData) => {
    setSelectedStudioFormData(formData);
    setFormsBrowserView("detail");
  };

  const handleBrowserBack = () => {
    if (formsBrowserView === "detail") {
      setSelectedStudioFormData(null);
      setFormsBrowserView("revisions");
    } else if (formsBrowserView === "revisions") {
      setSelectedStudioForm(null);
      setStudioFormDatas([]);
      setFormsBrowserView("list");
    }
  };

  const handleBrowserClose = () => {
    setFormsBrowserView(null);
    setSelectedStudioForm(null);
    setSelectedStudioFormData(null);
    setStudioFormDatas([]);
  };

  const doLoadFromCloud = async () => {
    const current = selectedStudioFormData;
    if (!current) return;

    try {
      const data = await getFormDataFromCloudById(current.studioFormId, current.id);

      setFormMeta({
        id: data.id || null,
        studioFormId: data.studioFormId || null,
        name: data.name || "",
        description: data.description || "",
        status: data.status || "draft",
        instanceVersion: data.instanceVersion || 0,
        createdAt: data.createdAt || null,
        updatedAt: data.updatedAt || null,
        published: data.published || [],
        metadata: data.metadata || {},
        createdBy: data.createdBy || null,
      });

      const { sections: dedupedSectionsRev, sectionIdMap: sectionIdMapRev } = deduplicateSectionsByAttributeName(data.sections || []);
      if (data.fields) {
        setFields(
          data.fields.map((f) => ({
            ...f,
            sectionId: sectionIdMapRev[f.sectionId] ?? f.sectionId,
          }))
        );
      }
      if (data.sections && dedupedSectionsRev.length) setSections(dedupedSectionsRev);
      else if (data.sections) setSections(data.sections);
      if (data.formDetails) setFormDetails(data.formDetails);
      // Raw / non-PDF smart form: hold the verbatim template JSON for the
      // JSON-only editor + publish path.
      const revIsRaw = isRawFormType(data.formDetails?.type);
      if (revIsRaw && data.rawFormJSON) {
        const split = splitRawFormBlob(data.rawFormJSON);
        setRawFormJson(split.template);
        setRawIntegrationJson(split.integration);
      } else {
        setRawFormJson(null);
        setRawIntegrationJson(null);
      }
      if (data.signatures && Object.keys(data.signatures).length > 0) {
        const { signatures: sigStripped, bindings } = resolveSignerAttributeBindingsForLoad(
          data.signatures,
          data.signerAttributeBindings,
        );
        setSignatures(normalizeSignatures(sigStripped));
        setSignerAttributeBindings(bindings);
      } else if (data.selectedRoles && data.selectedRoles.length > 0) {
        const template = buildSignaturesTemplate();
        setSignatures(normalizeSignatures(Object.fromEntries(
          data.selectedRoles.map((key) => [key, template[key]]).filter(([, v]) => !!v)
        )));
        setSignerAttributeBindings(data.signerAttributeBindings ?? []);
      } else {
        setSignatures(normalizeSignatures({}));
        setSignerAttributeBindings(data.signerAttributeBindings ?? []);
      }
      if (data.selectedCreators) setSelectedCreators(data.selectedCreators);
      if (data.formUploadSections) setFormUploadSections(data.formUploadSections);
      if (data.signatureAnnotations) setSignatureAnnotations(data.signatureAnnotations);
      setInitialsAnnotations(data.initialsAnnotations ?? []);
      if (data.formRules) setFormRules(data.formRules);
      if (data.offerCompare) setOfferCompare(data.offerCompare);
      setExtraFormSummary(data.extraFormSummary || {});
      setExtraDealSummary(data.extraDealSummary || {});
      setRulesJSON(data.rulesJSON ?? null);
      setInitials(data.initials ?? null);
      setAdditionalFields(sanitizeAdditionalPlacements(data.additionalFields ?? [], data));
      setInitialerAttributeBindings(data.initialerAttributeBindings ?? []);
      setSubsequentSigning(data.subsequentSigning ?? {});
      setSubsequentSigningAttributeBindings(data.subsequentSigningAttributeBindings ?? []);

      setFormJsonDirty(false);
      setFormRulesDirty(false);
      setOfferCompareDirty(false);

      // Restore PDF when loading from cloud (saved with form); clear if this revision has no PDF
      if (data.pdfB64) {
        try {
          const loadedPdf = base64ToFile(data.pdfB64, data.pdfName || "document.pdf");
          setPdfFile(loadedPdf);
          pdfBaselineFileRef.current = loadedPdf; // matches GCS; next save reuses if untouched
        } catch (e) {
          console.warn("Failed to restore PDF from cloud:", e);
          setPdfFile(null);
          pdfBaselineFileRef.current = null;
        }
      } else {
        setPdfFile(null);
        pdfBaselineFileRef.current = null;
      }

      setIsFormLoaded(true);
      // Raw forms are JSON-preview-only (tab 3). Otherwise, in JSON mode open on
      // Form Settings so locale/version are visible right away.
      if (revIsRaw) setActiveTab(3);
      else if (studioMode === "json") setActiveTab(1);
      trackLoad("cloud");
      handleBrowserClose();
    } catch (error) {
      console.error("Failed to load form from cloud:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Failed to load form."), severity: "error" });
    }
  };

  const handleLoadFromCloud = () => {
    if (formJsonDirty || formRulesDirty || offerCompareDirty) {
      setDirtyGuardAction(() => doLoadFromCloud);
      return;
    }
    doLoadFromCloud();
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const PUBLISHED_TYPE_ORDER = { form: 0, rules: 1, compare: 2 };

  const sortPublishedForModal = (published) => {
    return [...published].sort((a, b) => {
      const typeA = PUBLISHED_TYPE_ORDER[a.jsonType] ?? 99;
      const typeB = PUBLISHED_TYPE_ORDER[b.jsonType] ?? 99;
      if (typeA !== typeB) return typeA - typeB;
      return new Date(b.publishedAt) - new Date(a.publishedAt);
    });
  };


  const handleSaveClick = () => {
    const missing = [];
    if (!formDetails.locale) missing.push("locale");
    if (!formDetails.type) missing.push("type");
    if (!formDetails.formId || formDetails.formId === "00000000-0000-0000-0000-000000000000") missing.push("formId");
    if (!formDetails.name) missing.push("name");
    if (!formDetails.shortName) missing.push("shortName");

    if (missing.length > 0) {
      setSnackbar({
        open: true,
        message: `Form Details incomplete: ${missing.join(", ")}`,
        severity: "error",
      });
      return;
    }

    setSaveDialogName(formMeta.name || formDetails.shortName || "");
    setSaveDialogDescription(formMeta.description);
    setSaveDialogVersion(formDetails.version || `${new Date().getFullYear().toString().slice(-2)}.0.0`);
    setSaveDialogOpen(true);
  };

  const getUnannotatedSigners = () => {
    if (!signatures || Object.keys(signatures).length === 0) return [];
    const annotatedKeys = new Set(signatureAnnotations.map((a) => a.signerKey));
    return Object.entries(signatures)
      .map(([roleKey, roleValue]) => {
        const unannotated = (roleValue.signatures || []).filter((s) => !annotatedKeys.has(s.signerKey));
        return { roleKey, unannotated };
      })
      .filter((r) => r.unannotated.length > 0);
  };

  const doSave = async (signaturesOverride) => {
    const sigsToSave = signaturesOverride || signatures;
    setSaveInProgress(true);

    const { name: pName, description: pDesc, version: pVer } = pendingSaveRef.current;
    pendingSaveRef.current = null;

    // Sync version back into formDetails state
    if (pVer !== formDetails.version) {
      setFormDetails((prev) => ({ ...prev, version: pVer }));
    }

    // Reuse the stored GCS object when the PDF is byte-for-byte what we loaded/last saved
    // (same File instance) and we're updating an existing form. This skips re-encoding and,
    // via pdfSourceStudioFormDataId, tells the backend to reference the existing object
    // instead of uploading a duplicate. Any upload/replace/recovery makes a new File, so
    // identity breaks and we fall back to sending the bytes.
    const canReusePdf =
      !!pdfFile &&
      pdfFile === pdfBaselineFileRef.current &&
      !!formMeta.studioFormId &&
      !!formMeta.id;

    let pdfB64 = null;
    let pdfName = null;
    let pdfSourceStudioFormDataId = null;
    if (canReusePdf) {
      pdfSourceStudioFormDataId = formMeta.id;
    } else if (pdfFile) {
      try {
        pdfB64 = await fileToBase64(pdfFile);
        pdfName = pdfFile.name || "document.pdf";
      } catch (error) {
        console.error("Failed to encode PDF:", error);
      }
    }

    // Raw / non-PDF smart form: the verbatim template JSON is the source of
    // truth. Keep its `version` in lockstep with the revision version (pVer)
    // from the save dialog, and send it as an object so the backend stores its
    // exact key order. The structured field/section arrays are left empty for
    // these forms — they are never rebuilt from them.
    let rawFormJSONForSave = null;
    if (isRawMode && rawFormJson) {
      try {
        const parsedTemplate = JSON.parse(rawFormJson);
        parsedTemplate.version = pVer;
        const parsedIntegration = rawIntegrationJson ? JSON.parse(rawIntegrationJson) : buildIntegrationScaffold(parsedTemplate);
        parsedIntegration.version = pVer;
        // Persist both under the canonical two-key blob.
        rawFormJSONForSave = {
          [RAW_TEMPLATE_KEY]: parsedTemplate,
          [RAW_INTEGRATION_KEY]: parsedIntegration,
        };
        setRawFormJson(JSON.stringify(parsedTemplate, null, 2));
        setRawIntegrationJson(JSON.stringify(parsedIntegration, null, 2));
      } catch (e) {
        console.error("Raw form JSON is invalid on save:", e);
      }
    }

    const state = {
      studioFormId: formMeta.studioFormId || null,
      instanceVersion: formMeta.instanceVersion || 0,
      name: pName,
      description: pDesc,
      fields: isRawMode ? [] : fields,
      sections: isRawMode ? [] : sections,
      formDetails: { ...formDetails, version: pVer },
      signatures: sigsToSave,
      selectedCreators,
      formUploadSections,
      signatureAnnotations,
      initialsAnnotations,
      additionalFields,
      subsequentSigning,
      subsequentSigningAttributeBindings,
      signerAttributeBindings,
      initialerAttributeBindings,
      formRules,
      offerCompare,
      extraFormSummary,
      extraDealSummary,
      rulesJSON,
    initials,
      initials,
      // Only set for raw forms; ordinary PDF forms omit it (buildStudioFormData
      // drops null so the field never appears in the request).
      rawFormJSON: rawFormJSONForSave,
      jsonStatus: {
        dirty: { form: formJsonDirty, rules: formRulesDirty, compare: offerCompareDirty },
      },
      pdfB64,
      pdfName,
      pdfSourceStudioFormDataId,
    };

    try {
      let result;
      if (formMeta.studioFormId) {
        result = await updateFormToCloud(formMeta.studioFormId, state);
      } else {
        result = await saveFormToCloud(state);
      }
      if (signaturesOverride) {
        setSignatures(signaturesOverride);
      }
      setFormMeta({
        id: result.id,
        studioFormId: result.studioFormId || null,
        name: result.name,
        description: result.description || pDesc || "",
        status: result.status,
        instanceVersion: result.instanceVersion,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt,
        published: result.published || [],
        metadata: result.metadata || {},
        createdBy: result.createdBy || null,
      });
      setFormJsonDirty(false);
      setFormRulesDirty(false);
      setOfferCompareDirty(false);
      // Whatever PDF we just persisted (uploaded bytes or reused reference) now matches GCS
      // for the new revision (result.id), so the next unchanged save can reference it.
      pdfBaselineFileRef.current = pdfFile;
      clearAutoSave();
      trackSave();
      setSnackbar({ open: true, message: "Saved to cloud.", severity: "success" });
    } catch (error) {
      console.error("Cloud save failed:", error);
      setSnackbar({ open: true, message: formatApiError(error, "Cloud save failed."), severity: "error" });
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleSaveConfirm = () => {
    setSaveDialogOpen(false);

    pendingSaveRef.current = {
      name: saveDialogName.trim(),
      description: saveDialogDescription.trim(),
      version: saveDialogVersion.trim(),
    };

    // Prune is a PDF-v2 concept: it decides what to keep from signatureAnnotations, the boxes
    // placed on a PDF. A raw / mevi-sign form has no PDF to annotate, so every signer would look
    // unannotated and pruning would empty signaturesJSON. Never offer it for those.
    // Offer Prune when there is anything for it to fix: unannotated signers, or a role whose
    // max / additionalSignaturePage no longer match its slot count.
    const unannotated = isPdfV2Form ? getUnannotatedSigners() : [];
    const slotFixes = isPdfV2Form
      ? pruneUnannotatedSigners({ signatures, signatureAnnotations, initialsAnnotations, signerAttributeBindings }).slotFixes
      : [];
    if (unannotated.length > 0 || slotFixes.length > 0) {
      setSigWarningDetails(unannotated);
      setSigWarningSlotFixes(slotFixes);
      setSigWarningOpen(true);
      return;
    }

    doSave();
  };

  const handleSaveContinue = () => {
    setSigWarningOpen(false);
    doSave();
  };

  const handleSavePrune = () => {
    setSigWarningOpen(false);
    // Rule lives in utils/pruneSignatures.js, shared with scripts/prune-save-publish.js.
    const { signatures: pruned, signerAttributeBindings: keptBindings } = pruneUnannotatedSigners({
      signatures,
      signatureAnnotations,
      signerAttributeBindings,
    });
    setSignerAttributeBindings(keptBindings);
    doSave(pruned);
  };

  const handleDiscard = () => {
    // Reset all state to initial values
    setFormMeta({
      id: null,
      studioFormId: null,
      name: "",
      description: "",
      status: "draft",
      instanceVersion: 0,
      createdAt: null,
      updatedAt: null,
      published: [],
      metadata: {},
      createdBy: null,
    });
    setFields([]);
    setSections([
      {
        id: "section-0",
        title: "Default Section",
        attributeName: "defaultSection",
        subtitle: "",
        goTo: "",
      },
    ]);
    setSignatures({});
    setSelectedCreators([]);
    setFormDetails({
      locale: "",
      type: "",
      name: "",
      shortName: "",
      category: "",
      version: "",
    });
    setFormRules({ formNotes: "", fields: [] });
    setOfferCompare({ fields: [] });
    setExtraFormSummary({});
    setExtraDealSummary({});
    setRulesJSON(null);
    setInitials(null);
    setAdditionalFields([]);
    setSubsequentSigning({});
    setSubsequentSigningAttributeBindings([]);
    setSignerAttributeBindings([]);
    setRawFormJson(null); // Clear raw / non-PDF smart form JSON
    setRawIntegrationJson(null); // Clear companion integration JSON
    setFormJsonDirty(false);
    setFormRulesDirty(false);
    setOfferCompareDirty(false);
    setIsFormLoaded(false); // Reset transition state
    setPdfFile(null);
    setShowPdfButtonGlow(false); // Reset glow state
    setImportedFromJson(false); // Reset JSON import flag
    clearSavedPdf();
  };

  const allowedModes =
    activeRole === "admin"
      ? ["annotator", "json"]
      : activeRole
        ? [activeRole]
        : ["annotator", "json"];

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) return;
    setLoginLoading(true);
    setLoginError("");
    try {
      const profile = await loginWithEmailAndPassword(loginEmail, loginPassword);
      const roles = profile.roles || [];
      const rolePriority = ["studio_super_admin", "studio_admin", "studio_json", "studio_annotator"];
      const studioRole = rolePriority.find((r) => roles.includes(r));
      const role = studioRole === "studio_super_admin" ? "admin" : studioRole ? studioRole.replace("studio_", "") : "";
      if (!role) {
        setLoginError("No authorized studio profile found for this account.");
        return;
      }
      setIsSuperAdmin(roles.includes("studio_super_admin"));
      setActiveRole(role);
      setSnackbar((s) => ({ ...s, open: false }));
      localStorage.setItem("formStudioRole", role);
      localStorage.setItem("formStudioEmail", loginEmail);
      localStorage.setItem("formStudioToken", getToken());
    } catch (err) {
      setLoginError(err.message || "Login failed.");
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <ThemeProvider theme={mode === "light" ? lightTheme : darkTheme}>
      <CssBaseline />
      <ThemeTransition
        active={isTransitioning}
        direction={transitionDirection}
      />
      {!activeRole ? (
        <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Paper elevation={3} sx={{ p: 4, maxWidth: 360, width: "100%" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Sign In</Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              disabled={loginLoading}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
            <TextField
              fullWidth
              size="small"
              label="Password"
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              sx={{ mt: 2 }}
              disabled={loginLoading}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
            {loginError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {loginError}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleLogin}
                disabled={!loginEmail || !loginPassword || loginLoading}
              >
                {loginLoading ? "Signing in\u2026" : "Go"}
              </Button>
            </Box>
          </Paper>
        </Box>
      ) : (
      <>
      {/* Line height prompt (annotator, first PDF upload when no line height set) */}
      <Dialog
        open={
          studioMode === "annotator" &&
          !showAnalystForAnnotator &&
          !!pdfFile &&
          defaultLineHeightPx == null &&
          !lineHeightPromptDismissed
        }
        onClose={() => setLineHeightPromptDismissed(true)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Set line height?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Match one line of text so field heights stay consistent. You can set or change it anytime from the PDF header.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineHeightPromptDismissed(true)} color="inherit">
            Later
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setLineHeightPromptDismissed(true);
              handleLineHeightSet();
            }}
          >
            Set line height
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recovery Banner */}
      <Snackbar
        open={showRecoveryBanner && !!activeRole}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 2 }}
      >
        <Alert
          severity="info"
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button color="inherit" size="small" onClick={handleRestoreData}>
                Restore
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleDiscardRecovery}
              >
                Discard
              </Button>
              <MuiIconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleDiscardRecovery}
              >
                <CloseIcon fontSize="small" />
              </MuiIconButton>
            </Box>
          }
        >
          Previous work found from{" "}
          {recoveredData
            ? new Date(recoveredData.timestamp).toLocaleString()
            : ""}
          . Restore it?
        </Alert>
      </Snackbar>

      <DecisionOptionCountDialog
        open={decisionOptionCountDialogOpen}
        onClose={() => setDecisionOptionCountDialogOpen(false)}
        onConfirm={commitIdentifyAddDecisionSection}
        title="New Decision Section"
      />

      <Dialog open={flagDialogOpen} onClose={closeFlagDialog} fullWidth maxWidth="xs">
        <DialogTitle>Flag this form</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Add a brief note so you remember why this form is flagged.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            label="Note"
            value={flagDialogNote}
            onChange={(e) => setFlagDialogNote(e.target.value.slice(0, FORM_FLAG_NOTE_MAX_LEN))}
            inputProps={{ maxLength: FORM_FLAG_NOTE_MAX_LEN }}
            placeholder="e.g. Needs legal review"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFlagDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleFlagDialogSave} disabled={!flagDialogNote.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish to All Buckets (per-locale batch) */}
      <Dialog open={!!publishAllLocale} onClose={closePublishAll} maxWidth="sm" fullWidth>
        <DialogTitle>
          {publishAllTarget === PUBLISH_ALL_TARGET_ALL
            ? "Publish to All Buckets"
            : `Publish to ${publishAllTarget}`}
        </DialogTitle>
        <DialogContent>
          {!publishAllDone ? (
            <>
              <Typography variant="body2">
                Publish all forms in <strong>{publishAllLocale}</strong> that are Ready for STG to{" "}
                {publishAllTarget === PUBLISH_ALL_TARGET_ALL ? (
                  "all STG buckets?"
                ) : (
                  <>
                    <strong>{publishAllTarget}</strong> only?
                  </>
                )}
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel id="publish-all-target-label">Target</InputLabel>
                <Select
                  labelId="publish-all-target-label"
                  label="Target"
                  value={publishAllTarget}
                  disabled={publishAllRunning}
                  onChange={(e) => setPublishAllTarget(e.target.value)}
                >
                  <MenuItem value={PUBLISH_ALL_TARGET_ALL}>
                    All buckets ({PUBLISH_ALL_FORMS_ENVS.join(", ")}, then Staging)
                  </MenuItem>
                  {PUBLISH_ALL_FORMS_ENVS.map((env) => (
                    <MenuItem key={env} value={env}>
                      {env} only (dry run)
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {publishAllTarget === PUBLISH_ALL_TARGET_ALL
                    ? "Staging is published last, which retires Ready for STG."
                    : "Staging is not touched, so Ready for STG stays set and you can run the full batch after spot-checking."}
                </FormHelperText>
              </FormControl>
              {publishAllRunning && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Publishing…
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ maxHeight: 360, overflow: "auto" }}>
              {publishAllErrors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5 }}>
                    Errors
                  </Typography>
                  {publishAllErrors.map((er, i) => (
                    <Typography key={i} variant="body2" color="error.main">
                      {er.shortName} — {er.error}
                    </Typography>
                  ))}
                </Box>
              )}
              {publishAllResults.length > 0 ? (
                // Sorted on a copy at render time: the batch appends results as
                // each form finishes, and that arrival order is what drives the
                // live progress — only the final list is alphabetised.
                sortByShortName(publishAllResults).map((r, i) => (
                  <Typography key={i} variant="body2">
                    <strong>{r.shortName}</strong> published to {r.publishedTo.join(", ")}
                    {r.description ? `: ${r.description}` : ""}
                  </Typography>
                ))
              ) : (
                publishAllErrors.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nothing to publish.
                  </Typography>
                )
              )}
              {publishAllSkipped.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Already published, skipped: {[...publishAllSkipped].sort().join(", ")}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!publishAllDone ? (
            <>
              <Button onClick={closePublishAll} disabled={publishAllRunning}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => runPublishToAllBuckets(publishAllLocale, publishAllTarget)}
                disabled={publishAllRunning}
              >
                {publishAllRunning ? "Publishing…" : "Continue"}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setPublishAllLocale(null)}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{formMeta.id ? "Update Form" : "Save New Form"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={saveDialogName}
            onChange={(e) => setSaveDialogName(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={saveDialogDescription}
            onChange={(e) => setSaveDialogDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            minRows={2}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Version"
            value={saveDialogVersion}
            onChange={(e) => setSaveDialogVersion(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
          {formMeta.metadata?.stgReady === true && (
            <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: "warning.main", borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <ScienceIcon sx={{ fontSize: 20, color: "success.main" }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Ready to deploy to STG
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                This revision is marked ready to deploy to STG. Turn it off to save a new revision.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={handleClearStgReadyForSave}
                disabled={
                  stgReadyClearing || !(isSuperAdmin || formMeta.createdBy === getActiveProfile()?.id)
                }
              >
                {stgReadyClearing ? "Turning off…" : "Turn off"}
              </Button>
              {!(isSuperAdmin || formMeta.createdBy === getActiveProfile()?.id) && (
                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  Only the owner of this revision or a studio super admin can turn this off.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveConfirm}
            disabled={
              !saveDialogName.trim() ||
              !saveDialogDescription.trim() ||
              formMeta.metadata?.stgReady === true
            }
          >
            {formMeta.id ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unannotated Signatures Warning Dialog */}
      <Dialog open={sigWarningOpen} onClose={() => setSigWarningOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{sigWarningDetails.length > 0 ? "Unannotated Signatures" : "Signer Slot Counts"}</DialogTitle>
        <DialogContent>
          {sigWarningDetails.length > 0 && (
            <>
              <Typography sx={{ mb: 1 }}>
                Some signature roles have signers without PDF annotations:
              </Typography>
              {sigWarningDetails.map((r) => (
                <Typography key={r.roleKey} variant="body2" sx={{ ml: 2 }}>
                  <strong>{r.roleKey}</strong>: {r.unannotated.map((s) => s.signerKey).join(", ")}
                </Typography>
              ))}
            </>
          )}
          {sigWarningSlotFixes.length > 0 && (
            <>
              <Typography sx={{ mb: 1, mt: sigWarningDetails.length > 0 ? 2 : 0 }}>
                Some roles have a max / additional signature page that does not match their slots:
              </Typography>
              {sigWarningSlotFixes.map((f) => (
                <Typography key={f.roleKey} variant="body2" sx={{ ml: 2 }}>
                  <strong>{f.roleKey}</strong>: {f.slots} slot{f.slots === 1 ? "" : "s"} — max{" "}
                  {String(f.from.max)} → {f.to.max}, page {String(f.from.additionalSignaturePage)} →{" "}
                  {f.to.additionalSignaturePage}
                  {f.from.default !== f.to.default && `, default ${String(f.from.default)} → ${f.to.default}`}
                </Typography>
              ))}
            </>
          )}
          <Typography sx={{ mt: 2 }}>
            Continue saving as-is, or prune and fix the counts?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSigWarningOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveContinue}>Continue</Button>
          <Button onClick={handleSavePrune} color="warning" variant="contained">
            Prune &amp; Fix Counts
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registry import mismatch */}
      <Dialog
        open={registryImportDialogOpen}
        onClose={() => {
          pendingImportRef.current = null;
          setRegistryImportDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Registry mismatch</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>{registryImportMessage}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Apply registry fix sets Form ID, type, and short name from the production registry.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { pendingImportRef.current = null; setRegistryImportDialogOpen(false); }}>
            Cancel
          </Button>
          <Button onClick={handleRegistryImportKeep}>Keep imported values</Button>
          <Button variant="contained" onClick={handleRegistryImportApplyFix}>
            Apply registry fix
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Publish</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={publishDialogName}
            onChange={(e) => setPublishDialogName(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={publishDialogDescription}
            onChange={(e) => setPublishDialogDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            size="small"
            label="Version"
            value={publishDialogVersion}
            sx={{ mt: 2 }}
            required
            disabled
            helperText="Version can only be changed in the Save to Cloud flow."
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }} required>
            <InputLabel>JSON Type</InputLabel>
            <Select
              value={publishDialogJsonType}
              label="JSON Type"
              onChange={(e) => setPublishDialogJsonType(e.target.value)}
            >
              {/* Only "form" is supported by the backend publish path today. */}
              <MenuItem value="form">Form</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mt: 2 }} required>
            <InputLabel>Environment</InputLabel>
            <Select
              value={publishDialogEnv}
              label="Environment"
              onChange={(e) => setPublishDialogEnv(e.target.value)}
            >
              {getAvailableEnvs(publishDialogPublished).map((env) => (
                <MenuItem key={env} value={env}>{env}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {publishDialogJsonType === "form" && registry && publishRegistryValidation && !publishRegistryValidation.skipped && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Registry alignment</Typography>
              <Typography variant="body2">
                Locale: {formDetails.locale || "—"}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                Form ID (editor): {formDetails.formId || "—"}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                Form ID (registry): {publishRegistryValidation.registryRow?.formId || "—"}
              </Typography>
              <Typography variant="body2">
                Type (editor): {formDetails.type || "—"}
              </Typography>
              <Typography variant="body2">
                Type (registry): {publishRegistryValidation.registryRow?.type ?? "—"}
              </Typography>
              {!publishRegistryValidation.valid && (
                <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                  {formatRegistryValidationMessage(publishRegistryValidation, formDetails)}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePublishConfirm}
            disabled={
              !publishDialogName.trim() ||
              !publishDialogEnv ||
              publishInProgress ||
              !!publishBlockedReason
            }
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Guard Dialog */}
      <Dialog open={!!dirtyGuardAction} onClose={() => setDirtyGuardAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>You have unsaved changes that will be lost. Continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDirtyGuardAction(null)}>Cancel</Button>
          <Button onClick={() => { const action = dirtyGuardAction; setDirtyGuardAction(null); action(); }} color="error">
            Discard &amp; Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Result Dialog */}
      <Dialog open={!!publishResultData} onClose={() => setPublishResultData(null)} fullWidth maxWidth="md">
        <DialogTitle>Publish Result</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, margin: 0 }}>
            {publishResultData ? JSON.stringify(publishResultData, null, 2) : ""}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishResultData(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Picker Dialog */}
      <Dialog open={assignPickerOpen} onClose={() => { setAssignPickerOpen(false); setAssignMenuForm(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Assign To</DialogTitle>
        <DialogContent>
          {assignableProfiles.map((profile) => (
            <MenuItem key={profile.id} onClick={() => handleAssignProfile(profile.id)}>
              {profile.displayName || profile.email}
            </MenuItem>
          ))}
        </DialogContent>
      </Dialog>

      <input
        ref={pdfReplaceInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) setPendingReplacePdf(file);
          e.target.value = "";
        }}
      />

      <Dialog
        open={!!pendingReplacePdf}
        onClose={() => setPendingReplacePdf(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Replace PDF?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Replace the current PDF with <strong>{pendingReplacePdf?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Fields, options, signatures, rules, JSON edits, and all other form work
            will be preserved.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }} color="warning.main">
            If the new PDF has a different layout, existing PDF placement boxes may
            no longer align and may need to be repositioned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingReplacePdf(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handlePdfReplace(pendingReplacePdf);
              setPendingReplacePdf(null);
            }}
          >
            Replace PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteTarget?.type === "form" ? "this form" : "this revision"}
            {deleteTarget?.name ? ` "${deleteTarget.name}"` : ""}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!publishedDialogData} onClose={() => setPublishedDialogData(null)} fullWidth maxWidth="sm">
        <DialogTitle>Published History</DialogTitle>
        <DialogContent>
          {publishedDialogData && sortPublishedForModal(publishedDialogData).map((p, i) => (
            <Box key={i} sx={{ display: "flex", gap: 2, py: 0.5, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" sx={{ width: 70 }}>{p.jsonType}</Typography>
              <Typography variant="body2" sx={{ width: 100 }}>{p.env}</Typography>
              <Typography variant="body2" color="text.secondary">{formatDate(p.publishedAt)}</Typography>
              <Typography variant="body2" color="text.secondary">{p.publishedByUserProfile?.displayName || p.publishedBy || ""}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishedDialogData(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Events Dialog */}
      <Dialog open={!!trackingDialogData} onClose={() => setTrackingDialogData(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {trackingDialogData?.title}
          <Button size="small" onClick={() => {
            if (!trackingDialogData?.rawJson) return;
            const blob = new Blob([JSON.stringify(trackingDialogData.rawJson, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "events.json";
            a.click();
            URL.revokeObjectURL(url);
          }}>Export RAW JSON</Button>
        </DialogTitle>
        <DialogContent>
          {trackingDialogData?.sections.map((section, si) => (
            <Box key={si} sx={{ mb: 2 }}>
              {section.name !== undefined && (
                <Box sx={{ bgcolor: "action.hover", px: 1.5, py: 1, borderRadius: 1, mb: 1 }}>
                  <Typography variant="body2">Name: {section.name}  ·  Version: {section.version}</Typography>
                  <Typography variant="caption" color="text.secondary">Updated: {formatDate(section.updatedAt)}  ·  Instance Version: {section.instanceVersion ?? "—"}</Typography>
                </Box>
              )}
              {section.events.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No events</Typography>
              ) : section.events.map((evt, ei) => {
                const baseKeys = ["id", "createdAt", "updatedAt", "deletedAt", "instanceVersion",
                  "createdBy", "updatedBy", "deletedBy", "createdByUserProfile", "updatedByUserProfile", "metadata",
                  "studioFormId", "studioFormDataId", "profileId"];
                const eventFields = Object.entries(evt)
                  .filter(([k, v]) => !baseKeys.includes(k) && k !== "type" && v != null && v !== "")
                  .map(([k, v]) => `${k}: ${v}`)
                  .join("  ·  ");
                return (
                  <Box key={ei} sx={{ py: 0.5, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="body2">
                      {evt.createdByUserProfile?.displayName || evt.createdBy || "—"}  ·  Event: {evt.type}{eventFields ? `  ·  ${eventFields}` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Date: {formatDate(evt.createdAt)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDialogData(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* General Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? null : 5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ whiteSpace: "pre-line", maxWidth: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          height: "100vh", // Force full viewport height
          display: "flex",
          flexDirection: "column",
          pb: 0, // Remove padding to allow full height
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Flowing Wave Pattern Background */}
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: -1,
            opacity: 0.12,
            pointerEvents: "none",
            "@keyframes float": {
              "0%, 100%": {
                transform: "translate3d(0, 0, 0) rotate(0deg)",
              },
              "50%": {
                transform: "translate3d(0, -30px, 0) rotate(3deg)",
              },
            },
            "@keyframes slideInUp": {
              "0%": {
                transform: "translateY(50px)",
                opacity: 0,
                opacity: 1,
              },
            },
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
                transform: "scale(0.95)",
              },
              "100%": {
                opacity: 1,
                transform: "scale(1)",
              },
            },
          }}
        ></Box>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            py: 1, // Reduced padding
            px: 3,
            borderRadius: 0,
            border: "none", // Explicitly none
            boxShadow: "none", // Explicitly none
            bgcolor: "transparent",
            mb: 0, // Remove bottom margin to let HomePage control spacing
          }}
        >
          <Container maxWidth="xl">
            <Box
              display="flex"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              {/* Left Logo - flex: 0 so it never shrinks */}
              {isFormLoaded ? (
                <Box display="flex" alignItems="center" gap={2} sx={{ flex: "0 0 auto" }}>
                  <img
                    src="/mevi-logo.svg"
                    alt="Mevi Logo"
                    style={{
                      height: "40px",
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: "-0.5px",
                      background:
                        mode === "light"
                          ? "linear-gradient(45deg, #2c3e50 30%, #3498db 90%)"
                          : "linear-gradient(45deg, #f4f4f5 30%, #2dd4bf 90%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Mevi Form Studio
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ flex: "0 0 auto" }} />
              )}

              {/* Center: Form name — grows to fill space between left and right */}
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center", overflow: "hidden", px: 2 }}>
                {isFormLoaded && (formMeta.name || formDetails?.name || formDetails?.shortName) && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {formMeta.name || formDetails?.name || formDetails?.shortName}
                  </Typography>
                )}
              </Box>

              {/* Right Actions */}
              <Box
                sx={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  zIndex: 1,
                }}
              >
                {/* Upload PDF Button - Show when form loaded but no PDF.
                    Hidden for raw / non-PDF smart forms (they have no PDF). */}
                {isFormLoaded && !pdfFile && !isRawMode && (
                  <>
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-block",
                      }}
                    >
                      {showPdfButtonGlow && (
                        <Box
                          className="pdf-button-glow"
                          sx={{
                            position: "absolute",
                            top: "-6px",
                            left: "-6px",
                            right: "-6px",
                            bottom: "-6px",
                            borderRadius: "4px",
                            border: (theme) =>
                              `2px solid ${theme.palette.primary.main}`,
                            background: "transparent",
                            boxShadow: (theme) =>
                              `0 0 15px ${theme.palette.primary.main}, 0 0 25px ${theme.palette.primary.main}, 0 0 35px ${theme.palette.primary.main}`,
                            animation: "waveGlowFade 6s ease-in-out forwards",
                            zIndex: -1,
                            pointerEvents: "none",
                            "@keyframes waveGlowFade": {
                              "0%": {
                                opacity: 0.5,
                                transform: "scale(1)",
                                filter: "brightness(1)",
                              },
                              "8.33%": {
                                opacity: 0.8,
                                transform: "scale(1.02)",
                                filter: "brightness(1.2)",
                              },
                              "16.67%": {
                                opacity: 0.6,
                                transform: "scale(1)",
                                filter: "brightness(1)",
                              },
                              "25%": {
                                opacity: 0.8,
                                transform: "scale(1.02)",
                                filter: "brightness(1.2)",
                              },
                              "33.33%": {
                                opacity: 0.5,
                                transform: "scale(1)",
                                filter: "brightness(1)",
                              },
                              "41.67%": {
                                opacity: 0.8,
                                transform: "scale(1.02)",
                                filter: "brightness(1.2)",
                              },
                              "50%": {
                                opacity: 0.6,
                                transform: "scale(1)",
                                filter: "brightness(1)",
                              },
                              "58.33%": {
                                opacity: 0.8,
                                transform: "scale(1.02)",
                                filter: "brightness(1.2)",
                              },
                              "66.67%": {
                                opacity: 0.5,
                                transform: "scale(1)",
                                filter: "brightness(1)",
                              },
                              "75%": {
                                opacity: 0.8,
                                transform: "scale(1.02)",
                                filter: "brightness(1.2)",
                              },
                              "83.33%": {
                                opacity: 0.6,
                                transform: "scale(1)",
                                filter: "brightness(1)",
                              },
                              "83.34%": {
                                opacity: 0.6,
                                transform: "scale(1)",
                                filter: "brightness(1)",
                              },
                              "100%": {
                                opacity: 0,
                                transform: "scale(1)",
                                filter: "brightness(0.5)",
                              },
                            },
                          }}
                        />
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => pdfUploadInputRef.current?.click()}
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          borderColor: "primary.main",
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: "rgba(0, 168, 150, 0.08)",
                            borderColor: "primary.dark",
                          },
                        }}
                      >
                        Upload PDF
                      </Button>
                    </Box>
                    <input
                      ref={pdfUploadInputRef}
                      type="file"
                      accept="application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handlePdfUpload(e.target.files[0]);
                        }
                      }}
                    />
                  </>
                )}

                {/* Forms Browser */}
                {!!activeRole && (
                  <Button
                    variant="outlined"
                    startIcon={<CloudOutlined />}
                    onClick={() => {
                      setFormsBrowserView("list");
                      fetchStudioForms();
                    }}
                  >
                    Forms
                  </Button>
                )}

                {/* Save to Cloud — only when form data is loaded */}
                {isFormLoaded && (
                  <Tooltip
                    title={
                      !formMeta.id
                        ? "Save to cloud (new form)"
                        : (formJsonDirty || formRulesDirty || offerCompareDirty)
                          ? "Save to cloud (unsaved changes)"
                          : "Save to cloud (all changes saved)"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleSaveClick}
                        // Raw / non-PDF smart forms have no PDF, so don't gate Save on one.
                        disabled={saveInProgress || (!pdfFile && !isRawMode)}
                        sx={{
                          ...(!formMeta.id
                            ? {}
                            : (formJsonDirty || formRulesDirty || offerCompareDirty)
                              ? { bgcolor: "warning.main", "&:hover": { bgcolor: "warning.dark" } }
                              : { bgcolor: "success.main", "&:hover": { bgcolor: "success.dark" } }
                          ),
                        }}
                      >
                        Save
                      </Button>
                    </span>
                  </Tooltip>
                )}

                {/* Publish — loaded-form only; hidden for users who can't publish at all.
                    Muted-but-clickable when blocked; clicking pops the reason. */}
                {isFormLoaded && canEverPublish && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={(e) => {
                        if (publishBlockedReason) setPublishBlockedAnchor(e.currentTarget);
                        else handlePublishClick();
                      }}
                      sx={{
                        ml: 1,
                        bgcolor: publishBlockedReason ? "action.disabledBackground" : "#a5d6a7",
                        color: publishBlockedReason ? "text.disabled" : "common.black",
                        boxShadow: publishBlockedReason ? "none" : undefined,
                        "&:hover": {
                          bgcolor: publishBlockedReason ? "action.disabledBackground" : "#81c784",
                        },
                      }}
                    >
                      Publish
                    </Button>
                    <Popover
                      open={Boolean(publishBlockedAnchor)}
                      anchorEl={publishBlockedAnchor}
                      onClose={() => setPublishBlockedAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                      transformOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                      <Typography variant="body2" sx={{ p: 1.5, maxWidth: 260 }}>
                        {publishBlockedReason}
                      </Typography>
                    </Popover>
                  </>
                )}

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={studioMode}
                  onChange={(_, value) => {
                    if (value) setStudioMode(value);
                  }}
                  sx={{
                    mr: 1,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.5,
                    },
                  }}
                >
                  {/* Annotator mode is PDF-only — hidden for raw / non-PDF smart forms. */}
                  {allowedModes.includes("annotator") && !isRawMode && (
                    <ToggleButton value="annotator">
                      <PictureAsPdfIcon sx={{ mr: 0.5 }} fontSize="small" />
                      Annotator
                    </ToggleButton>
                  )}
                  {allowedModes.includes("json") && (
                    <ToggleButton value="json">
                      <DesignServicesIcon sx={{ mr: 0.5 }} fontSize="small" />
                      JSON
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
                {activeRole && (
                  <>
                    <MuiIconButton
                      size="small"
                      onClick={handleProfileMenuOpen}
                      color="inherit"
                    >
                      {getActiveProfile()?.avatarUrl ? (
                        <img
                          src={getActiveProfile().avatarUrl}
                          alt="Profile"
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <AccountCircleIcon fontSize="small" />
                      )}
                    </MuiIconButton>
                    <Menu
                      anchorEl={profileMenuAnchor}
                      open={Boolean(profileMenuAnchor)}
                      onClose={handleProfileMenuClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleProfileMenuClose();
                          handleThemeToggle();
                        }}
                      >
                        {mode === "dark" ? (
                          <LightModeIcon fontSize="small" sx={{ mr: 1 }} />
                        ) : (
                          <DarkModeIcon fontSize="small" sx={{ mr: 1 }} />
                        )}
                        {mode === "dark" ? "Light Mode" : "Dark Mode"}
                      </MenuItem>
                      <MenuItem
                        // Additive: raw / non-PDF smart forms have no fields or
                        // PDF, so also enable when a raw form is loaded. Non-raw
                        // behavior is unchanged.
                        disabled={!(fields.length > 0 || pdfFile || (isRawMode && rawFormJson))}
                        onClick={() => {
                          handleProfileMenuClose();
                          handleDiscard();
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                        Start Over
                      </MenuItem>
                      <Divider />
                      {getActiveProfile()?.displayName && (
                        <Box sx={{ px: 2, py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {getActiveProfile().displayName}
                          </Typography>
                        </Box>
                      )}
                      <MenuItem onClick={handleLogout}>
                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                        Log out
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Box>
          </Container>
        </Paper>

        {/* Studio Forms Browser Overlay */}
        {formsBrowserView && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
              pt: "64px",
              pb: 4,
            }}
          >
            {/* Backdrop */}
            <Box
              onClick={handleBrowserClose}
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.4)",
              }}
            />
            {/* Panel */}
            <Paper
              elevation={8}
              sx={{
                position: "relative",
                width: "80%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: 2,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 3,
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {formsBrowserView !== "list" && (
                    <MuiIconButton size="small" onClick={handleBrowserBack}>
                      <ArrowBackIcon />
                    </MuiIconButton>
                  )}
                  <Typography variant="h6">
                    {formsBrowserView === "list" ? "Studio Forms" : formsBrowserView === "revisions" ? "Studio Form" : "Studio Form Data"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {formsBrowserView === "list" && (
                    <>
                      <TextField
                        size="small"
                        placeholder="Search by name or short name"
                        value={formsListSearch}
                        onChange={(e) => setFormsListSearch(e.target.value)}
                        sx={{ minWidth: 220, "& .MuiInputBase-root": { bgcolor: "background.paper" } }}
                      />
                      <Tooltip title="Refresh" arrow>
                        <MuiIconButton size="small" onClick={fetchStudioForms}>
                          <RefreshIcon />
                        </MuiIconButton>
                      </Tooltip>
                    </>
                  )}
                  {formsBrowserView === "revisions" && (
                    <Tooltip title="Refresh" arrow>
                      <MuiIconButton size="small" onClick={() => selectedStudioForm && handleStudioFormClick(selectedStudioForm)}>
                        <RefreshIcon />
                      </MuiIconButton>
                    </Tooltip>
                  )}
                  <MuiIconButton size="small" onClick={handleBrowserClose}>
                    <CloseIcon />
                  </MuiIconButton>
                </Box>
              </Box>

              {/* Info row (revisions + detail views) */}
              {(formsBrowserView === "revisions" || formsBrowserView === "detail") && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                      <strong>Name:</strong> {selectedStudioForm?.name || "—"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" component="span" sx={{ flexShrink: 0, fontWeight: 600 }}>
                        Description:
                      </Typography>
                      <Tooltip title={(formsBrowserView === "detail" ? selectedStudioFormData?.description : selectedStudioForm?.description) || ""} placement="bottom-start" enterDelay={400}>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            minWidth: 0,
                            flex: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                        >
                          {(formsBrowserView === "detail" ? selectedStudioFormData?.description : selectedStudioForm?.description) || "—"}
                        </Typography>
                      </Tooltip>
                    </Box>
                    {formsBrowserView === "revisions" && (
                      <Typography variant="body2" sx={{ flexShrink: 0 }}>
                        <strong>Version:</strong> {selectedStudioForm?.version || "—"}
                      </Typography>
                    )}
                  </Box>
                  {formsBrowserView === "detail" && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CloudDownloadOutlined />}
                      onClick={handleLoadFromCloud}
                      sx={{ flexShrink: 0 }}
                    >
                      Load
                    </Button>
                  )}
                </Box>
              )}

              {/* View: List */}
              {formsBrowserView === "list" && (
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  <Tabs
                    value={formsListTab}
                    onChange={(_, nextValue) => setFormsListTab(nextValue)}
                    sx={{ mb: 2 }}
                  >
                    <Tab label="All" value="all" />
                    <Tab label="Assigned to me" value="assigned" />
                    {(activeRole === "admin" || activeRole === "json") && (
                      <Tab label="Annotator complete" value="complete" />
                    )}
                    {(activeRole === "admin" || activeRole === "json") && (
                      <Tab label="JSON complete" value="jsonComplete" />
                    )}
                  </Tabs>
                  {(() => {
                    const activeProfileId = getActiveProfile()?.id;
                    const searchLower = (formsListSearch || "").trim().toLowerCase();
                    let visibleForms =
                      formsListTab === "assigned"
                        ? studioForms.filter((form) => form.assignedTo === activeProfileId)
                        : formsListTab === "complete"
                          ? studioForms.filter((form) => !!annotationCompleteMap[form.id])
                          : formsListTab === "jsonComplete"
                            ? studioForms.filter((form) => !!jsonCompleteMap[form.id])
                            : studioForms;
                    if (searchLower) {
                      visibleForms = visibleForms.filter(
                        (form) =>
                          (form.name || "").toLowerCase().includes(searchLower) ||
                          (form.shortName || "").toLowerCase().includes(searchLower) ||
                          (form.formName || "").toLowerCase().includes(searchLower),
                      );
                    }
                    if (formsListTab === "assigned") {
                      visibleForms = [...visibleForms].sort((a, b) => {
                        const aComplete = !!annotationCompleteMap[a.id];
                        const bComplete = !!annotationCompleteMap[b.id];
                        return (aComplete ? 1 : 0) - (bComplete ? 1 : 0);
                      });
                    }

                    if (studioFormsLoading) {
                      return (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                          <CircularProgress />
                        </Box>
                      );
                    }

                    if (visibleForms.length === 0) {
                      return (
                        <Typography color="text.secondary" sx={{ textAlign: "center", py: 8 }}>
                          {searchLower
                            ? "No forms match your search"
                            : formsListTab === "assigned"
                              ? "No forms assigned to you"
                              : formsListTab === "complete"
                                ? "No annotator-complete forms"
                                : formsListTab === "jsonComplete"
                                  ? "No JSON-complete forms"
                                  : "No forms found"}
                        </Typography>
                      );
                    }

                    // Within-locale ordering: bucket by "ready to deploy to STG"
                    // and publish state, then newest-updated first inside each
                    // bucket. Buckets (top -> bottom):
                    //   0: stgReady
                    //   1: published to Staging but NOT to all 5 envs
                    //   2: published to all 5 envs (Staging + Forms-1..4)
                    //   3: published to one+ Forms-N but NOT Staging
                    //   4: everything else
                    const FORMS_ENVS = ["Forms-1", "Forms-2", "Forms-3", "Forms-4"];
                    const ALL_ENVS = ["Staging", ...FORMS_ENVS];
                    const formSortRank = (form) => {
                      if (stgReadyMap[form.id]) return 0;
                      const envs = new Set(
                        (form.published || [])
                          .filter((p) => p && p.jsonType === "form")
                          .map((p) => p.env),
                      );
                      const hasStaging = envs.has("Staging");
                      const hasAllEnvs = ALL_ENVS.every((e) => envs.has(e));
                      const hasAnyForms = FORMS_ENVS.some((e) => envs.has(e));
                      if (hasStaging && !hasAllEnvs) return 1;
                      if (hasAllEnvs) return 2;
                      if (hasAnyForms && !hasStaging) return 3;
                      return 4;
                    };
                    const byBucketThenUpdated = (a, b) => {
                      const rankDiff = formSortRank(a) - formSortRank(b);
                      if (rankDiff !== 0) return rankDiff;
                      // Newest updated first.
                      return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                    };

                    // Group by locale
                    const localeGroups = {};
                    visibleForms.forEach((form) => {
                      const locale = form.locale || "—";
                      if (!localeGroups[locale]) localeGroups[locale] = [];
                      localeGroups[locale].push(form);
                    });
                    Object.keys(localeGroups).forEach((loc) => {
                      localeGroups[loc].sort(byBucketThenUpdated);
                    });
                    const sortedLocales = Object.keys(localeGroups).sort();

                    return sortedLocales.map((locale) => {
                      const forms = localeGroups[locale];
                      const isCollapsed = !!localeCollapsed[locale];
                      return (
                        <Box key={locale} sx={{ mb: 1 }}>
                          {/* Locale group header */}
                          <Box
                            onClick={() => toggleLocale(locale)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.75,
                              cursor: "pointer",
                              borderRadius: 1,
                              userSelect: "none",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            {isCollapsed
                              ? <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                              : <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                            }
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 1, color: "text.secondary", textTransform: "uppercase" }}>
                              {locale}
                            </Typography>
                            <Chip label={forms.length} size="small" sx={{ height: 18, fontSize: "0.68rem" }} />
                            {/* Super-admin batch publish: only when the locale has a Ready-for-STG form. */}
                            {isSuperAdmin && forms.some((f) => stgReadyMap[f.id]) && (
                              <>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<ScienceIcon sx={{ fontSize: 18 }} />}
                                  onClick={(e) => { e.stopPropagation(); openPublishAll(locale); }}
                                  sx={{ textTransform: "none", py: 0.1 }}
                                >
                                  Publish to All Buckets
                                </Button>
                              </>
                            )}
                          </Box>
                          {/* Form rows */}
                          <Collapse in={!isCollapsed}>
                            {forms.map((form) => {
                              const isDisabled = activeRole === "annotator" && form.assignedTo !== activeProfileId;
                              const isComplete = !!annotationCompleteMap[form.id];
                              const isJsonComplete = !!jsonCompleteMap[form.id];
                              // Raw / non-PDF "HTML Smart Form": shown with a single
                              // green chip in place of the Annotate + JSON chips.
                              const isRawSmartForm = isRawFormType(form.type);
                              // View-mode forms (the PDF is the whole form, no data-entry
                              // sections) get one chip in place of Annotate + JSON, same as
                              // HTML Smart Form. Mirrored onto the StudioForm metadata on
                              // save from formDetails.settings; shareOnly wins if both are set.
                              const isShareOnly = form.metadata?.shareOnly === true;
                              const isSigOnly = !isShareOnly && form.metadata?.sigOnly === true;
                              // Buckets the latest revision's form template is published to.
                              // `form.published` is the latest revision's publish history.
                              const publishedEnvs = new Set(
                                (form.published || [])
                                  .filter((p) => p && p.jsonType === "form")
                                  .map((p) => p.env),
                              );
                              const publishedTokens = [
                                ["Forms-1", "1"], ["Forms-2", "2"], ["Forms-3", "3"], ["Forms-4", "4"], ["Staging", "S"],
                              ]
                                .filter(([envName]) => publishedEnvs.has(envName))
                                .map(([, token]) => token);
                              const publishedLabel = publishedTokens.join(", ");
                              const publishedHasStaging = publishedTokens.includes("S");
                              const flagNote = formFlagMap[form.id];
                              const isFlagged = !!(flagNote && String(flagNote).trim());
                              const isStgReady = !!stgReadyMap[form.id];
                              const displayName = form.shortName || form.name || "Untitled";
                              const subName = form.formName && form.formName !== form.shortName ? form.formName : null;
                              return (
                                <Box
                                  key={form.id}
                                  onClick={isDisabled ? undefined : () => handleStudioFormClick(form)}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 2,
                                    py: 0.75,
                                    borderBottom: 1,
                                    borderColor: "divider",
                                    ...(isDisabled
                                      ? { opacity: 0.5, cursor: "default" }
                                      : { cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }),
                                  }}
                                >
                                  {/* Status badges + flag */}
                                  {/* Fixed-width badge column: flag + stg-ready icons, then the
                                      annotate/json/published slots. Width must cover the whole
                                      row of slots or it spills over the name column. */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: 265, flexShrink: 0, overflow: "hidden" }}>
                                    <Tooltip
                                      title={
                                        isFlagged ? (
                                          <Box component="span" sx={{ display: "block", maxWidth: 265, whiteSpace: "pre-wrap" }}>
                                            {flagNote}
                                          </Box>
                                        ) : (
                                          "Flag for follow-up"
                                        )
                                      }
                                      slotProps={{
                                        popper: { sx: { "& .MuiTooltip-tooltip": { maxWidth: 320 } } },
                                      }}
                                    >
                                      <MuiIconButton
                                        size="small"
                                        aria-label={isFlagged ? "Unflag form" : "Flag form"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleFlagIconPress(form.id, isFlagged);
                                        }}
                                        sx={{
                                          p: 0.25,
                                          color: isFlagged ? "primary.main" : "action.active",
                                        }}
                                      >
                                        {isFlagged ? (
                                          <FlagIcon sx={{ fontSize: 20 }} />
                                        ) : (
                                          <OutlinedFlagIcon sx={{ fontSize: 20 }} />
                                        )}
                                      </MuiIconButton>
                                    </Tooltip>
                                    <Tooltip title={isStgReady ? "Ready to deploy to STG" : "Not ready to deploy to STG"}>
                                      <MuiIconButton
                                        size="small"
                                        aria-label={isStgReady ? "Clear ready to deploy to STG" : "Mark ready to deploy to STG"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStgReadyToggle(form, isStgReady);
                                        }}
                                        sx={{
                                          p: 0.25,
                                          color: isStgReady ? "success.main" : "action.disabled",
                                        }}
                                      >
                                        {isStgReady ? (
                                          <ScienceIcon sx={{ fontSize: 20 }} />
                                        ) : (
                                          <ScienceOutlinedIcon sx={{ fontSize: 20 }} />
                                        )}
                                      </MuiIconButton>
                                    </Tooltip>
                                    {/* Fixed slots so each badge keeps its column even when absent.
                                        Raw / non-PDF smart forms replace the Annotate + JSON chips
                                        with one light-green "HTML Smart Form" chip. */}
                                    {isRawSmartForm ? (
                                      <Box sx={{ width: 134, flexShrink: 0 }}>
                                        <Chip
                                          label="HTML Smart Form"
                                          size="small"
                                          title="Non-PDF integration form — edited and published as a JSON passthrough"
                                          sx={{ bgcolor: "success.light", color: "common.black", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                        />
                                      </Box>
                                    ) : isSigOnly ? (
                                      <Box sx={{ width: 134, flexShrink: 0 }}>
                                        <Chip
                                          label="Sig Only"
                                          size="small"
                                          title="View / share / sign only — publishes with no form sections"
                                          /* green[200] — a step lighter than the HTML Smart Form chip */
                                          sx={{ bgcolor: "#a5d6a7", color: "common.black", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                        />
                                      </Box>
                                    ) : isShareOnly ? (
                                      <Box sx={{ width: 134, flexShrink: 0 }}>
                                        <Chip
                                          label="Share Only"
                                          size="small"
                                          title="View / share only — publishes with no form sections"
                                          /* green[50] with charcoal text — the lightest of the three */
                                          sx={{ bgcolor: "#e8f5e9", color: "#37474f", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                        />
                                      </Box>
                                    ) : (
                                      <>
                                        <Box sx={{ width: 72, flexShrink: 0 }}>
                                          {isComplete && (
                                            <Chip label="Annotate ✓" size="small" sx={{ bgcolor: "warning.main", color: "warning.contrastText", fontWeight: 600, fontSize: "0.65rem", height: 20, width: 72 }} />
                                          )}
                                        </Box>
                                        <Box sx={{ width: 56, flexShrink: 0 }}>
                                          {isJsonComplete && (
                                            <Chip label="JSON ✓" size="small" sx={{ bgcolor: "success.main", color: "success.contrastText", fontWeight: 600, fontSize: "0.65rem", height: 20, width: 56 }} />
                                          )}
                                        </Box>
                                      </>
                                    )}
                                    {publishedLabel && (
                                      <Chip
                                        label={publishedLabel}
                                        size="small"
                                        title={`Published to ${[...publishedEnvs].join(", ")}`}
                                        sx={{
                                          bgcolor: publishedHasStaging ? "common.black" : "grey.300",
                                          color: publishedHasStaging ? "common.white" : "common.black",
                                          fontWeight: 600,
                                          fontSize: "0.65rem",
                                          height: 20,
                                        }}
                                      />
                                    )}
                                  </Box>
                                  {/* Two stacked rows: name/version/revs above, the
                                      updated stamp + description below. The old fixed-width
                                      "Updated" column was dropped so the name gets its space. */}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, minWidth: 0 }}>
                                      {/* Name (tooltip shows the full name when truncated) */}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Tooltip title={subName ? `${displayName} ${subName}` : displayName}>
                                          <Typography variant="body2" noWrap>
                                            <Box component="span" sx={{ fontWeight: 600 }}>{displayName}</Box>
                                            {subName && (
                                              <Box component="span" sx={{ color: "text.primary", ml: 1 }}>{subName}</Box>
                                            )}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                      {/* Version */}
                                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, minWidth: 48, textAlign: "right" }}>
                                        {form.version}
                                      </Typography>
                                      {/* Revs */}
                                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, width: 44, textAlign: "right" }}>
                                        {form.revisions} rev{form.revisions !== 1 ? "s" : ""}
                                      </Typography>
                                    </Box>
                                    {/* Updated stamp + who + description. The stamp never shrinks;
                                        the description takes the remaining width and ellipses. */}
                                    <Box sx={{ display: "flex", alignItems: "baseline", minWidth: 0 }}>
                                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        Updated {formatUpdatedStamp(form.updatedAt)} by {form.updatedByUserProfile?.displayName || "—"}
                                        {form.description ? "," : ""}
                                      </Typography>
                                      {form.description && (
                                        <Tooltip title={form.description}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                            sx={{ fontStyle: "italic", ml: 0.5, minWidth: 0 }}
                                          >
                                            {form.description}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Box>
                                  {/* Assignee chip */}
                                  <Box sx={{ flexShrink: 0, width: 125 }}>
                                    {form.assignedTo ? (
                                      <Chip
                                        size="small"
                                        label={form.assignedToUserProfile?.displayName || "Assigned"}
                                        onDelete={activeRole === "admin" ? (e) => handleInlineUnassign(e, form) : undefined}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ maxWidth: 125 }}
                                      />
                                    ) : activeRole === "admin" ? (
                                      <Chip
                                        size="small"
                                        label="+ Assign"
                                        variant="outlined"
                                        onClick={(e) => handleInlineAssignClick(e, form)}
                                        sx={{ opacity: 0.5, "&:hover": { opacity: 1 }, maxWidth: 125 }}
                                      />
                                    ) : null}
                                  </Box>
                                  {/* 3-dot menu */}
                                  {(activeRole === "admin" || activeRole === "json") && (
                                    <MuiIconButton size="small" onClick={(e) => handleAssignMenuOpen(e, form)}>
                                      <MoreVertIcon fontSize="small" />
                                    </MuiIconButton>
                                  )}
                                </Box>
                              );
                            })}
                          </Collapse>
                        </Box>
                      );
                    });
                  })()}
                  <Menu
                    anchorEl={assignMenuAnchor}
                    open={Boolean(assignMenuAnchor)}
                    onClose={handleAssignMenuClose}
                  >
                    {assignMenuForm && (
                      <MenuItem onClick={handleToggleAnnotationComplete}>
                        {annotationCompleteMap[assignMenuForm.id] ? "Mark Annotate Incomplete" : "Mark Annotate Complete"}
                      </MenuItem>
                    )}
                    {(activeRole === "admin" || activeRole === "json") && assignMenuForm && (
                      <MenuItem onClick={handleToggleJsonComplete}>
                        {jsonCompleteMap[assignMenuForm.id] ? "Mark JSON Incomplete" : "Mark JSON Complete"}
                      </MenuItem>
                    )}
                    {isSuperAdmin && <MenuItem onClick={handleViewFormTracking}>View Tracking</MenuItem>}
                    <MenuItem onClick={handleDeleteFormClick} sx={{ color: "error.main" }}>Delete Form</MenuItem>
                  </Menu>
                </Box>
              )}

              {/* View: Revisions */}
              {formsBrowserView === "revisions" && (
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  {studioFormDatasLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : studioFormDatas.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: "center", py: 8 }}>
                      No revisions found
                    </Typography>
                  ) : (
                    studioFormDatas.map((formData) => (
                      <Box
                        key={formData.id}
                        onClick={() => handleStudioFormDataClick(formData)}
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          bgcolor: formData.id === formMeta.id ? (t) => `${t.palette.primary.main}30` : undefined,
                          "&:hover": { bgcolor: formData.id === formMeta.id ? (t) => `${t.palette.primary.main}40` : "action.hover" },
                          cursor: "pointer",
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">Name</Typography>
                            <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{formData.name}</Typography>
                          </Box>
                          <Box sx={{ flex: 2, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">Description</Typography>
                            {formData.description && formData.description.length > 245 ? (
                              <Tooltip title={formData.description} arrow>
                                <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {formData.description.slice(0, 245)}...
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{formData.description}</Typography>
                            )}
                          </Box>
                          <Box sx={{ width: 60 }}>
                            <Typography variant="caption" color="text.secondary">Locale</Typography>
                            <Typography variant="body2">{formData.formDetails?.locale}</Typography>
                          </Box>
                          <Box sx={{ width: 120 }}>
                            <Typography variant="caption" color="text.secondary">Type</Typography>
                            <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{formData.formDetails?.type}</Typography>
                          </Box>
                          <Box sx={{ width: 200 }}>
                            <Typography variant="caption" color="text.secondary">Form Id</Typography>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              {formData.formDetails?.formId ? (<>{formData.formDetails.formId.slice(0, 24)}<br />{formData.formDetails.formId.slice(24)}</>) : ""}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">Form Name</Typography>
                            <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{formData.formDetails?.name}</Typography>
                          </Box>
                          <Box sx={{ width: 120 }}>
                            <Typography variant="caption" color="text.secondary">Short Name</Typography>
                            <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{formData.formDetails?.shortName}</Typography>
                          </Box>
                          {canDeleteFormData() && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <MuiIconButton size="small" onClick={(e) => handleFormDataMenuOpen(e, formData)}>
                                <MoreVertIcon fontSize="small" />
                              </MuiIconButton>
                            </Box>
                          )}
                        </Box>
                        {/* Footnote */}
                        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Created At: {formatDate(formData.createdAt)}</Typography>
                          <Typography variant="caption" color="text.secondary">Created By: {formData.createdByUserProfile?.displayName || ""}</Typography>
                          <Typography variant="caption" color="text.secondary">Updated At: {formatDate(formData.updatedAt)}</Typography>
                          <Typography variant="caption" color="text.secondary">Updated By: {formData.updatedByUserProfile?.displayName || ""}</Typography>
                          <Typography variant="caption" color="text.secondary">Instance Version: {formData.instanceVersion}</Typography>
                          <Typography variant="caption" color="text.secondary">Version: {formData.formDetails?.version || "—"}</Typography>
                        </Box>
                        {/* Publish history: link opens the full history modal */}
                        {formData.published?.length > 0 && (
                          <Box sx={{ display: "flex", flexDirection: "column", mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ cursor: "pointer", textDecoration: "underline", "&:hover": { color: "primary.main" } }}
                              onClick={(e) => { e.stopPropagation(); setPublishedDialogData(formData.published); }}
                            >
                              Publish History
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))
                  )}
                  <Menu
                    anchorEl={formDataMenuAnchor}
                    open={Boolean(formDataMenuAnchor)}
                    onClose={handleFormDataMenuClose}
                  >
                    {/* Publish moved to the loaded-form toolbar (it serializes live editor state). */}
                    {isSuperAdmin && <MenuItem onClick={handleViewFormDataTracking}>View Tracking</MenuItem>}
                    <MenuItem onClick={handleDeleteFormDataClick} sx={{ color: "error.main" }}>Delete</MenuItem>
                  </Menu>
                </Box>
              )}

              {/* View: Detail */}
              {formsBrowserView === "detail" && selectedStudioFormData && (
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  <Box sx={{ bgcolor: "#1e1e1e", borderRadius: 2, p: 2, overflow: "auto", flex: 1, "& *": { fontWeight: "normal !important" } }}>
                    <JsonView data={selectedStudioFormData} style={jsonViewerStyles} />
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        <Container
          maxWidth={pdfFile ? false : "xl"}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            pb: 4, // Restore bottom padding inside the container
          }}
        >
          {showOptionAnnotationPrompt && optionAnnotationState?.items?.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                py: 1.5,
                px: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderBottom: 1,
                borderColor: "primary.dark",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Draw a box on the PDF for: {optionAnnotationState.items[0].label}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  ({optionAnnotationState.items.length} remaining)
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setOptionAnnotationState(null);
                  setShowOptionAnnotationPrompt(false);
                }}
                sx={{
                  color: "inherit",
                  borderColor: "currentColor",
                  "&:hover": { borderColor: "currentColor", bgcolor: "rgba(255,255,255,0.15)" },
                }}
              >
                Stop
              </Button>
            </Box>
          )}
          {!isFormLoaded ? (
            <DashboardPage
              studioForms={studioForms}
              studioFormsLoading={studioFormsLoading}
              annotationCompleteMap={annotationCompleteMap}
              jsonCompleteMap={jsonCompleteMap}
              activeProfileId={getActiveProfile()?.id}
              activeRole={activeRole}
              onOpenForm={handleDashboardLoadForm}
              onPdfUpload={handlePdfUpload}
              onFormImport={handleFormImport}
            />
          ) : studioMode === "annotator" ? (
            showAnalystForAnnotator ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                {pdfFile && analystPdfBuffer && analystTemplate ? (
                  <PdfV2AnalystElement
                    ref={analystElementRef}
                    pdfBuffer={analystPdfBuffer}
                    formTemplate={analystTemplate}
                    analystRole="annotator"
                    onSaveTemplateRequested={handleAnalystSaveTemplate}
                  />
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">
                      {!pdfFile
                        ? "Load a PDF to use the analyst panel."
                        : "Preparing analyst panel…"}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
            <Box
              sx={{
                animation: isFormLoaded ? "slideInUp 0.8s ease-out" : "none",
                opacity: isFormLoaded ? 1 : 0,
                transform: isFormLoaded ? "translateY(0)" : "translateY(50px)",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {pdfFile && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: { xs: "600px", lg: "100%" },
                    display: "flex",
                    flexDirection: "column",
                    animation: "fadeIn 0.6s ease-out 0.1s both",
                  }}
                >
                  {pdfFile ? (
                    <InteractivePdfViewer
                      pdfFile={pdfFile}
                      onClose={() => setPdfFile(null)}
                      onReplacePdf={() => pdfReplaceInputRef.current?.click()}
                      onPdfClick={handlePdfClick}
                      onInstantCreate={handleInstantCreate}
                      fields={fields}
                      signatureAnnotations={signatureAnnotations}
                      initialsAnnotations={initialsAnnotations}
                      additionalFields={additionalFields}
                      signatures={signatures}
                      signerAttributeBindings={signerAttributeBindings}
                      initialerAttributeBindings={initialerAttributeBindings}
                      subsequentSigningBindings={subsequentSigningAttributeBindings}
                      subsequentSigning={subsequentSigning}
                      onSignerBindingLocationChange={handleSignerBindingLocationChange}
                      onDeleteSignerBinding={handleDeleteSignerBinding}
                      onSubsequentSigningLocationChange={handleSubsequentSigningLocationChange}
                      onDeleteSubsequentSigningBinding={handleDeleteSubsequentSigningBinding}
                      onEditField={(field) => {
                        setCreateFieldDialogOpen(false);
                        setAnnotatorEditingField({
                          ...field,
                          rules: getFormRulesField(field.attributeName).rules,
                          offerCompare: isFieldInOfferCompare(field.attributeName),
                        });
                      }}
                      onFieldLocationChange={handleFieldLocationChange}
                      onOptionLocationChange={handleOptionLocationChange}
                      onSignatureLocationChange={handleSignatureLocationChange}
                      onDeleteSignature={handleSignatureDelete}
                      onAdditionalFieldLocationChange={handleAdditionalFieldLocationChange}
                      onDeleteAdditionalField={handleDeleteAdditionalField}
                      onAddAdditionalAnnotation={
                        activeRole === "annotator" ? undefined : handleAddAdditionalAnnotation
                      }
                      onAddSignerBindingAnnotation={
                        activeRole === "annotator" ? undefined : handleAddSignerBindingAnnotation
                      }
                      onSignatureSelect={setSelectedSignatureId}
                      defaultLineHeightPx={defaultLineHeightPx}
                      lineHeightSettingMode={lineHeightSettingMode}
                      lineHeightCaptureBox={lineHeightCaptureBox}
                      onLineHeightSet={handleLineHeightSet}
                      onLineHeightConfirm={handleLineHeightConfirm}
                      onLineHeightConfirmError={handleLineHeightConfirmError}
                      onLineHeightCancel={handleLineHeightCancel}
                      onLineHeightReset={handleLineHeightReset}
                      onLineHeightCaptureChange={setLineHeightCaptureBox}
                      preserveDrawnBoxSize={!!optionAnnotationState}
                      identifySectionsMode={identifySectionsMode}
                      signaturePlacementMode={signaturePlacementMode}
                      initialsPlacementMode={initialsPlacementMode}
                      onToggleSignaturePlacement={enterSignaturePlacementMode}
                      onToggleInitialsPlacement={enterInitialsPlacementMode}
                      onToggleIdentifySections={(nextValue) => {
                        const next = !!nextValue;
                        if (!next) {
                          closeIdentifySectionsPanel();
                          return;
                        }
                        setIdentifySectionsMode(true);
                        setSignaturePlacementMode(false);
                        setInitialsPlacementMode(false);
                      }}
                      onSelectFields={handleIdentifySectionSelectFields}
                      onAssignFieldToSection={
                        handleAssignFieldToIdentifySection
                      }
                      selectedFieldIds={
                        identifySectionsMode
                          ? selectedSectionFieldIds
                          : pdfSelectedFieldIds
                      }
                      primarySelectId={pdfPrimarySelectId}
                      onFieldSelect={handlePdfFieldSelect}
                      mode="annotator"
                      annotatorMarkComplete={
                        formMeta.studioFormId
                          ? {
                              visible: true,
                              isComplete: !!annotationCompleteMap[formMeta.studioFormId],
                              onToggle: handleAnnotatorMarkComplete,
                            }
                          : null
                      }
                      studioFormFlag={
                        formMeta.studioFormId
                          ? {
                              visible: true,
                              note: formFlagMap[formMeta.studioFormId] || "",
                              isFlagged: !!(formFlagMap[formMeta.studioFormId]?.trim()),
                              onFlagIconClick: () => {
                                const id = formMeta.studioFormId;
                                handleFlagIconPress(id, !!(formFlagMap[id]?.trim()));
                              },
                            }
                          : null
                      }
                    />
                  ) : (
                    <Paper
                      elevation={24}
                      sx={{
                        p: 3,
                        height: "100%",
                        background:
                          "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                        backdropFilter: "blur(4px)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          minHeight: 320,
                          border: "1px dashed",
                          borderColor: "divider",
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                          bgcolor: "background.paper",
                        }}
                      >
                        <Typography variant="body2">
                          Upload a PDF to start annotating
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Box>
                )}
                {pdfFile && (
                <Box
                  onPointerDown={(e) => {
                    e.preventDefault();
                    splitDragRef.current = { startX: e.clientX, startPercent: annotatorSplitPercent, moved: false };
                    e.currentTarget.setPointerCapture(e.pointerId);
                  }}
                  onPointerMove={(e) => {
                    if (!splitDragRef.current) return;
                    const delta = e.clientX - splitDragRef.current.startX;
                    if (Math.abs(delta) > 4) splitDragRef.current.moved = true;
                    if (!annotatorPanelCollapsed && splitDragRef.current.moved) {
                      const container = e.currentTarget.parentElement;
                      const containerWidth = container.getBoundingClientRect().width;
                      const deltaPercent = (delta / containerWidth) * 100;
                      const next = Math.min(Math.max(splitDragRef.current.startPercent + deltaPercent, 20), 80);
                      setAnnotatorSplitPercent(next);
                    }
                  }}
                  onPointerUp={() => {
                    if (splitDragRef.current) {
                      if (!splitDragRef.current.moved) {
                        const next = !annotatorPanelCollapsed;
                        setAnnotatorPanelCollapsed(next);
                        localStorage.setItem("formStudioAnnotatorCollapsed", String(next));
                      } else if (!annotatorPanelCollapsed) {
                        localStorage.setItem("formStudioAnnotatorSplit", String(annotatorSplitPercent));
                      }
                      splitDragRef.current = null;
                    }
                  }}
                  sx={{
                    width: "20px",
                    cursor: annotatorPanelCollapsed ? "pointer" : "ew-resize",
                    flexShrink: 0,
                    display: { xs: "none", lg: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    transition: "background-color 0.2s",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  {annotatorPanelCollapsed
                    ? <ChevronLeftIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    : <ChevronRightIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  }
                </Box>
                )}
                <Box
                  sx={{
                    width: { xs: "100%", lg: pdfFile ? `${100 - annotatorSplitPercent}%` : "100%" },
                    minWidth: { lg: annotatorPanelCollapsed ? 0 : 320 },
                    height: "100%",
                    overflow: "hidden",
                    display: annotatorPanelCollapsed ? "none" : "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        overflow: "auto",
                      }}
                    >
                      {lineHeightSettingMode && (
                        <Paper
                          elevation={24}
                          sx={{
                            p: 3,
                            mb: 2,
                            background:
                              "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                            backdropFilter: "blur(4px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Set line height
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Resize the box on the PDF to match one line of text, then click <strong>Confirm</strong> in the PDF header above.
                          </Typography>
                        </Paper>
                      )}
                      {signaturePlacementMode && (
                        <Paper
                          elevation={24}
                          sx={{
                            height: "100%",
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            background:
                              "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                            backdropFilter: "blur(4px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderBottom: "none",
                          }}
                        >
                          <Typography variant="subtitle1">
                            Add Signature
                          </Typography>
                          <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select
                              label="Role"
                              value={signatureRoleKey}
                              onChange={(event) =>
                                setSignatureRoleKey(event.target.value)
                              }
                            >
                              {signatureRoleOptions.length > 0 ? (
                                signatureRoleOptions.map((roleKey) => (
                                  <MenuItem key={roleKey} value={roleKey}>
                                    {signatureRoleLabels[roleKey] || roleKey}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value="" disabled>
                                  Add roles in Form Settings first
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                          <FormControl fullWidth size="small">
                            <InputLabel>Signer</InputLabel>
                            <Select
                              label="Signer"
                              value={signatureSignerKey}
                              onChange={(event) =>
                                setSignatureSignerKey(event.target.value)
                              }
                              disabled={!signatureRoleKey}
                            >
                              {signatureSignerOptions.length > 0 ? (
                                signatureSignerOptions.map((signerKey) => (
                                  <MenuItem key={signerKey} value={signerKey}>
                                    {signerKey}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value="" disabled>
                                  No available signers
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary">
                            Add roles and signers in Form Settings first. Then
                            drag on the PDF to place. Select a signature to
                            nudge (bottom toolbar or arrow keys) and resize
                            (bottom toolbar or Alt+arrows).
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                              mt: "auto",
                            }}
                          >
                            {selectedSignatureId && (
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  handleSignatureDelete(selectedSignatureId)
                                }
                              >
                                Delete
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              onClick={exitSignaturePlacementMode}
                            >
                              Done
                            </Button>
                          </Box>
                        </Paper>
                      )}
                      {initialsPlacementMode && (
                        <Paper
                          elevation={24}
                          sx={{
                            height: "100%",
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            background:
                              "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                            backdropFilter: "blur(4px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderBottom: "none",
                          }}
                        >
                          <Typography variant="subtitle1">Add Initials</Typography>
                          <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select
                              label="Role"
                              value={initialsRoleKey}
                              onChange={(event) => setInitialsRoleKey(event.target.value)}
                            >
                              {signatureRoleOptions.length > 0 ? (
                                signatureRoleOptions.map((roleKey) => (
                                  <MenuItem key={roleKey} value={roleKey}>
                                    {signatureRoleLabels[roleKey] || roleKey}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value="" disabled>
                                  Add roles in Form Settings first
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                          <FormControl fullWidth size="small">
                            <InputLabel>Initials Signer</InputLabel>
                            <Select
                              label="Initials Signer"
                              value={initialsSignerKey}
                              onChange={(event) => setInitialsSignerKey(event.target.value)}
                              disabled={!initialsRoleKey}
                            >
                              {initialsSignerOptions.length > 0 ? (
                                initialsSignerOptions.map((signerKey) => (
                                  <MenuItem key={signerKey} value={signerKey}>
                                    {signerKey}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value="" disabled>
                                  No available signers
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary">
                            Drag on the PDF to place an initials box (published as initialsJSON, same shape as
                            signaturesJSON). Select a box to nudge or resize like signatures.
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                              mt: "auto",
                            }}
                          >
                            {selectedSignatureId && (
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleSignatureDelete(selectedSignatureId)}
                              >
                                Delete
                              </Button>
                            )}
                            <Button variant="outlined" onClick={exitInitialsPlacementMode}>
                              Done
                            </Button>
                          </Box>
                        </Paper>
                      )}
                      {!signaturePlacementMode &&
                      !initialsPlacementMode &&
                      identifySectionsMode && (
                        <Paper
                          elevation={24}
                          sx={{
                            height: "100%",
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            background:
                              "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                            backdropFilter: "blur(4px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderBottom: "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              flexShrink: 0,
                            }}
                          >
                            <Typography variant="subtitle1">Identify Sections</Typography>
                            <MuiIconButton
                              size="small"
                              onClick={closeIdentifySectionsPanel}
                              aria-label="Close identify sections"
                            >
                              <CloseIcon fontSize="small" />
                            </MuiIconButton>
                          </Box>
                          <Paper
                            elevation={0}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                              bgcolor: "background.paper",
                              px: 2,
                              py: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              {sections.map((section) => (
                                <Button
                                  key={section.id}
                                  onClick={() => {
                                    setIdentifySectionActiveId(section.id);
                                    setIdentifySectionName(section.title || "");
                                    setIdentifySectionDescription(
                                      section.description || "",
                                    );
                                    setIdentifySectionTitleShort(section.titleShort || "");
                                    setIdentifySectionInfo(section.info || "");
                                    setIdentifySectionMetadata(
                                      typeof section.metadata === "object" && section.metadata !== null
                                        ? JSON.stringify(section.metadata, null, 2)
                                        : "{}",
                                    );
                                    setSelectedSectionFieldIds(
                                      fields
                                        .filter(
                                          (field) =>
                                            field.sectionId === section.id,
                                        )
                                        .map((field) => field.id),
                                    );
                                  }}
                                  variant={
                                    section.id === identifySectionActiveId
                                      ? "contained"
                                      : "outlined"
                                  }
                                  size="small"
                                  sx={{ textTransform: "none" }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <span>{section.title || "Untitled"}</span>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        bgcolor:
                                          section.id === identifySectionActiveId
                                            ? "primary.main"
                                            : "action.selected",
                                        color:
                                          section.id === identifySectionActiveId
                                            ? "white"
                                            : "text.secondary",
                                        px: 0.8,
                                        py: 0.2,
                                        borderRadius: 1,
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {
                                        fields.filter(
                                          (field) =>
                                            field.sectionId === section.id,
                                        ).length
                                      }
                                    </Typography>
                                    <MuiIconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleIdentifyDeleteSection(section.id);
                                      }}
                                      sx={{ color: "inherit" }}
                                      aria-label={`Delete ${section.title || "section"}`}
                                    >
                                      <DeleteOutlineIcon fontSize="inherit" />
                                    </MuiIconButton>
                                  </Box>
                                </Button>
                              ))}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleIdentifyAddSection}
                                sx={{ textTransform: "none" }}
                              >
                                + New Section
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleIdentifyAddDecisionSection}
                                sx={{ textTransform: "none" }}
                              >
                                + New Decision Section
                              </Button>
                            </Box>
                          </Paper>
                          <TextField
                            label="Section Number"
                            value={identifySectionNumber}
                            onChange={(e) =>
                              setIdentifySectionNumber(e.target.value)
                            }
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Section Name"
                            value={identifySectionName}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setIdentifySectionName(nextValue);
                              if (!identifySectionActiveId) return;
                              setFormJsonDirty(true);
                              setSections((prev) =>
                                prev.map((section) =>
                                  section.id === identifySectionActiveId
                                    ? { ...section, title: nextValue }
                                    : section,
                                ),
                              );
                            }}
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Section Description"
                            value={identifySectionDescription}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setIdentifySectionDescription(nextValue);
                              if (!identifySectionActiveId) return;
                              setFormJsonDirty(true);
                              setSections((prev) =>
                                prev.map((section) =>
                                  section.id === identifySectionActiveId
                                    ? { ...section, description: nextValue }
                                    : section,
                                ),
                              );
                            }}
                            multiline
                            minRows={2}
                            fullWidth
                          />
                          <TextField
                            label="Section Short Title"
                            value={identifySectionTitleShort}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setIdentifySectionTitleShort(nextValue);
                              if (!identifySectionActiveId) return;
                              setFormJsonDirty(true);
                              setSections((prev) =>
                                prev.map((section) =>
                                  section.id === identifySectionActiveId
                                    ? { ...section, titleShort: nextValue || null }
                                    : section,
                                ),
                              );
                            }}
                            size="small"
                            fullWidth
                            helperText="Abbreviated section title"
                          />
                          <TextField
                            label="Section help (info)"
                            placeholder="HTML string for section help"
                            value={identifySectionInfo}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setIdentifySectionInfo(nextValue);
                              if (!identifySectionActiveId) return;
                              setFormJsonDirty(true);
                              setSections((prev) =>
                                prev.map((section) =>
                                  section.id === identifySectionActiveId
                                    ? { ...section, info: nextValue || null }
                                    : section,
                                ),
                              );
                            }}
                            multiline
                            minRows={2}
                            fullWidth
                            helperText="Section help text (HTML string)"
                          />
                          {identifySectionActiveId && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Section type (read-only)
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                {sections.find((s) => s.id === identifySectionActiveId)?.type ?? "data"}
                              </Typography>
                              {sections.find((s) => s.id === identifySectionActiveId)?.type === "decision" && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Navigation is set per option in the decision/decisionWithOther field (Go To Section), not here.</Typography>
                              )}
                            </Box>
                          )}
                          <TextField
                            label="Section metadata (JSON)"
                            placeholder='e.g. {"meviDealRoleRules": {"write": [], "read": []}}'
                            value={identifySectionMetadata}
                            onChange={(e) => setIdentifySectionMetadata(e.target.value)}
                            onBlur={() => {
                              if (!identifySectionActiveId) return;
                              try {
                                const parsed = JSON.parse(identifySectionMetadata || "{}");
                                setFormJsonDirty(true);
                                setSections((prev) =>
                                  prev.map((section) =>
                                    section.id === identifySectionActiveId
                                      ? { ...section, metadata: parsed }
                                      : section,
                                  ),
                                );
                              } catch (_) {
                                // Invalid JSON; keep previous section.metadata
                              }
                            }}
                            multiline
                            minRows={3}
                            fullWidth
                            helperText="Section metadata object (e.g. meviDealRoleRules write/read arrays). Saved on blur."
                            sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.875rem" } }}
                          />
                          <TextField
                            label="Section Notes"
                            placeholder="Info entered here will help to drive the MTI model"
                            value={identifySectionNotes}
                            onChange={(e) =>
                              setIdentifySectionNotes(e.target.value)
                            }
                            multiline
                            minRows={2}
                            fullWidth
                          />
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              border: "1px dashed",
                              borderColor: "divider",
                              borderRadius: 2,
                              bgcolor: "background.paper",
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Fields in this section (
                              {
                                fields.filter(
                                  (field) =>
                                    field.sectionId === identifySectionActiveId,
                                ).length
                              }
                              )
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {fields
                                .filter(
                                  (field) =>
                                    field.sectionId === identifySectionActiveId,
                                )
                                .map((field) => (
                                  <Box
                                    key={field.id}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      bgcolor: "action.selected",
                                      color: "text.secondary",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    <span>
                                      {field.label ||
                                        field.attributeName ||
                                        field.id}
                                    </span>
                                    <MuiIconButton
                                      size="small"
                                      onClick={() => {
                                        setFormJsonDirty(true);
                                        setFields((prev) =>
                                          prev.map((item) =>
                                            item.id === field.id
                                              ? { ...item, sectionId: null }
                                              : item,
                                          ),
                                        );
                                        setSelectedSectionFieldIds((prev) =>
                                          prev.filter((id) => id !== field.id),
                                        );
                                      }}
                                      sx={{ color: "text.secondary" }}
                                      aria-label={`Remove ${field.label || field.attributeName || "field"}`}
                                    >
                                      <DeleteOutlineIcon fontSize="inherit" />
                                    </MuiIconButton>
                                  </Box>
                                ))}
                              {fields.filter(
                                (field) =>
                                  field.sectionId === identifySectionActiveId,
                              ).length === 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  No fields assigned yet.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                              mt: "auto",
                            }}
                          >
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setIdentifySectionNumber("");
                                setIdentifySectionName("");
                                setIdentifySectionDescription("");
                                setIdentifySectionNotes("");
                                setIdentifySectionTitleShort("");
                                setIdentifySectionInfo("");
                                setIdentifySectionMetadata("{}");
                                setSelectedSectionFieldIds([]);
                              }}
                            >
                              Clear
                            </Button>
                            <Button
                              variant="contained"
                              disabled={selectedSectionFieldIds.length === 0}
                              onClick={() => {
                                if (selectedSectionFieldIds.length === 0)
                                  return;
                                setFormJsonDirty(true);
                                setFields((prevFields) => {
                                  const targetSectionId =
                                    identifySectionActiveId || null;
                                  const targetFields = targetSectionId
                                    ? prevFields.filter(
                                        (field) =>
                                          field.sectionId === targetSectionId,
                                      )
                                    : [];
                                  let nextOrder =
                                    targetFields.reduce(
                                      (max, field) =>
                                        Math.max(max, field.order || 0),
                                      0,
                                    ) + 1;

                                  return prevFields.map((field) => {
                                    if (
                                      !selectedSectionFieldIds.includes(
                                        field.id,
                                      )
                                    ) {
                                      return field;
                                    }

                                    const shouldMove =
                                      targetSectionId &&
                                      field.sectionId !== targetSectionId;
                                    if (field.attributeName) {
                                      updateFormRulesField(field.attributeName, {
                                        rules: {
                                          sectionNumber: identifySectionNumber,
                                          sectionName: identifySectionName,
                                          sectionDescription: identifySectionDescription,
                                          sectionNotes: identifySectionNotes,
                                        },
                                      });
                                    }
                                    const nextField = {
                                      ...field,
                                      touchedByAnnotator: true,
                                    };

                                    if (shouldMove) {
                                      nextField.sectionId = targetSectionId;
                                      nextField.order = nextOrder;
                                      nextOrder += 1;
                                      if (!nextField.label && nextField.attributeName) {
                                        nextField.label = (nextField.attributeName || "")
                                          .replace(/([A-Z])/g, " $1")
                                          .trim()
                                          .replace(/^./, (s) => s.toUpperCase());
                                      }
                                    }

                                    return nextField;
                                  });
                                });
                                setSelectedSectionFieldIds([]);
                              }}
                            >
                              Apply to Selected (
                              {selectedSectionFieldIds.length})
                            </Button>
                          </Box>
                        </Paper>
                      )}
                      {!signaturePlacementMode &&
                      !initialsPlacementMode &&
                      !identifySectionsMode &&
                      createFieldDialogOpen ? (
                        <CreateFieldDialog
                          open={createFieldDialogOpen}
                          onClose={() => {
                            setCreateFieldDialogOpen(false);
                            setAnnotatorEditingField(null);
                            setPendingFieldType(null);
                            setPendingPdfLocation(null);
                            setCreateFieldDialogDefaultAssignTab(false);
                          }}
                          onCreate={handleCreateField}
                          onAssignExisting={
                            activeRole === "annotator"
                              ? undefined
                              : handleAssignPdfLocationToExistingField
                          }
                          defaultToAssignTab={createFieldDialogDefaultAssignTab}
                          sections={sections}
                          fields={fields}
                          mode={studioMode}
                          initialData={
                            pendingPdfLocation
                              ? {
                                  metadata: { pdfLocation: pendingPdfLocation },
                                  ...(pendingFieldType ? { type: pendingFieldType } : {}),
                                }
                              : null
                          }
                          variant="panel"
                          isPurchaseContract={isPurchaseContract}
                        />
                      ) : !signaturePlacementMode &&
                        !initialsPlacementMode &&
                        !identifySectionsMode &&
                        annotatorEditingField ? (
                        <EditFieldDialog
                          key={annotatorEditingField?.id}
                          open={!!annotatorEditingField}
                          field={annotatorEditingField}
                          daysAfterSettings={daysAfterSettings}
                          sections={sections}
                          fields={fields}
                          signatures={signatures}
                          additionalFields={additionalFields}
                          signerAttributeBindings={signerAttributeBindings}
                          initialerAttributeBindings={initialerAttributeBindings}
                          subsequentSigningBindings={subsequentSigningAttributeBindings}
                          subsequentSigning={subsequentSigning}
                        signatureAnnotations={signatureAnnotations}
                          initialsAnnotations={initialsAnnotations}
                          initials={initials}
                          onFieldPdfBindingSync={applyFieldPdfBindingFromEditor}
                          onClose={() => {
                            setAnnotatorEditingField(null);
                            setCreateFieldDialogOpen(false);
                          }}
                          onSave={handleAnnotatorSaveField}
                          onDelete={(fieldId) =>
                            handleAnnotatorDeleteField(fieldId)
                          }
                          mode="annotator"
                          variant="panel"
                          isPurchaseContract={isPurchaseContract}
                        />
                      ) : !signaturePlacementMode && !initialsPlacementMode && !identifySectionsMode ? (
                        <Paper
                          elevation={24}
                          sx={{
                            height: "100%",
                            p: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            background:
                              "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                            backdropFilter: "blur(4px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <Typography variant="body2">
                            Select a field on the PDF to edit its settings
                          </Typography>
                        </Paper>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            )
          ) : (
            <Box
              sx={{
                animation: isFormLoaded ? "slideInUp 0.8s ease-out" : "none",
                opacity: isFormLoaded ? 1 : 0,
                transform: isFormLoaded ? "translateY(0)" : "translateY(50px)",
                flex: 1, // Fill remaining space
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  height: "100%", // Ensure flex container takes full height
                }}
              >
                {/* PDF Viewer Column - only when a PDF is loaded so content can use full viewport when missing.
                    Hidden in Mevi Preview tab (activeTab === 4) — that tab embeds its own PDF viewer (the
                    pdf-v2-analyst-element) and needs the full panel width. */}
                {pdfFile && activeTab !== 4 && (
                  <Box
                    sx={{
                      width: { xs: "100%", lg: `${splitPercent}%` },
                      height: { xs: "600px", lg: "100%" },
                      animation: "fadeIn 0.6s ease-out 0.1s both",
                    }}
                  >
                    {pdfFile ? (
                      <InteractivePdfViewer
                        pdfFile={pdfFile}
                        onClose={() => setPdfFile(null)}
                        onReplacePdf={() => pdfReplaceInputRef.current?.click()}
                        onPdfClick={handlePdfClick}
                        fields={fields}
                        signatureAnnotations={signatureAnnotations}
                        initialsAnnotations={initialsAnnotations}
                        additionalFields={additionalFields}
                        signatures={signatures}
                        signerAttributeBindings={signerAttributeBindings}
                        initialerAttributeBindings={initialerAttributeBindings}
                        subsequentSigningBindings={subsequentSigningAttributeBindings}
                        subsequentSigning={subsequentSigning}
                        onSignerBindingLocationChange={handleSignerBindingLocationChange}
                        onDeleteSignerBinding={handleDeleteSignerBinding}
                        onSubsequentSigningLocationChange={handleSubsequentSigningLocationChange}
                        onDeleteSubsequentSigningBinding={handleDeleteSubsequentSigningBinding}
                        onAddSignerBindingAnnotation={handleAddSignerBindingAnnotation}
                        onAddSubsequentSigningAnnotation={handleAddSubsequentSigningAnnotation}
                        onEditField={setEditingField}
                        onFieldLocationChange={handleFieldLocationChange}
                        onOptionLocationChange={handleOptionLocationChange}
                        onSignatureLocationChange={
                          handleSignatureLocationChange
                        }
                        onDeleteSignature={handleSignatureDelete}
                        onAdditionalFieldLocationChange={handleAdditionalFieldLocationChange}
                        onDeleteAdditionalField={handleDeleteAdditionalField}
                        onAddAdditionalAnnotation={handleAddAdditionalAnnotation}
                        onSignatureSelect={(sigId) => {
                          // Route through the common selection handler so initials/signature
                          // annotations behave the same as other PDF-extra bindings.
                          handlePdfFieldSelect(sigId || null);
                        }}
                        primarySelectId={pdfPrimarySelectId}
                        onFieldSelect={handlePdfFieldSelect}
                        scrollToFieldId={scrollToFieldId}
                        onScrollToFieldDone={() => setScrollToFieldId(null)}
                        identifySectionsMode={identifySectionsMode}
                        onToggleIdentifySections={(nextValue) => {
                          const next = !!nextValue;
                          if (!next) {
                            closeIdentifySectionsPanel();
                            return;
                          }
                          setIdentifySectionsMode(true);
                          setSignaturePlacementMode(false);
                          setInitialsPlacementMode(false);
                        }}
                        onSelectFields={identifySectionsMode ? handleIdentifySectionSelectFields : undefined}
                        selectedFieldIds={
                          identifySectionsMode
                            ? selectedSectionFieldIds
                            : pdfSelectedFieldIds
                        }
                        signaturePlacementMode={signaturePlacementMode}
                        initialsPlacementMode={initialsPlacementMode}
                        onToggleSignaturePlacement={enterSignaturePlacementMode}
                        onToggleInitialsPlacement={enterInitialsPlacementMode}
                        defaultLineHeightPx={defaultLineHeightPx}
                        preserveDrawnBoxSize={!!optionAnnotationState}
                        lineHeightSettingMode={lineHeightSettingMode}
                        lineHeightCaptureBox={lineHeightCaptureBox}
                        onLineHeightSet={handleLineHeightSet}
                        onLineHeightConfirm={handleLineHeightConfirm}
                        onLineHeightConfirmError={handleLineHeightConfirmError}
                        onLineHeightCancel={handleLineHeightCancel}
                        onLineHeightReset={handleLineHeightReset}
                        onLineHeightCaptureChange={setLineHeightCaptureBox}
                        mode="json"
                        jsonMarkComplete={
                          formMeta.studioFormId
                            ? {
                                visible: true,
                                isComplete: !!jsonCompleteMap[formMeta.studioFormId],
                                onToggle: handleJsonMarkComplete,
                              }
                            : null
                        }
                        studioFormFlag={
                          formMeta.studioFormId
                            ? {
                                visible: true,
                                note: formFlagMap[formMeta.studioFormId] || "",
                                isFlagged: !!(formFlagMap[formMeta.studioFormId]?.trim()),
                                onFlagIconClick: () => {
                                  const id = formMeta.studioFormId;
                                  handleFlagIconPress(id, !!(formFlagMap[id]?.trim()));
                                },
                              }
                            : null
                        }
                      />
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "background.paper",
                          borderRadius: 2,
                          p: 4,
                          textAlign: "center",
                          border: "2px dashed",
                          borderColor: "warning.light",
                          bgcolor: "warning.lighter",
                        }}
                      >
                        <Box sx={{ mb: 2, color: "warning.main" }}>
                          <InfoIcon sx={{ fontSize: 48 }} />
                        </Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          color="text.primary"
                        >
                          PDF File Missing
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3, maxWidth: 300 }}
                        >
                          Your fields are restored, but the PDF file needs to be
                          re-uploaded to view the annotations.
                        </Typography>
                        <Button
                          variant="contained"
                          component="label"
                          color="warning"
                        >
                          Upload PDF
                          <input
                            type="file"
                            hidden
                            accept="application/pdf"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handlePdfUpload(e.target.files[0]);
                              }
                            }}
                          />
                        </Button>
                      </Paper>
                    )}
                  </Box>
                )}

                {/* Drag handle - only when PDF is loaded AND not in Mevi Preview (where the PDF column is hidden). */}
                {pdfFile && activeTab !== 4 && (
                  <Box
                    onPointerDown={(e) => {
                      e.preventDefault();
                      splitDragRef.current = {
                        startX: e.clientX,
                        startPercent: splitPercent,
                      };
                      e.currentTarget.setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                      if (!splitDragRef.current) return;
                      const container = e.currentTarget.parentElement;
                      const containerWidth =
                        container.getBoundingClientRect().width;
                      const delta =
                        e.clientX - splitDragRef.current.startX;
                      const deltaPercent =
                        (delta / containerWidth) * 100;
                      const next = Math.min(
                        Math.max(
                          splitDragRef.current.startPercent +
                            deltaPercent,
                          20
                        ),
                        80
                      );
                      setSplitPercent(next);
                    }}
                    onPointerUp={() => {
                      splitDragRef.current = null;
                      localStorage.setItem(
                        "formStudioSplit",
                        String(splitPercent)
                      );
                    }}
                    sx={{
                      width: "6px",
                      cursor: "ew-resize",
                      flexShrink: 0,
                      display: { xs: "none", lg: "block" },
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "4px",
                        height: "40px",
                        borderRadius: "2px",
                        bgcolor: "divider",
                        transition: "background-color 0.2s",
                      },
                      "&:hover::after": {
                        bgcolor: "primary.main",
                      },
                    }}
                  />
                )}

                {/* Form Editor Column.
                    Mevi Preview tab (4) renders the pdf-v2-analyst-element which has its own PDF panel
                    on the left and tools on the right; this column needs the full row width when that
                    tab is active so the embedded layout has room. */}
                <Box
                  sx={{
                    width: {
                      xs: "100%",
                      lg:
                        pdfFile && activeTab !== 4
                          ? `${100 - splitPercent}%`
                          : "100%",
                    },
                    height: "100%",
                    overflow: "hidden", // Prevent double scrollbars
                    pr: {
                      xs: 0,
                      lg: pdfFile && activeTab !== 4 ? 1 : 0,
                    },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      animation: isFormLoaded
                        ? "fadeIn 0.6s ease-out 0.1s both"
                        : "none",
                      opacity: isFormLoaded ? 1 : 0,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {identifySectionsMode ? (
                      <Paper
                        elevation={24}
                        sx={{
                          height: "100%",
                          p: 3,
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          overflowY: "auto",
                          background:
                            "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                          backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          borderBottom: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            flexShrink: 0,
                          }}
                        >
                          <Typography variant="subtitle1">Identify Sections</Typography>
                          <MuiIconButton
                            size="small"
                            onClick={closeIdentifySectionsPanel}
                            aria-label="Close identify sections"
                          >
                            <CloseIcon fontSize="small" />
                          </MuiIconButton>
                        </Box>
                        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", px: 2, py: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            {sections.map((section) => (
                              <Button
                                key={section.id}
                                onClick={() => {
                                  setIdentifySectionActiveId(section.id);
                                  setIdentifySectionName(section.title || "");
                                  setIdentifySectionDescription(section.description || "");
                                  setIdentifySectionTitleShort(section.titleShort || "");
                                  setIdentifySectionInfo(section.info || "");
                                  setIdentifySectionMetadata(typeof section.metadata === "object" && section.metadata !== null ? JSON.stringify(section.metadata, null, 2) : "{}");
                                  setSelectedSectionFieldIds(fields.filter((f) => f.sectionId === section.id).map((f) => f.id));
                                }}
                                variant={section.id === identifySectionActiveId ? "contained" : "outlined"}
                                size="small"
                                sx={{ textTransform: "none" }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <span>{section.title || "Untitled"}</span>
                                  <Typography variant="caption" sx={{ bgcolor: section.id === identifySectionActiveId ? "primary.main" : "action.selected", color: section.id === identifySectionActiveId ? "white" : "text.secondary", px: 0.8, py: 0.2, borderRadius: 1, fontSize: "0.7rem", fontWeight: "bold" }}>
                                    {fields.filter((f) => f.sectionId === section.id).length}
                                  </Typography>
                                  <MuiIconButton size="small" onClick={(e) => { e.stopPropagation(); handleIdentifyDeleteSection(section.id); }} sx={{ color: "inherit" }} aria-label={`Delete ${section.title || "section"}`}>
                                    <DeleteOutlineIcon fontSize="inherit" />
                                  </MuiIconButton>
                                </Box>
                              </Button>
                            ))}
                            <Button size="small" variant="outlined" onClick={handleIdentifyAddSection} sx={{ textTransform: "none" }}>+ New Section</Button>
                            <Button size="small" variant="outlined" onClick={handleIdentifyAddDecisionSection} sx={{ textTransform: "none" }}>+ New Decision Section</Button>
                          </Box>
                        </Paper>
                        <TextField label="Section Number" value={identifySectionNumber} onChange={(e) => setIdentifySectionNumber(e.target.value)} size="small" fullWidth />
                        <TextField label="Section Name" value={identifySectionName} onChange={(e) => { const v = e.target.value; setIdentifySectionName(v); if (identifySectionActiveId) { setFormJsonDirty(true); setSections((prev) => prev.map((s) => s.id === identifySectionActiveId ? { ...s, title: v } : s)); } }} size="small" fullWidth />
                        <TextField label="Section Description" value={identifySectionDescription} onChange={(e) => { const v = e.target.value; setIdentifySectionDescription(v); if (identifySectionActiveId) { setFormJsonDirty(true); setSections((prev) => prev.map((s) => s.id === identifySectionActiveId ? { ...s, description: v } : s)); } }} multiline minRows={2} fullWidth />
                        <TextField label="Section Short Title" value={identifySectionTitleShort} onChange={(e) => { const v = e.target.value; setIdentifySectionTitleShort(v); if (identifySectionActiveId) { setFormJsonDirty(true); setSections((prev) => prev.map((s) => s.id === identifySectionActiveId ? { ...s, titleShort: v || null } : s)); } }} size="small" fullWidth helperText="Abbreviated section title" />
                        <TextField label="Section help (info)" placeholder="HTML string for section help" value={identifySectionInfo} onChange={(e) => { const v = e.target.value; setIdentifySectionInfo(v); if (identifySectionActiveId) { setFormJsonDirty(true); setSections((prev) => prev.map((s) => s.id === identifySectionActiveId ? { ...s, info: v || null } : s)); } }} multiline minRows={2} fullWidth helperText="Section help text (HTML string)" />
                        {identifySectionActiveId && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Section type (read-only)</Typography>
                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{sections.find((s) => s.id === identifySectionActiveId)?.type ?? "data"}</Typography>
                            {sections.find((s) => s.id === identifySectionActiveId)?.type === "decision" && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Navigation is set per option in the decision/decisionWithOther field (Go To Section), not here.</Typography>
                            )}
                          </Box>
                        )}
                        <TextField label="Section metadata (JSON)" placeholder='e.g. {"meviDealRoleRules": {"write": [], "read": []}}' value={identifySectionMetadata} onChange={(e) => setIdentifySectionMetadata(e.target.value)} onBlur={() => { if (!identifySectionActiveId) return; try { const parsed = JSON.parse(identifySectionMetadata || "{}"); setFormJsonDirty(true); setSections((prev) => prev.map((s) => s.id === identifySectionActiveId ? { ...s, metadata: parsed } : s)); } catch (_) {} }} multiline minRows={3} fullWidth helperText="Section metadata. Saved on blur." sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.875rem" } }} />
                        <TextField label="Section Notes" placeholder="Info entered here will help to drive the MTI model" value={identifySectionNotes} onChange={(e) => setIdentifySectionNotes(e.target.value)} multiline minRows={2} fullWidth />
                        <Box sx={{ mt: 1, p: 1.5, border: "1px dashed", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
                          <Typography variant="subtitle2" gutterBottom>Fields in this section ({fields.filter((f) => f.sectionId === identifySectionActiveId).length})</Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {fields.filter((f) => f.sectionId === identifySectionActiveId).map((field) => (
                              <Box key={field.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5, borderRadius: 1, bgcolor: "action.selected", color: "text.secondary", fontSize: "0.75rem", fontWeight: 600 }}>
                                <span>{field.label || field.attributeName || field.id}</span>
                                <MuiIconButton size="small" onClick={() => { setFormJsonDirty(true); setFields((prev) => prev.map((item) => item.id === field.id ? { ...item, sectionId: null } : item)); setSelectedSectionFieldIds((prev) => prev.filter((id) => id !== field.id)); }} sx={{ color: "text.secondary" }} aria-label={`Remove ${field.label || field.attributeName || "field"}`}><DeleteOutlineIcon fontSize="inherit" /></MuiIconButton>
                              </Box>
                            ))}
                            {fields.filter((f) => f.sectionId === identifySectionActiveId).length === 0 && <Typography variant="caption" color="text.secondary">No fields assigned yet.</Typography>}
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: "auto" }}>
                          <Button variant="outlined" onClick={() => { setIdentifySectionNumber(""); setIdentifySectionName(""); setIdentifySectionDescription(""); setIdentifySectionNotes(""); setIdentifySectionTitleShort(""); setIdentifySectionInfo(""); setIdentifySectionMetadata("{}"); setSelectedSectionFieldIds([]); }}>Clear</Button>
                          <Button variant="contained" disabled={selectedSectionFieldIds.length === 0} onClick={() => { if (selectedSectionFieldIds.length === 0) return; setFormJsonDirty(true); setFields((prevFields) => { const targetSectionId = identifySectionActiveId || null; const targetFields = targetSectionId ? prevFields.filter((f) => f.sectionId === targetSectionId) : []; let nextOrder = targetFields.reduce((max, f) => Math.max(max, f.order || 0), 0) + 1; return prevFields.map((field) => { if (!selectedSectionFieldIds.includes(field.id)) return field; const shouldMove = targetSectionId && field.sectionId !== targetSectionId; if (field.attributeName) updateFormRulesField(field.attributeName, { rules: { sectionNumber: identifySectionNumber, sectionName: identifySectionName, sectionDescription: identifySectionDescription, sectionNotes: identifySectionNotes } }); const nextField = { ...field, touchedByAnnotator: true }; if (shouldMove) { nextField.sectionId = targetSectionId; nextField.order = nextOrder; nextOrder += 1; } return nextField; }); }); setSelectedSectionFieldIds([]); }}>Apply to Selected ({selectedSectionFieldIds.length})</Button>
                        </Box>
                      </Paper>
                    ) : (
                    <>
                    <Paper
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                        bgcolor: "transparent",
                      }}
                    >
                      <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="form editor tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        onMouseDown={handleTabsMouseDown}
                        sx={{
                          cursor: "grab",
                          "& .MuiTab-root": {
                            fontWeight: 600,
                            textTransform: "none",
                            fontSize: "0.95rem",
                          },
                          "& .MuiTabs-scroller": {
                            overflowX: "auto",
                          },
                        }}
                      >
                        {/* Raw / non-PDF smart forms are JSON-preview-only: the
                            other tabs are hidden (kept mounted so the positional
                            activeTab index mapping is preserved). */}
                        <Tab label="Field Editor" sx={{ display: isRawMode ? "none" : undefined }} />
                        <Tab label="Form Settings" sx={{ display: isRawMode ? "none" : undefined }} />
                        <Tab label="Form Structure" sx={{ display: isRawMode ? "none" : undefined }} />
                        <Tab label="JSON Preview" />
                        <Tab label="Mevi Preview" sx={{ display: isRawMode ? "none" : undefined }} />
                        <Tab label="Field Guidelines" sx={{ display: isRawMode ? "none" : undefined }} />
                      </Tabs>
                    </Paper>

                    {/* Tab 0: Field Editor */}
                    <Box
                      role="tabpanel"
                      hidden={activeTab !== 0}
                      sx={{
                        display: activeTab === 0 ? "block" : "none",
                        flex: 1,
                        overflowY: "auto",
                        pb: 2,
                      }}
                    >
                      <Paper
                        elevation={24}
                        sx={{
                          p: 3,
                          minHeight: 400,
                          background:
                            "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                          backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        {signaturePlacementMode ? (
                          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
                            <Typography variant="subtitle1">Add Signature</Typography>
                            <FormControl fullWidth size="small">
                              <InputLabel>Role</InputLabel>
                              <Select
                                label="Role"
                                value={signatureRoleKey}
                                onChange={(event) =>
                                  setSignatureRoleKey(event.target.value)
                                }
                              >
                                {signatureRoleOptions.length > 0 ? (
                                  signatureRoleOptions.map((roleKey) => (
                                    <MenuItem key={roleKey} value={roleKey}>
                                      {signatureRoleLabels[roleKey] || roleKey}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem value="" disabled>
                                    Add roles in Form Settings first
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <InputLabel>Signer</InputLabel>
                              <Select
                                label="Signer"
                                value={signatureSignerKey}
                                onChange={(event) =>
                                  setSignatureSignerKey(event.target.value)
                                }
                                disabled={!signatureRoleKey}
                              >
                                {signatureSignerOptions.length > 0 ? (
                                  signatureSignerOptions.map((signerKey) => (
                                    <MenuItem key={signerKey} value={signerKey}>
                                      {signerKey}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem value="" disabled>
                                    No available signers
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                            <Typography variant="caption" color="text.secondary">
                              Drag on the PDF to place a signature. Select an existing signature box to edit its persona output.
                            </Typography>
                            <Box sx={{ mt: "auto", display: "flex", justifyContent: "flex-end", gap: 1 }}>
                              {selectedSignatureId && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleSignatureDelete(selectedSignatureId)}
                                >
                                  Delete
                                </Button>
                              )}
                              <Button variant="outlined" onClick={exitSignaturePlacementMode}>
                                Done
                              </Button>
                            </Box>
                          </Box>
                        ) : initialsPlacementMode ? (
                          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
                            <Typography variant="subtitle1">Add Initials</Typography>
                            <FormControl fullWidth size="small">
                              <InputLabel>Role</InputLabel>
                              <Select
                                label="Role"
                                value={initialsRoleKey}
                                onChange={(event) => setInitialsRoleKey(event.target.value)}
                              >
                                {signatureRoleOptions.length > 0 ? (
                                  signatureRoleOptions.map((roleKey) => (
                                    <MenuItem key={roleKey} value={roleKey}>
                                      {signatureRoleLabels[roleKey] || roleKey}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem value="" disabled>
                                    Add roles in Form Settings first
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <InputLabel>Initials Signer</InputLabel>
                              <Select
                                label="Initials Signer"
                                value={initialsSignerKey}
                                onChange={(event) => setInitialsSignerKey(event.target.value)}
                                disabled={!initialsRoleKey}
                              >
                                {initialsSignerOptions.length > 0 ? (
                                  initialsSignerOptions.map((signerKey) => (
                                    <MenuItem key={signerKey} value={signerKey}>
                                      {signerKey}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem value="" disabled>
                                    No available signers
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                            <Typography variant="caption" color="text.secondary">
                              Drag on the PDF to place an initials box. Output is published as initialsJSON (parallel to
                              signaturesJSON).
                            </Typography>
                            <Box sx={{ mt: "auto", display: "flex", justifyContent: "flex-end", gap: 1 }}>
                              {selectedSignatureId && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleSignatureDelete(selectedSignatureId)}
                                >
                                  Delete
                                </Button>
                              )}
                              <Button variant="outlined" onClick={exitInitialsPlacementMode}>
                                Done
                              </Button>
                            </Box>
                          </Box>
                        ) : studioMode === "json" && createFieldDialogOpen && pendingPdfLocation ? (
                          <CreateFieldDialog
                            open={true}
                            onClose={() => {
                              setCreateFieldDialogOpen(false);
                              setPendingPdfLocation(null);
                              setCreateFieldDialogDefaultAssignTab(false);
                            }}
                            onCreate={handleCreateField}
                            onAssignExisting={
                              activeRole === "annotator"
                                ? undefined
                                : handleAssignPdfLocationToExistingField
                            }
                            defaultToAssignTab={createFieldDialogDefaultAssignTab}
                            sections={sections}
                            fields={fields}
                            mode="json"
                            initialData={{ metadata: { pdfLocation: pendingPdfLocation } }}
                            variant="panel"
                            isPurchaseContract={isPurchaseContract}
                          />
                        ) : pdfSelectedFieldIds.length > 1 && pdfSectionFieldSelections.length >= 2 ? (
                          <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Typography variant="subtitle1" color="text.primary">
                              {pdfSelectedFieldIds.length} fields selected
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                              Hold Ctrl (or Cmd) and click to add or remove fields. Combine them into one field with a single bounding box.
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<MergeTypeIcon />}
                              onClick={handleCombinePdfFields}
                            >
                              Combine into one field
                            </Button>
                            <Button size="small" onClick={() => { setPdfSelectedFieldIds([]); setPdfPrimarySelectId(null); }}>
                              Clear selection
                            </Button>
                          </Box>
                        ) : pdfSelectedFieldIds.length > 1 ? (
                          <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Typography variant="subtitle1" color="text.primary">
                              {pdfSelectedFieldIds.length} regions selected
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                              Combine only works when at least two selections are section fields. Clear selection or select only one extra PDF binding to edit it.
                            </Typography>
                            <Button size="small" onClick={() => { setPdfSelectedFieldIds([]); setPdfPrimarySelectId(null); }}>
                              Clear selection
                            </Button>
                          </Box>
                        ) : fieldEditorPdfExtraId ? (() => {
                          const ann = (signatureAnnotations || []).find((a) => a.id === fieldEditorPdfExtraId);
                          const initAnn = (initialsAnnotations || []).find((a) => a.id === fieldEditorPdfExtraId);
                          const af = (additionalFields || []).find((a) => a.id === fieldEditorPdfExtraId);
                          const sb = (signerAttributeBindings || []).find((b) => b.id === fieldEditorPdfExtraId);
                          const ib = (initialerAttributeBindings || []).find((b) => b.id === fieldEditorPdfExtraId);
                          const ss = (subsequentSigningAttributeBindings || []).find((b) => b.id === fieldEditorPdfExtraId);
                          const baseLoc =
                            ann?.pdfLocation ||
                            initAnn?.pdfLocation ||
                            af?.pdfLocation ||
                            sb?.pdfLocation ||
                            ib?.pdfLocation ||
                            ss?.pdfLocation;
                          const lineVal = af?.lines ?? sb?.lines ?? ss?.lines;
                          const pdfLoc =
                            baseLoc != null
                              ? {
                                  ...baseLoc,
                                  ...(lineVal != null && Number(lineVal) > 0 ? { lines: Number(lineVal) } : {}),
                                }
                              : undefined;
                          if (ann) {
                            const compound =
                              ann.roleKey && ann.signerKey ? `${ann.roleKey}\t${ann.signerKey}` : "";
                            const syntheticField = {
                              id: ann.id,
                              type: "text",
                              label: `Signature image (${ann.signerKey || "signer"})`,
                              attributeName: "",
                              metadata: {
                                pdfLocation: ann.pdfLocation ? { ...ann.pdfLocation } : undefined,
                              },
                              pdfEditorSyntheticKind: "signatureAnnotation",
                              pdfExtraSignerSelectionCompound: compound,
                              preserveAttributeNameCasing: true,
                              pdfExtraOutputTarget: "personaData",
                            };
                            return (
                              <EditFieldDialog
                                key={syntheticField.id}
                                open={true}
                                field={syntheticField}
                                daysAfterSettings={daysAfterSettings}
                                sections={sections}
                                fields={fields}
                                signatures={signatures}
                                additionalFields={additionalFields}
                        signerAttributeBindings={signerAttributeBindings}
                                initialerAttributeBindings={initialerAttributeBindings}
                                subsequentSigningBindings={subsequentSigningAttributeBindings}
                                subsequentSigning={subsequentSigning}
                                signatureAnnotations={signatureAnnotations}
                                initialsAnnotations={initialsAnnotations}
                                initials={initials}
                                onClose={() => {
                                  setPdfSelectedFieldIds([]);
                                  setPdfPrimarySelectId(null);
                                  setSelectedSignatureId(null);
                                }}
                                onSave={handleSavePdfExtraBinding}
                                onDelete={handleDeletePdfExtraBinding}
                                mode="json"
                                variant="panel"
                                isPurchaseContract={isPurchaseContract}
                              />
                            );
                          }
                          if (initAnn) {
                            const compound =
                              initAnn.roleKey && initAnn.signerKey
                                ? `${initAnn.roleKey}\t${initAnn.signerKey}`
                                : "";
                            const syntheticField = {
                              id: initAnn.id,
                              type: "text",
                              label: `Initials image (${initAnn.signerKey || "signer"})`,
                              attributeName: "",
                              metadata: {
                                pdfLocation: initAnn.pdfLocation ? { ...initAnn.pdfLocation } : undefined,
                              },
                              pdfEditorSyntheticKind: "initialsAnnotation",
                              pdfExtraSignerSelectionCompound: compound,
                              preserveAttributeNameCasing: true,
                              pdfExtraOutputTarget: "initialsData",
                            };
                            return (
                              <EditFieldDialog
                                key={syntheticField.id}
                                open={true}
                                field={syntheticField}
                                daysAfterSettings={daysAfterSettings}
                                sections={sections}
                                fields={fields}
                                signatures={signatures}
                                additionalFields={additionalFields}
                                signerAttributeBindings={signerAttributeBindings}
                                initialerAttributeBindings={initialerAttributeBindings}
                                subsequentSigningBindings={subsequentSigningAttributeBindings}
                                subsequentSigning={subsequentSigning}
                                signatureAnnotations={signatureAnnotations}
                                initialsAnnotations={initialsAnnotations}
                                initials={initials}
                                onClose={() => {
                                  setPdfSelectedFieldIds([]);
                                  setPdfPrimarySelectId(null);
                                  setSelectedSignatureId(null);
                                }}
                                onSave={handleSavePdfExtraBinding}
                                onDelete={handleDeletePdfExtraBinding}
                                mode="json"
                                variant="panel"
                                isPurchaseContract={isPurchaseContract}
                              />
                            );
                          }
                          if (af) {
                            const syntheticField = {
                              id: af.id,
                              type: "text",
                              label: af.label ?? "",
                              attributeName: af.attributeName || "",
                              metadata: { pdfLocation: pdfLoc },
                              pdfEditorSyntheticKind: "additional",
                              preserveAttributeNameCasing: true,
                              linkedFieldId: af.linkedFieldId,
                              pdfExtraOutputTarget: "additionalField",
                            };
                            return (
                              <EditFieldDialog
                                key={syntheticField.id}
                                open={true}
                                field={syntheticField}
                                daysAfterSettings={daysAfterSettings}
                                sections={sections}
                                fields={fields}
                                signatures={signatures}
                                additionalFields={additionalFields}
                                signerAttributeBindings={signerAttributeBindings}
                                initialerAttributeBindings={initialerAttributeBindings}
                                subsequentSigningBindings={subsequentSigningAttributeBindings}
                                subsequentSigning={subsequentSigning}
                                signatureAnnotations={signatureAnnotations}
                                initialsAnnotations={initialsAnnotations}
                                initials={initials}
                                onClose={() => {
                                  setPdfSelectedFieldIds([]);
                                  setPdfPrimarySelectId(null);
                                  setSelectedSignatureId(null);
                                }}
                                onSave={handleSavePdfExtraBinding}
                                onDelete={handleDeletePdfExtraBinding}
                                mode="json"
                                variant="panel"
                                isPurchaseContract={isPurchaseContract}
                              />
                            );
                          }
                          if (sb) {
                            const compound =
                              sb.roleKey && sb.signerKey ? `${sb.roleKey}\t${sb.signerKey}` : "";
                            const syntheticField = {
                              ...(sb.def || {}),
                              id: sb.id,
                              type: sb.def?.type && sb.def.type !== "text" ? sb.def.type : "input",
                              label: sb.label ?? sb.def?.label ?? "",
                              attributeName: sb.attributeName || sb.def?.attributeName || "",
                              metadata: { ...(sb.def?.metadata || {}), pdfLocation: pdfLoc },
                              pdfEditorSyntheticKind: "signerBinding",
                              pdfExtraSignerSelectionCompound: compound,
                              preserveAttributeNameCasing: true,
                              linkedFieldId: sb.linkedFieldId,
                              pdfExtraOutputTarget: "personaData",
                              pdfPersonaSubtype: "attributePlacements",
                            };
                            return (
                              <EditFieldDialog
                                key={syntheticField.id}
                                open={true}
                                field={syntheticField}
                                daysAfterSettings={daysAfterSettings}
                                sections={sections}
                                fields={fields}
                                signatures={signatures}
                                additionalFields={additionalFields}
                                signerAttributeBindings={signerAttributeBindings}
                                initialerAttributeBindings={initialerAttributeBindings}
                                subsequentSigningBindings={subsequentSigningAttributeBindings}
                                subsequentSigning={subsequentSigning}
                                signatureAnnotations={signatureAnnotations}
                                initialsAnnotations={initialsAnnotations}
                                initials={initials}
                                onClose={() => {
                                  setPdfSelectedFieldIds([]);
                                  setPdfPrimarySelectId(null);
                                  setSelectedSignatureId(null);
                                }}
                                onSave={handleSavePdfExtraBinding}
                                onDelete={handleDeletePdfExtraBinding}
                                mode="json"
                                variant="panel"
                                isPurchaseContract={isPurchaseContract}
                              />
                            );
                          }
                          if (ib) {
                            const compound =
                              ib.roleKey && ib.signerKey ? `${ib.roleKey}\t${ib.signerKey}` : "";
                            const syntheticField = {
                              ...(ib.def || {}),
                              id: ib.id,
                              type: ib.def?.type && ib.def.type !== "text" ? ib.def.type : "input",
                              label: ib.label ?? ib.def?.label ?? "",
                              attributeName: ib.attributeName || ib.def?.attributeName || "",
                              metadata: { ...(ib.def?.metadata || {}), pdfLocation: pdfLoc },
                              pdfEditorSyntheticKind: "signerBinding",
                              pdfExtraSignerSelectionCompound: compound,
                              preserveAttributeNameCasing: true,
                              linkedFieldId: ib.linkedFieldId,
                              pdfExtraOutputTarget: "initialsData",
                              pdfPersonaSubtype: "attributePlacements",
                            };
                            return (
                              <EditFieldDialog
                                key={syntheticField.id}
                                open={true}
                                field={syntheticField}
                                daysAfterSettings={daysAfterSettings}
                                sections={sections}
                                fields={fields}
                                signatures={signatures}
                                additionalFields={additionalFields}
                                signerAttributeBindings={signerAttributeBindings}
                                initialerAttributeBindings={initialerAttributeBindings}
                                subsequentSigningBindings={subsequentSigningAttributeBindings}
                                subsequentSigning={subsequentSigning}
                                signatureAnnotations={signatureAnnotations}
                                initialsAnnotations={initialsAnnotations}
                                initials={initials}
                                onClose={() => {
                                  setPdfSelectedFieldIds([]);
                                  setPdfPrimarySelectId(null);
                                  setSelectedSignatureId(null);
                                }}
                                onSave={handleSavePdfExtraBinding}
                                onDelete={handleDeletePdfExtraBinding}
                                mode="json"
                                variant="panel"
                                isPurchaseContract={isPurchaseContract}
                              />
                            );
                          }
                          if (ss) {
                            // The stored def comes FIRST so the dialog edits the real field —
                            // its type, validation, placeholder and date metadata — not a shell.
                            // Without it the dialog returned a bare {type,label,attributeName},
                            // snapshotSignerFieldDef turned that into a non-null def, and the
                            // `snapshot ?? fromSb.def` fallback in handleSavePdfExtraBinding could
                            // never reach the good one: a saved acceptanceDate came back as
                            // type "input" with no validation, placeholder or fieldData.
                            // Same shape as the signerBinding synthetic above.
                            const syntheticField = {
                              ...(ss.def || {}),
                              id: ss.id,
                              type: ss.def?.type && ss.def.type !== "text" ? ss.def.type : "input",
                              label: ss.label ?? ss.def?.label ?? "",
                              attributeName: ss.attributeName || ss.def?.attributeName || "",
                              metadata: { ...(ss.def?.metadata || {}), pdfLocation: pdfLoc },
                              pdfEditorSyntheticKind: "subsequentSigning",
                              preserveAttributeNameCasing: true,
                              linkedFieldId: ss.linkedFieldId,
                              pdfExtraOutputTarget: "subsequentSigningJSON",
                              _pdfSyntheticSubsequentSigners: ss.signers || [],
                              signers: ss.signers,
                            };
                            return (
                              <EditFieldDialog
                                key={syntheticField.id}
                                open={true}
                                field={syntheticField}
                                daysAfterSettings={daysAfterSettings}
                                sections={sections}
                                fields={fields}
                                signatures={signatures}
                                additionalFields={additionalFields}
                                signerAttributeBindings={signerAttributeBindings}
                                initialerAttributeBindings={initialerAttributeBindings}
                                subsequentSigningBindings={subsequentSigningAttributeBindings}
                                subsequentSigning={subsequentSigning}
                                signatureAnnotations={signatureAnnotations}
                                initialsAnnotations={initialsAnnotations}
                                initials={initials}
                                onClose={() => {
                                  setPdfSelectedFieldIds([]);
                                  setPdfPrimarySelectId(null);
                                  setSelectedSignatureId(null);
                                }}
                                onSave={handleSavePdfExtraBinding}
                                onDelete={handleDeletePdfExtraBinding}
                                mode="json"
                                variant="panel"
                                isPurchaseContract={isPurchaseContract}
                              />
                            );
                          }
                          return (
                            <Typography color="text.secondary">Select a field on the PDF to edit it here.</Typography>
                          );
                        })() : fieldEditorFieldId ? (() => {
                          const field = fields.find((f) => f.id === fieldEditorFieldId);
                          if (!field) return (
                            <Typography color="text.secondary">Select a field on the PDF to edit it here.</Typography>
                          );
                          const fieldForEdit = field && field.attributeName
                            ? { ...field, rules: getFormRulesField(field.attributeName).rules, offerCompare: isFieldInOfferCompare(field.attributeName) }
                            : field;
                          return (
                            <EditFieldDialog
                              key={fieldForEdit?.id}
                              open={true}
                              field={fieldForEdit}
                              daysAfterSettings={daysAfterSettings}
                              sections={sections}
                              fields={fields}
                              signatures={signatures}
                              additionalFields={additionalFields}
                              signerAttributeBindings={signerAttributeBindings}
                              initialerAttributeBindings={initialerAttributeBindings}
                              subsequentSigningBindings={subsequentSigningAttributeBindings}
                              subsequentSigning={subsequentSigning}
                              signatureAnnotations={signatureAnnotations}
                              initialsAnnotations={initialsAnnotations}
                              initials={initials}
                              onFieldPdfBindingSync={applyFieldPdfBindingFromEditor}
                              onClose={() => {
                                setPdfSelectedFieldIds([]);
                                setPdfPrimarySelectId(null);
                              }}
                              onSave={(updatedField) => {
                                handleAnnotatorSaveField(updatedField);
                                setPdfSelectedFieldIds([]);
                                setPdfPrimarySelectId(null);
                              }}
                              onDelete={(fieldOrId) => {
                                handleAnnotatorDeleteField(fieldOrId);
                                setPdfSelectedFieldIds([]);
                                setPdfPrimarySelectId(null);
                              }}
                              mode="json"
                              variant="panel"
                              isPurchaseContract={isPurchaseContract}
                              onAttachPlacedAnnotation={handleAttachPlacedAnnotationToField}
                            />
                          );
                        })() : (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, color: "text.secondary" }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>Field Editor</Typography>
                            <Typography variant="body2">Select a field on the PDF to edit it. Hold Ctrl (or Cmd) to select multiple, then combine.</Typography>
                          </Box>
                        )}
                      </Paper>
                    </Box>

                    {/* Tab 1: Form Settings */}
                    <Box
                      role="tabpanel"
                      hidden={activeTab !== 1}
                      sx={{
                        display: activeTab === 1 ? "block" : "none",
                        flex: 1,
                        overflowY: "auto",
                        pb: 2, // Add some padding at the bottom
                      }}
                    >
                      <Box sx={{ mb: 3 }}>
                        <FormDetailsSelector
                          formDetails={formDetails}
                          onFormDetailsChange={handleFormDetailsChange}
                          formRules={formRules}
                          onFormRulesChange={handleFormRulesChange}
                          extraFormSummary={extraFormSummary}
                          onExtraFormSummaryChange={(v) => { setFormJsonDirty(true); setExtraFormSummary(v); }}
                          extraDealSummary={extraDealSummary}
                          onExtraDealSummaryChange={(v) => { setFormJsonDirty(true); setExtraDealSummary(v); }}
                          signatures={signatures}
                          registry={registry}
                          registryValidation={registryValidation}
                        />
                      </Box>
                      <Box sx={{ mb: 3 }}>
                        <FormCreatorSelector
                          selectedCreators={selectedCreators}
                          onCreatorsChange={handleCreatorsChange}
                        />
                      </Box>
                      <Box>
                        <SignerRoleSelector
                          signatures={signatures}
                          onRolesChange={handleRolesChange}
                        />
                        <InitialsRoleSelector
                          initials={initials}
                          annotations={initialsAnnotations}
                          bindings={initialerAttributeBindings}
                          onInitialsRolesChange={handleInitialsRolesChange}
                          onBlocked={(message) =>
                            setSnackbar({ open: true, message, severity: "warning" })
                          }
                        />
                        <SubsequentSigningRoleSelector
                          subsequentSigning={subsequentSigning}
                          bindings={subsequentSigningAttributeBindings}
                          onSubsequentSigningChange={handleSubsequentSigningChange}
                          onBlocked={(message) =>
                            setSnackbar({ open: true, message, severity: "warning" })
                          }
                        />
                      </Box>
                    </Box>

                    {/* Tab 2: Form Structure */}
                    <Box
                      role="tabpanel"
                      hidden={activeTab !== 2}
                      sx={{
                        display: activeTab === 2 ? "block" : "none",
                        flex: 1,
                        overflowY: "auto",
                        pb: 2,
                      }}
                    >
                      {/* Form Upload Structure - collapsible, only show when fields are marked for upload */}
                      {fields.some((field) => field.formUpload) && (
                        <Box sx={{ mb: 2 }}>
                          <Paper
                            elevation={0}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              onClick={() => setFormUploadStructureOpen((o) => !o)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1.25,
                                cursor: "pointer",
                                bgcolor: "action.hover",
                                "&:hover": { bgcolor: "action.selected" },
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Form Upload Structure
                              </Typography>
                              <IconButton size="small" aria-label={formUploadStructureOpen ? "Collapse" : "Expand"}>
                                {formUploadStructureOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>
                            <Collapse in={formUploadStructureOpen}>
                              <Box
                                sx={{
                                  p: 3,
                                  background:
                                    "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                                  borderTop: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <FormUploadStructure
                                  fields={fields}
                                  value={formUploadSections}
                                  onChange={setFormUploadSections}
                                />
                              </Box>
                            </Collapse>
                          </Paper>
                        </Box>
                      )}

                      {/* Form Structure (sections & fields) - collapsible */}
                      <Box>
                        <Paper
                          elevation={0}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            onClick={() => setFormStructureOpen((o) => !o)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 2,
                              py: 1.25,
                              cursor: "pointer",
                              bgcolor: "action.hover",
                              "&:hover": { bgcolor: "action.selected" },
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Form Structure
                            </Typography>
                            <IconButton size="small" aria-label={formStructureOpen ? "Collapse" : "Expand"}>
                              {formStructureOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                          <Collapse in={formStructureOpen}>
                            <Box
                              sx={{
                                p: 3,
                                background:
                                  "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                                borderTop: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <JsonEditor
                            fields={fields}
                            sections={sections}
                            setSections={setSections}
                            onFieldAssign={handleFieldAssignment}
                            onFieldsUpdate={handleFieldsUpdate}
                            onDecisionSectionFieldReady={(field) =>
                              startOptionAnnotationQueueIfFieldAlreadyCommitted(field)
                            }
                            signatures={signatures}
                            additionalFields={additionalFields}
                            signerAttributeBindings={signerAttributeBindings}
                            initialerAttributeBindings={initialerAttributeBindings}
                            subsequentSigningBindings={subsequentSigningAttributeBindings}
                            subsequentSigning={subsequentSigning}
                            signatureAnnotations={signatureAnnotations}
                            initialsAnnotations={initialsAnnotations}
                            onFieldPdfBindingSync={applyFieldPdfBindingFromEditor}
                            onSelectField={(id) => {
                              if (id != null) {
                                setScrollToFieldId(id);
                              } else {
                                setScrollToFieldId(null);
                              }
                            }}
                            onSectionsChange={() => setFormJsonDirty(true)}
                            offerCompare={offerCompare}
                            isPurchaseContract={isPurchaseContract}
                            getFieldForEdit={(field) =>
                              field && field.attributeName
                                ? {
                                    ...field,
                                    rules: getFormRulesField(field.attributeName).rules,
                                    offerCompare: isFieldInOfferCompare(field.attributeName),
                                  }
                                : field
                            }
                            onOpenCreateDialog={handleOpenCreateDialog}
                            editingField={editingField}
                            onEditField={(field) => {
                              if (field && field.attributeName) {
                                setEditingField({
                                  ...field,
                                  rules: getFormRulesField(field.attributeName).rules,
                                  offerCompare: isFieldInOfferCompare(field.attributeName),
                                });
                              } else {
                                setEditingField(field);
                              }
                            }}
                            formDetails={formDetails}
                            createFieldDialogOpen={
                              studioMode !== "annotator" &&
                              createFieldDialogOpen &&
                              !(studioMode === "json" && pendingPdfLocation)
                            }
                            onCloseCreateDialog={() => {
                              setCreateFieldDialogOpen(false);
                              setPendingPdfLocation(null);
                            }}
                            onCreateField={handleCreateField}
                            createFieldInitialData={
                              pendingPdfLocation
                                ? {
                                    metadata: {
                                      pdfLocation: pendingPdfLocation,
                                    },
                                  }
                                : null
                            }
                          />
                            </Box>
                          </Collapse>
                        </Paper>
                      </Box>
                    </Box>

                    {/* Tab 3: JSON Preview */}
                    <Box
                      role="tabpanel"
                      hidden={activeTab !== 3}
                      sx={{
                        display: activeTab === 3 ? "block" : "none",
                        flex: 1,
                        overflowY: "auto",
                        pb: 2,
                      }}
                    >
                      <Paper
                        elevation={24}
                        sx={{
                          p: 3,
                          background:
                            "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                          backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        {isRawMode ? (
                          <RawJsonEditor
                            templateJson={rawFormJson || ""}
                            integrationJson={rawIntegrationJson || ""}
                            onApply={handleRawJsonApply}
                            setSnackbar={setSnackbar}
                          />
                        ) : (
                          <Preview
                            fields={fields}
                            sections={sections}
                            signatures={signatures}
                            selectedCreators={selectedCreators}
                            formDetails={formDetails}
                            formUploadSections={formUploadSections}
                            onFormJsonEdit={handleFormJsonEdit}
                            setSnackbar={setSnackbar}
                            signatureAnnotations={signatureAnnotations}
                            initialsAnnotations={initialsAnnotations}
                            additionalFields={additionalFields}
                            signerAttributeBindings={signerAttributeBindings}
                            initialerAttributeBindings={initialerAttributeBindings}
                            subsequentSigning={subsequentSigning}
                            subsequentSigningAttributeBindings={subsequentSigningAttributeBindings}
                            formRules={formRules}
                            offerCompare={offerCompare}
                            isPurchaseContract={isPurchaseContract}
                            extraFormSummary={extraFormSummary}
                            extraDealSummary={extraDealSummary}
                            rulesJSON={rulesJSON}
                            initials={initials}
                          />
                        )}
                      </Paper>
                    </Box>

                    {/* Tab 4: Mevi Preview — embeds the
                        `<pdf-v2-analyst-element>` web component
                        (built from app-mevi's pdf-viewer-v2 module
                        and dropped into public/pdf-v2-element/).
                        The element renders the PDF on the left and
                        analyst toolbars on the right; its header
                        bar Save Form Template button routes back
                        through the same JSON-Preview-Edit-Apply
                        path so Save-to-Cloud goes orange on save. */}
                    <Box
                      role="tabpanel"
                      hidden={activeTab !== 4}
                      sx={{
                        display: activeTab === 4 ? "flex" : "none",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                      }}
                    >
                      {pdfFile && analystPdfBuffer && analystTemplate ? (
                        <PdfV2AnalystElement
                          ref={analystElementRef}
                          pdfBuffer={analystPdfBuffer}
                          formTemplate={analystTemplate}
                          analystRole="analyst"
                          onSaveTemplateRequested={handleAnalystSaveTemplate}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                          }}
                        >
                          <Typography variant="body2">
                            {!pdfFile
                              ? "Load a PDF to use the analyst panel."
                              : "Preparing analyst panel…"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {/* Tab 5: Field Guidelines */}
                    <Box
                      role="tabpanel"
                      hidden={activeTab !== 5}
                      sx={{
                        display: activeTab === 5 ? "block" : "none",
                        flex: 1,
                        overflowY: "auto",
                        pb: 2,
                      }}
                    >
                      <Paper
                        elevation={24}
                        sx={{
                          p: 3,
                          background:
                            "linear-gradient(rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02))",
                          backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        <FieldGuidelines />
                      </Paper>
                    </Box>
                    </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Container>
      </Box>
      </>
      )}
      <Typography
        variant="caption"
        sx={{
          position: "fixed",
          bottom: 4,
          left: 8,
          opacity: 0.35,
          fontSize: "0.65rem",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        v{appVersion}
      </Typography>
    </ThemeProvider>
  );
}

export default App;
